import React, { useState, useRef, useCallback } from "react";
import { BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
         ResponsiveContainer, ReferenceLine, Area, AreaChart, ComposedChart } from "recharts";
import { listProjects, saveProject, deleteProject } from "./projectsCloud";
import { generateProposal } from "./proposalDoc";

// ─── Constants ───────────────────────────────────────────────────────────────
const MONTH_NAMES = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
const NAROK_IRR   = [5.8,6.1,6.3,5.9,5.4,4.9,4.7,5.2,5.8,5.9,5.7,5.5];


// ─── PROJECT STORAGE ──────────────────────────────────────────────────────────
const STORAGE_KEY = "solarsim_ci_projects";

function loadAllProjects() {
  try { return JSON.parse(localStorage.getItem(STORAGE_KEY) || "[]"); }
  catch { return []; }
}

function saveAllProjects(projects) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(projects));
}

function serializeProject(name, site, assumptions, diesel, profile, results, irrData, irrMeta) {
  return {
    id:        Date.now(),
    name:      name || (site.clientName ? `${site.clientName} — ${new Date().toLocaleDateString()}` : `Project ${new Date().toLocaleDateString()}`),
    savedAt:   new Date().toISOString(),
    site, assumptions, diesel,
    // Profile: strip rawRows (too large) — keep the aggregated data only
    profile: profile ? { ...profile, rawRows: [] } : null,
    results, irrData, irrMeta,
  };
}

const DEFAULT_ASSUMPTIONS = {
  // KPLC tariff — can be set as a single rate OR derived from components below
  kplcRate: 22.5,            // KES/kWh — used directly if components are all zero
  // KPLC component rates (KES/kWh each) — formula: (sum) × 1.05
  // When any component is non-zero, the formula overrides kplcRate in calculations
  highRateKwh:     0,        // High/Peak rate energy charge
  lowRateKwh:      0,        // Low/Off-peak rate energy charge
  fecKwh:          0,        // Fuel Energy Charge
  ferfaKwh:        0,        // Foreign Exchange Rate Fluctuation Adjustment
  inflationAdjKwh: 0,        // Inflation Adjustment Surcharge
  ercLevyKwh:      0,        // ERC Levy
  wraLevyKwh:      0,        // WRA Levy
  demandCharge: 800,          // KES per kVA of peak demand per month (C&I tariff)
  pvCostPerKwp: 42900,        // KES per kWp — supply & install
  bessCostPerKwh: 30290,       // KES per kWh — battery storage
  installationCostPerPanel: 10, // KES per panel — BOS, civils, commissioning
  omCostPerYearKes: 120000,
  tariffEscalation: 0.05,
  inflationRate: 0.06,
  projectLifeYears: 25,
  // Inverter size override — for modular inverter selection
  inverterKwOverride:     "",   // blank = auto (calculated). Set to e.g. 100 for 100kW unit
  inverterConfigOverride: "",   // blank = 1 unit. Set to e.g. 2 for 2 units
  // Operating calendar — accounts for weekend/holiday reduced demand
  satFactor:           0.55,   // Saturday as fraction of full weekday demand (0.55 = till noon)
  sunFactor:           0.10,   // Sunday & public holiday base load fraction
  publicHolidaysPerYear: 10,   // Kenya: ~10 public holidays/year
  co2FactorKgPerKwh: 0.5,
  systemEff: 0.80,
  moduleWp: 580,
  pvDegradationPerYear: 0.005,
  bessUnitKwh: 100.0,         // C&I BESS units are larger
  bessDod: 0.90,
  bessDeg10yr: 0.10,
  bessRoundTripEff: 0.92,
  bessInitSoc: 0.50,
  targetSelfSufficiency: 0.80, // % of consumption to cover with solar+BESS
  discountRate: 0.10,           // discount rate for NPV calculation (10% default)
  // APFC — Automatic Power Factor Correction
  apfcTargetPf:    0.95,       // target power factor after correction
  apfcDetuning:    0.07,       // detuning factor (7% standard, 14% for high THD sites)
  apfcStepSize:    10,         // kVAR per capacitor step (standard sizes: 5, 10, 12.5, 25, 50)
  apfcCostPerKvar: 65,         // USD per kVAR installed (panel + controller + installation)
};

const DEFAULT_SITE = {
  clientName: "", siteName: "", location: "", sector: "Commercial",
  lat: "", lng: "", tariffType: "C&I Time-of-Use",
};

// ─── Design tokens ────────────────────────────────────────────────────────────
// ─── SnC Brand Design System ──────────────────────────────────────────────────
// Colours: Navy #1F3A5F (primary) · Red #C0392B (accent only) · Mid blue #2E75B6
// Font: Inter · No gradients · No shadows · Border-radius ≤ 6px
const C = {
  navy:   "#1F3A5F",   // primary — headers, active states, metric values
  red:    "#C0392B",   // accent only — CTA, active tab underline, warnings
  mid:    "#2E75B6",   // secondary actions, links, solar series
  lt:     "#BFD3F0",   // light blue — text on dark, subtle fills
  green:  "#16a34a",   // positive financial results only
  amber:  "#d97706",   // warnings, irradiance data
  muted:  "#64748b",   // secondary text
  text:   "#1e293b",   // primary body text
  dim:    "#334155",   // secondary body text
  bg:     "#F4F6F9",   // page background — slightly cooler than pure white
  surface:"#ffffff",   // card / panel surface
  border: "#D6DEE8",   // borders — slightly blue-tinted
  deep:   "#EDF1F7",   // input backgrounds, section fills
  // legacy aliases so chart colours don't break
  blue:   "#2E75B6",
  purple: "#5b21b6",
  teal:   "#0f766e",
};
const S = {
  app:    { fontFamily:"'Inter','Segoe UI',system-ui,sans-serif", background:C.bg, minHeight:"100vh", color:C.text },
  header: {
    background:C.navy,
    borderBottom:`3px solid ${C.red}`,
    padding:"0 32px",
    display:"flex", alignItems:"stretch", justifyContent:"space-between",
    minHeight:"56px",
  },
  tabs:   { display:"flex", background:C.surface, borderBottom:`1px solid ${C.border}`, paddingLeft:"8px" },
  tab:    a => ({
    padding:"12px 22px", fontSize:"10px", letterSpacing:"0.13em", textTransform:"uppercase",
    cursor:"pointer",
    background:"transparent",
    color:a?C.navy:C.muted,
    fontWeight:a?700:400,
    borderBottom:a?`2px solid ${C.red}`:"2px solid transparent",
    border:"none", fontFamily:"inherit", transition:"all 0.15s",
  }),
  content:{ padding:"24px 32px", maxWidth:"1400px" },
  card:   { background:C.surface, border:`1px solid ${C.border}`, borderRadius:"6px", padding:"20px" },
  metric: { background:C.surface, border:`1px solid ${C.border}`, borderRadius:"6px", padding:"16px 18px" },
  grid2:  { display:"grid", gridTemplateColumns:"1fr 1fr", gap:"16px", marginBottom:"16px" },
  grid3:  { display:"grid", gridTemplateColumns:"repeat(3,1fr)", gap:"14px", marginBottom:"16px" },
  grid4:  { display:"grid", gridTemplateColumns:"repeat(4,1fr)", gap:"14px", marginBottom:"16px" },
  label:  { fontSize:"10px", color:C.muted, letterSpacing:"0.08em", textTransform:"uppercase", display:"block", marginBottom:"5px", fontWeight:500 },
  input:  { width:"100%", background:C.deep, border:`1px solid ${C.border}`, borderRadius:"4px", color:C.text, padding:"8px 10px", fontSize:"12px", fontFamily:"inherit", boxSizing:"border-box", outline:"none", transition:"border-color 0.15s" },
  sec:    { fontSize:"9px", letterSpacing:"0.18em", textTransform:"uppercase", color:C.red, fontWeight:700, marginBottom:"14px", paddingBottom:"8px", borderBottom:`1px solid ${C.border}` },
  btn:    v => ({
    padding:v==="sm"?"5px 12px":"10px 20px",
    fontSize:v==="sm"?"9px":"10px",
    letterSpacing:"0.12em", textTransform:"uppercase",
    border:v==="danger"?`1px solid #fca5a5`:v==="ghost"?`1px solid ${C.border}`:v==="outline"?`1px solid ${C.navy}`:"none",
    borderRadius:"4px", cursor:"pointer", fontFamily:"inherit",
    background:v==="primary"?C.navy:v==="success"?"#1a4a2e":v==="danger"?"#fee2e2":v==="outline"?"transparent":"#EDF1F7",
    color:v==="primary"?"#ffffff":v==="success"?"#dcfce7":v==="danger"?C.red:v==="outline"?C.navy:C.dim,
    fontWeight:v==="primary"||v==="outline"?700:500,
    transition:"all 0.15s", whiteSpace:"nowrap",
  }),
  th:     { padding:"8px 10px", textAlign:"left", fontSize:"9px", letterSpacing:"0.1em", textTransform:"uppercase", color:C.muted, fontWeight:600, borderBottom:`1px solid ${C.border}`, background:C.deep },
  td:     { padding:"8px 10px", borderBottom:`1px solid ${C.border}`, color:C.dim, verticalAlign:"middle", fontSize:"12px" },
  badge:  c => ({
    display:"inline-block", padding:"3px 8px", borderRadius:"3px",
    fontSize:"9px", letterSpacing:"0.1em", textTransform:"uppercase", fontWeight:700,
    background:c==="green"?"#dcfce7":c==="blue"?`${C.lt}44`:c==="amber"?"#fef3c7":c==="purple"?"#ede9fe":c==="navy"?C.navy:"#fee2e2",
    color:c==="green"?"#166534":c==="blue"?C.navy:c==="amber"?"#92400e":c==="purple"?"#5b21b6":c==="navy"?C.lt:C.red,
  }),
  ct:     { fontSize:"9px", letterSpacing:"0.13em", textTransform:"uppercase", color:C.muted, fontWeight:500, marginBottom:"10px" },
};

const CT = ({ active, payload, label, unit="" }) => {
  if (!active || !payload?.length) return null;
  return (
    <div style={{background:C.surface,border:`1px solid ${C.border}`,padding:"7px 11px",borderRadius:"3px",fontSize:"10px"}}>
      <div style={{color:C.muted,marginBottom:"3px"}}>{label}</div>
      {payload.map((p,i) => <div key={i} style={{color:p.color}}>{p.name}: {typeof p.value==="number"?p.value.toFixed(2):p.value}{unit?` ${unit}`:""}</div>)}
    </div>
  );
};

// ─── BILL PARSER ─────────────────────────────────────────────────────────────
function calcTariffFromBill(bill) {
  const high    = parseFloat(bill.highRateKwh)    || 0;
  const low     = parseFloat(bill.lowRateKwh)     || 0;
  const fec     = parseFloat(bill.fecKwh)         || 0;
  const ferfa   = parseFloat(bill.ferfaKwh)       || 0;
  const infl    = parseFloat(bill.inflationAdjKwh)|| 0;
  const erc     = parseFloat(bill.ercLevyKwh)     || 0;
  const wra     = parseFloat(bill.wraLevyKwh)     || 0;
  const componentSum = high + low + fec + ferfa + infl + erc + wra;
  if (componentSum > 0.5) {
    // Components found — use the formula
    return +((componentSum) * 1.05).toFixed(4);
  }
  // No components extracted — fall back to total bill ÷ kWh
  const kwh = parseFloat(bill.unitsKwh) || 0;
  const kes = parseFloat(bill.totalKes) || 0;
  return kwh > 0 && kes > 0 ? +(kes / kwh).toFixed(4) : 0;
}

function synthesiseProfileFromBills(bills) {
  if (!bills || bills.length === 0) throw new Error("No bill data to synthesise from.");

  // Average across all bills
  const avgMonthlyKwh  = bills.reduce((s,b) => s + (b.unitsKwh  || 0), 0) / bills.length;
  const avgPeakKva     = bills.reduce((s,b) => s + (b.peakKva   || 0), 0) / bills.length;
  const avgTotalKes    = bills.reduce((s,b) => s + (b.totalKes   || 0), 0) / bills.length;

  if (avgMonthlyKwh <= 0) throw new Error("Units consumed (kWh) could not be determined from the bills.");

  const dailyKwh   = +(avgMonthlyKwh / 30).toFixed(2);
  // Peak kW from kVA — assume power factor 0.9 for C&I
  const peakKw     = avgPeakKva > 0 ? +(avgPeakKva * 0.9).toFixed(2) : +(dailyKwh / 10).toFixed(2);

  // Compute tariff for each bill using component formula, then average
  const billTariffs = bills.map(b => ({ tariff: calcTariffFromBill(b), usedComponents: (b.highRateKwh||0)+(b.lowRateKwh||0)+(b.fecKwh||0) > 0 }));
  const validTariffs = billTariffs.filter(t => t.tariff > 0);
  const effTariff  = validTariffs.length > 0
    ? +(validTariffs.reduce((s,t) => s + t.tariff, 0) / validTariffs.length).toFixed(4)
    : 22.5;
  const usedComponentCalc = billTariffs.some(t => t.usedComponents);

  // Build a synthetic 24-hr average load profile
  // KPLC C&I clients: low overnight, ramp up 7am, peak 9-17, taper off 17-21
  // Profile shape normalised to sum to dailyKwh across 24 hours
  const SHAPE = [
    0.018, 0.016, 0.015, 0.015, 0.016, 0.020,  // 00-05 low overnight
    0.030, 0.050, 0.065, 0.075, 0.080, 0.080,  // 06-11 ramp + peak
    0.078, 0.075, 0.073, 0.070, 0.065, 0.055,  // 12-17 peak + taper
    0.040, 0.032, 0.028, 0.025, 0.022, 0.019,  // 18-23 evening rundown
  ]; // sums to 1.0
  const totalShape = SHAPE.reduce((a,b)=>a+b,0);
  const hourlyAvg  = SHAPE.map(f => +(f / totalShape * dailyKwh).toFixed(3));
  // Max hourly = avg × 1.4 (typical C&I peak factor)
  const hourlyMax  = hourlyAvg.map(v => +(v * 1.4).toFixed(3));

  // Seasonal monthly variation for charting (rough ±10% spread)
  const SEASONAL = [0.97,0.95,1.00,1.02,1.05,1.03,1.00,0.98,1.00,1.02,1.03,0.98];

  return {
    // Same shape as parseLoggerFile output
    hourlyAvg, hourlyMax,
    dailyKwh,
    peakKw,
    peakTime: "11:00",  // assumed peak hour for C&I
    days:     bills.length * 30,
    intervalMins: 60,
    rawRows: [],
    warnings: [],
    headers: [],
    // Bill-specific extras
    fromBills:      true,
    billCount:      bills.length,
    avgMonthlyKwh:    +avgMonthlyKwh.toFixed(0),
    avgPeakKva:       +avgPeakKva.toFixed(1),
    avgTotalKes:      +avgTotalKes.toFixed(0),
    effTariff,
    usedComponentCalc,  // true if at least one bill used the component formula
    billTariffs:      billTariffs.map(t => +t.tariff.toFixed(4)),
    seasonal:         SEASONAL.map((f,i) => ({
      month: ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"][i],
      kwh:   +(avgMonthlyKwh * f).toFixed(0),
    })),
    bills, // keep raw bill records for display
  };
}

// Parse bill text extracted from PDF/image using Claude API
async function parseBillWithClaude(text) {
  const response = await fetch("/api/parse-bill", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      model: "claude-sonnet-4-6",
      max_tokens: 1000,
      messages: [{
        role: "user",
        content: `Extract the following fields from this KPLC electricity bill. Return ONLY a valid JSON object with no markdown, no explanation, no backticks.

Fields to extract:
- period: billing period as "YYYY-MM" — use the bill month/year shown
- unitsKwh: total units/energy consumed in kWh (numeric). Sum of high-rate and low-rate units if shown separately.
- peakKva: maximum demand recorded in kVA (numeric). Use 0 if not shown.
- tariffCode: tariff code if shown (e.g. "SC5", "DC", "LV2", "CI1") — use "" if not found
- totalKes: total amount due in KES (numeric)

TARIFF COMPONENTS — extract each as KES per kWh (rate per unit), NOT total amounts:
- highRateKwh: High Rate / Peak Rate energy charge per kWh (e.g. "Energy Charge - Peak" or "High Voltage Peak")
- lowRateKwh: Low Rate / Off-Peak energy charge per kWh (e.g. "Energy Charge - Off Peak" or "Low Voltage Off Peak")
- fecKwh: Fuel Energy Charge (FEC) per kWh — sometimes called "Fuel Cost Charge"
- ferfaKwh: Foreign Exchange Rate Fluctuation Adjustment (FERFA) per kWh
- inflationAdjKwh: Inflation Adjustment per kWh — may be labelled "Inflation Adjustment Surcharge" or "IAS"
- ercLevyKwh: ERC Levy per kWh — Energy Regulatory Commission levy
- wraLevyKwh: WRA Levy per kWh — Water Resources Authority levy

If a component is not shown on the bill, use 0.
If a value is shown as a total amount (not per-kWh), divide by unitsKwh to get per-kWh rate.

Return exactly this structure:
{"period":"","unitsKwh":0,"peakKva":0,"tariffCode":"","totalKes":0,"highRateKwh":0,"lowRateKwh":0,"fecKwh":0,"ferfaKwh":0,"inflationAdjKwh":0,"ercLevyKwh":0,"wraLevyKwh":0}

Bill text:
${text.slice(0, 4000)}`
      }]
    })
  });
  if (!response.ok) {
    const errText = await response.text();
    throw new Error(`API error ${response.status}: ${errText.slice(0, 300)}`);
  }
  const data = await response.json();
  const raw  = (data.content?.[0]?.text || "").trim();
  return extractJson(raw);
}

// Pull the first valid JSON object out of a model response, even if surrounded
// by prose, markdown fences, or commentary. Throws if no object is found.
function extractJson(raw) {
  let s = raw.replace(/^```[a-z]*\n?|```$/g, "").trim();
  const start = s.indexOf("{");
  const end   = s.lastIndexOf("}");
  if (start === -1 || end === -1 || end <= start) {
    throw new Error(`No JSON object in response: ${raw.slice(0, 200)}`);
  }
  return JSON.parse(s.slice(start, end + 1));
}

// Extract text from a PDF bill file using PDF.js from CDN
// Load PDF.js from CDN — shared by text extraction and rasterisation
async function loadPdfJs() {
  if (window.pdfjsLib) return;
  await new Promise((resolve, reject) => {
    const s = document.createElement("script");
    s.src = "https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.min.js";
    s.onload = resolve;
    s.onerror = () => reject(new Error("Could not load PDF parser. Check internet connection."));
    document.head.appendChild(s);
  });
  window.pdfjsLib.GlobalWorkerOptions.workerSrc =
    "https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.worker.min.js";
}

// Try to extract embedded text from a PDF.
// Returns the text string (may be empty/short for scanned PDFs).
async function extractPdfText(file) {
  await loadPdfJs();
  const ab  = await file.arrayBuffer();
  const pdf = await window.pdfjsLib.getDocument({ data: new Uint8Array(ab) }).promise;
  let text = "";
  for (let p = 1; p <= Math.min(pdf.numPages, 4); p++) {
    const page = await pdf.getPage(p);
    const tc   = await page.getTextContent();
    text += tc.items.map(i => i.str).join(" ") + "\n";
  }
  return text;
}

// Rasterise the first page of a PDF to a JPEG base64 string using PDF.js canvas.
// Scale = 2.0 gives ~150 DPI equivalent — enough for Claude vision to read bill text.
async function rasterisePdfPage(file) {
  await loadPdfJs();
  const ab    = await file.arrayBuffer();
  const pdf   = await window.pdfjsLib.getDocument({ data: new Uint8Array(ab) }).promise;
  const page  = await pdf.getPage(1);
  const scale = 2.0;
  const vp    = page.getViewport({ scale });
  const canvas = document.createElement("canvas");
  canvas.width  = vp.width;
  canvas.height = vp.height;
  const ctx = canvas.getContext("2d");
  await page.render({ canvasContext: ctx, viewport: vp }).promise;
  // Return as base64 JPEG (smaller than PNG, still legible)
  const dataUrl = canvas.toDataURL("image/jpeg", 0.92);
  return dataUrl.split(",")[1]; // strip "data:image/jpeg;base64," prefix
}

// Convert image file (JPG/PNG) to base64 for Claude vision
async function imageToBase64(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload  = () => resolve(reader.result.split(",")[1]);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

// Send a base64 image to Claude vision and extract bill fields
async function parseBillFromImage(b64, mediaType="image/jpeg") {
  const response = await fetch("/api/parse-bill", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      model: "claude-sonnet-4-6",
      max_tokens: 800,
      messages: [{
        role: "user",
        content: [
          { type: "image", source: { type: "base64", media_type: mediaType, data: b64 } },
          { type: "text", text: `Extract these fields from this KPLC electricity bill image. Return ONLY valid JSON, no markdown, no explanation.

Required fields:
- period: "YYYY-MM"
- unitsKwh: total kWh consumed (numeric)
- peakKva: max demand kVA (numeric, 0 if absent)
- tariffCode: tariff code string ("" if absent)
- totalKes: total amount due in KES (numeric)

Tariff component rates (KES per kWh — divide total amounts by unitsKwh if only totals are shown):
- highRateKwh: High/Peak Rate energy charge per kWh
- lowRateKwh: Low/Off-Peak Rate energy charge per kWh
- fecKwh: Fuel Energy Charge per kWh
- ferfaKwh: Foreign Exchange Rate Fluctuation Adjustment (FERFA) per kWh
- inflationAdjKwh: Inflation Adjustment per kWh
- ercLevyKwh: ERC Levy per kWh
- wraLevyKwh: WRA Levy per kWh

Use 0 for any field not visible on the bill.
{"period":"","unitsKwh":0,"peakKva":0,"tariffCode":"","totalKes":0,"highRateKwh":0,"lowRateKwh":0,"fecKwh":0,"ferfaKwh":0,"inflationAdjKwh":0,"ercLevyKwh":0,"wraLevyKwh":0}` }
        ]
      }]
    })
  });
  if (!response.ok) throw new Error(`Claude API error ${response.status}: ${await response.text()}`);
  const data = await response.json();
  const raw  = (data.content?.[0]?.text || "").trim();
  return extractJson(raw);
}

// Parse a single bill file.
// Strategy:
//   PDF → try text extraction first (fast, free)
//         if text is too short (scanned bill) → rasterise page 1 → Claude vision
//   Image (JPG/PNG/WEBP) → Claude vision directly
async function parseSingleBill(file) {
  const ext = file.name.split(".").pop().toLowerCase();
  if (ext === "pdf") {
    // Step 1: attempt text extraction
    let text = "";
    try { text = await extractPdfText(file); } catch(e) { /* ignore — fall through to vision */ }
    if (text.trim().length >= 80) {
      // Good text layer — use Claude text API
      return await parseBillWithClaude(text);
    }
    // Step 2: scanned PDF — rasterise and send to Claude vision
    const b64 = await rasterisePdfPage(file);
    return await parseBillFromImage(b64, "image/jpeg");
  }
  if (["jpg","jpeg","png","webp"].includes(ext)) {
    const b64 = await imageToBase64(file);
    const mt  = ext === "png" ? "image/png" : "image/jpeg";
    return await parseBillFromImage(b64, mt);
  }
  throw new Error(`Unsupported file type: .${ext}. Upload PDF, JPG, or PNG.`);
}

// ─── FILE PARSER ─────────────────────────────────────────────────────────────
// Parses Fusion Solar / generic power logger Excel or CSV files.
// Robust: handles title rows above headers, string-formatted numbers,
// string datetimes, any column order, multiple logger brands.
async function parseLoggerFile(file) {
  const ext = file.name.split(".").pop().toLowerCase();

  // ── CSV path ──────────────────────────────────────────────────────────────
  if (ext === "csv") {
    const text = await file.text();
    const lines = text.split(/\r?\n/).filter(l => l.trim());
    return parseRows(lines.map(l => l.split(",")), file.name);
  }

  // ── Excel path — load SheetJS from CDN ───────────────────────────────────
  await new Promise((resolve, reject) => {
    if (window.XLSX) { resolve(); return; }
    const s = document.createElement("script");
    s.src = "https://cdn.jsdelivr.net/npm/xlsx@0.18.5/dist/xlsx.full.min.js";
    s.onload  = resolve;
    s.onerror = () => reject(new Error("Could not load Excel parser library. Check your internet connection."));
    document.head.appendChild(s);
  });

  const ab = await file.arrayBuffer();
  // raw:false → everything comes back as formatted strings (matches what Python saw)
  // cellDates:false → keep dates as strings not JS Date objects (more predictable)
  const wb  = window.XLSX.read(ab, { type:"array", raw:false, cellDates:false });
  const ws  = wb.Sheets[wb.SheetNames[0]];
  const raw = window.XLSX.utils.sheet_to_json(ws, { header:1, raw:false, defval:"" });

  return parseRows(raw, file.name);
}

// ── Core parser — works on any 2D array of strings ────────────────────────
function parseRows(raw, fileName) {
  if (raw.length < 3) throw new Error("File appears empty or has fewer than 3 rows.");

  // ── Step 1: find the header row ──────────────────────────────────────────
  // Scan up to row 10. Header row = first row that has ≥2 non-empty string cells
  // AND at least one cell matching date/time/kw/power/consumption keywords.
  const isHeaderRow = row => {
    const cells = row.map(c => (c||"").toString().toLowerCase().trim());
    const nonEmpty = cells.filter(c => c.length > 0).length;
    const hasKeyword = cells.some(c =>
      /date|time|stamp|datetime|hour|kw|power|consumption|load|demand|pv|solar|mains|grid|import/i.test(c)
    );
    return nonEmpty >= 2 && hasKeyword;
  };

  let headerRowIdx = -1;
  for (let i = 0; i < Math.min(10, raw.length); i++) {
    if (isHeaderRow(raw[i])) { headerRowIdx = i; break; }
  }
  if (headerRowIdx === -1) throw new Error(
    "Could not find a header row in the first 10 rows. Expected column names like 'Date', 'Consumption power (kW)', etc."
  );

  const headers = raw[headerRowIdx].map(h => (h||"").toString().toLowerCase().trim());

  // ── Step 2: detect columns ───────────────────────────────────────────────
  const findCol = (...terms) => headers.findIndex(h => terms.some(t => h.includes(t.toLowerCase())));

  const dtCol    = findCol("date","datetime","timestamp","time","hour");
  const consCol  = findCol("consumption","total load","load","demand","active power","total power");
  const pvCol    = findCol("pv output","pv power","pv(kw)","solar","generation","inverter");
  const mainsCol = findCol("mains","grid","import","purchase","buy","utility");

  if (dtCol   === -1) throw new Error(`No datetime column found. Headers detected: ${headers.filter(h=>h).join(", ")}`);
  if (consCol === -1) throw new Error(`No consumption/load column found. Headers detected: ${headers.filter(h=>h).join(", ")}`);

  const warnings = [];
  if (pvCol    === -1) warnings.push("No PV output column detected — existing solar generation not shown.");
  if (mainsCol === -1) warnings.push("No mains/grid import column detected.");

  // ── Step 3: parse data rows ───────────────────────────────────────────────
  // Datetime parser: handles "2025-12-01 13:45", "01/12/2025 13:45", Excel serial numbers
  const parseDt = val => {
    if (!val && val !== 0) return null;
    const s = val.toString().trim();
    if (!s) return null;
    // Excel serial number (e.g. 46022.573)
    if (/^\d{5}(\.\d+)?$/.test(s)) {
      const serial = parseFloat(s);
      const ms = (serial - 25569) * 86400 * 1000; // Excel epoch to Unix
      const d = new Date(ms);
      return isNaN(d) ? null : d;
    }
    // Standard string formats
    const d = new Date(s.replace(/\//g,"-").replace(/^(\d{2})-(\d{2})-(\d{4})/,"$3-$2-$1"));
    return isNaN(d.getTime()) ? null : d;
  };

  const rows = [];
  for (let i = headerRowIdx + 1; i < raw.length; i++) {
    const row = raw[i];
    if (!row || row.every(c => !c && c !== 0)) continue; // skip blank rows

    const dt = parseDt(row[dtCol]);
    if (!dt) continue; // skip rows where datetime didn't parse

    const cons  = parseFloat(row[consCol]) || 0;
    const pv    = pvCol    !== -1 ? (parseFloat(row[pvCol])    || 0) : 0;
    const mains = mainsCol !== -1 ? (parseFloat(row[mainsCol]) || 0) : 0;

    if (cons === 0 && pv === 0 && mains === 0) continue; // skip summary/empty rows

    rows.push({ dt, hour:dt.getHours(), min:dt.getMinutes(), cons, pv, mains });
  }

  if (rows.length < 24) throw new Error(
    `Only ${rows.length} valid data rows found after parsing. ` +
    `Need at least 24. Check the file has a full day of readings. ` +
    `Detected headers: ${headers.filter(h=>h).join(", ")}`
  );

  // ── Step 4: detect interval ───────────────────────────────────────────────
  let intervalMins = 60;
  if (rows.length >= 2) {
    const diff = Math.round((rows[1].dt - rows[0].dt) / 60000);
    if (diff > 0 && diff <= 60) intervalMins = diff;
  }
  const hrsPerRow = intervalMins / 60;
  const days = new Set(rows.map(r => r.dt.toDateString())).size;

  // ── Step 5: hourly aggregation ────────────────────────────────────────────
  const buckets = Array.from({length:24}, () => ({cons:[],pv:[],mains:[]}));
  rows.forEach(r => {
    buckets[r.hour].cons.push(r.cons);
    buckets[r.hour].pv.push(r.pv);
    buckets[r.hour].mains.push(r.mains);
  });
  const avg = arr => arr.length ? arr.reduce((a,b)=>a+b,0)/arr.length : 0;
  const max = arr => arr.length ? Math.max(...arr) : 0;

  const hourlyAvg   = buckets.map(b => +avg(b.cons).toFixed(3));
  const hourlyMax   = buckets.map(b => +max(b.cons).toFixed(3));
  const hourlyPv    = buckets.map(b => +avg(b.pv).toFixed(3));
  const hourlyMains = buckets.map(b => +avg(b.mains).toFixed(3));

  const dailyKwh = +(rows.reduce((s,r)=>s+r.cons*hrsPerRow,0)  / days).toFixed(2);
  const pvKwh    = +(rows.reduce((s,r)=>s+r.pv*hrsPerRow,0)    / days).toFixed(2);
  const mainsKwh = +(rows.reduce((s,r)=>s+r.mains*hrsPerRow,0) / days).toFixed(2);

  const peakRow  = rows.reduce((a,b) => b.cons > a.cons ? b : a, rows[0]);
  const peakKw   = +peakRow.cons.toFixed(3);
  const peakTime = `${String(peakRow.dt.getHours()).padStart(2,"0")}:${String(peakRow.dt.getMinutes()).padStart(2,"0")}`;

  // ── Step 6: first-day raw rows for 5-min chart ────────────────────────────
  const firstDay = rows[0].dt.toDateString();
  const rawRows  = rows
    .filter(r => r.dt.toDateString() === firstDay)
    .map(r => ({
      time:  `${String(r.dt.getHours()).padStart(2,"0")}:${String(r.dt.getMinutes()).padStart(2,"0")}`,
      cons:  +r.cons.toFixed(3),
      pv:    +r.pv.toFixed(3),
      mains: +r.mains.toFixed(3),
    }));

  return {
    hourlyAvg, hourlyMax, hourlyPv, hourlyMains,
    dailyKwh, pvKwh, mainsKwh, peakKw, peakTime,
    days, rawRows, intervalMins, warnings,
    columns:{ dtCol, consCol, pvCol, mainsCol }, headers,
  };
}

// ── Blended tariff helper ────────────────────────────────────────────────────
// Computes the weighted average effective cost of electricity (grid + diesel)
// for a given year, accounting for separate escalation rates on each source.
function calcBlended(a, year=1) {
  // All values safe-parsed — || fallbacks prevent NaN cascading through financials
  const gridPct     = Math.min(1, Math.max(0, parseFloat(a.gridPct)          || 0.6));
  const dieselPct   = 1 - gridPct;
  const kplcRate    = calcKplcFromComponents(a);       // uses component formula if set, else kplcRate
  const tariffEsc   = parseFloat(a.tariffEscalation)  || 0.05;
  const dieselPrice = parseFloat(a.dieselPricePerL)   || 120;
  const dieselSfc   = parseFloat(a.dieselSfc)         || 0.35;
  const omFactor    = parseFloat(a.gensetOmFactor)    || 1.20;
  const dieselEsc   = parseFloat(a.dieselEscalation)  || 0.08;
  const kplcY       = kplcRate    * Math.pow(1 + tariffEsc,  year-1);
  const dieselBase  = dieselPrice * dieselSfc * omFactor;
  const dieselY     = dieselBase  * Math.pow(1 + dieselEsc,  year-1);
  const blended     = gridPct*kplcY + dieselPct*dieselY;
  return {
    blended:       isNaN(blended)  ? 0 : +blended.toFixed(4),
    gridCostKwh:   isNaN(kplcY)   ? 0 : +kplcY.toFixed(4),
    dieselCostKwh: isNaN(dieselY) ? 0 : +dieselY.toFixed(4),
    dieselPct, gridPct,
  };
}


const safeKes = (v, dec=2) => isNaN(v)||v==null ? "—" : Number(v).toFixed(dec);

// Compute effective KPLC tariff from component rates.
// Formula: (highRate + lowRate + FEC + FERFA + inflationAdj + ERClevy + WRAlevy) × 1.05
// If no components are set, falls back to the plain kplcRate field.
function calcKplcFromComponents(a) {
  const high  = parseFloat(a.highRateKwh)     || 0;
  const low   = parseFloat(a.lowRateKwh)      || 0;
  const fec   = parseFloat(a.fecKwh)          || 0;
  const ferfa = parseFloat(a.ferfaKwh)        || 0;
  const infl  = parseFloat(a.inflationAdjKwh) || 0;
  const erc   = parseFloat(a.ercLevyKwh)      || 0;
  const wra   = parseFloat(a.wraLevyKwh)      || 0;
  const sum   = high + low + fec + ferfa + infl + erc + wra;
  if (sum > 0.5) return +((sum) * 1.05).toFixed(4);
  return parseFloat(a.kplcRate) || 22.5;
}
// ─── SIMULATION ENGINE (C&I version) ─────────────────────────────────────────
// Uses actual measured hourly demand from the logger instead of equipment list.
function runSimulation(profile, site, a, irradiance) {
  const { hourlyAvg, dailyKwh, peakKw } = profile;
  const avgIrr = irradiance.reduce((s,v)=>s+v,0) / 12;

  // ── Operating calendar — effective annual days ────────────────────────────
  // A full 365-day assumption overstates consumption for sites closed weekends.
  // Effective days = weekdays×1.0 + saturdays×satFactor + (sundays+PHs)×sunFactor
  const satFactor  = parseFloat(a.satFactor)              || 0.55;
  const sunFactor  = parseFloat(a.sunFactor)              || 0.10;
  const numPH      = parseFloat(a.publicHolidaysPerYear)  || 10;
  const weekdays   = 261;                          // Mon–Fri in a typical year
  const saturdays  = 52;
  const sunAndPH   = 52 + numPH;                  // Sundays + public holidays
  const effectiveDays = weekdays * 1.0 + saturdays * satFactor + sunAndPH * sunFactor;
  // Effective demand multiplier vs 365-day assumption
  const calendarFactor = +(effectiveDays / 365).toFixed(6);

  // Night load = what has to come from BESS (no sun)
  const nightLoad = hourlyAvg.reduce((s,kw,h) => {
    // Night hours: before sunrise (~6) and after sunset (~18)
    return (h < 6 || h >= 18) ? s + kw : s;
  }, 0);
  const dayLoad = dailyKwh - nightLoad;

  // PV sizing — size to cover target self-sufficiency
  const pvKwp     = (dailyKwh * a.targetSelfSufficiency) / (avgIrr * a.systemEff);
  const numModules = Math.ceil((pvKwp * 1000) / a.moduleWp);
  const actualPvKwp= (numModules * a.moduleWp) / 1000;

  // BESS sizing — cover night load with DoD + degradation margins
  const bessDegF  = 1 - a.bessDeg10yr;
  const bessReq   = nightLoad / (a.bessDod * bessDegF * a.bessRoundTripEff);
  const bessUnits = Math.ceil(bessReq / a.bessUnitKwh);
  const bessActual= bessUnits * a.bessUnitKwh;

  // ── Financial ──────────────────────────────────────────────────────────────
  const annualKwh   = dailyKwh * effectiveDays;  // calendar-adjusted annual consumption
  const pvCostKes        = actualPvKwp * a.pvCostPerKwp;
  const bessCostKes      = bessActual  * a.bessCostPerKwh;
  const installPerPanel  = parseFloat(a.installationCostPerPanel) || 10;
  const installationCost = numModules  * installPerPanel;
  const totalCapex       = pvCostKes + bessCostKes + installationCost;

  // Blended effective tariff — weighted average of grid + diesel costs
  const b1               = calcBlended(a, 1);
  const blendedRateY1    = b1.blended;
  const dieselCostPerKwhY1 = b1.dieselCostKwh;

  // Year 1 savings: energy at blended rate + demand charge reduction
  const annualEnergySaving = annualKwh * a.targetSelfSufficiency * blendedRateY1;
  const annualDemandSaving = peakKw    * a.targetSelfSufficiency * (parseFloat(a.demandCharge)||800) * 12;
  const annualSavingsY1    = annualEnergySaving + annualDemandSaving - (parseFloat(a.omCostPerYearKes)||120000);

  // Multi-year cashflow — grid and diesel escalate at different rates each year
  let cumSavings = 0;
  const cumulativeArr  = [];
  const annualBreakdown = [];
  for (let y = 1; y <= (parseFloat(a.projectLifeYears)||25); y++) {
    const by       = calcBlended(a, y);
    const degraded = annualKwh * a.targetSelfSufficiency * Math.pow(1 - (parseFloat(a.pvDegradationPerYear)||0.005), y-1);
    const energySav= degraded  * by.blended;
    const demandSav= annualDemandSaving * Math.pow(1 + (parseFloat(a.tariffEscalation)||0.05), y-1);
    const omCost   = (parseFloat(a.omCostPerYearKes)||120000) * Math.pow(1 + (parseFloat(a.inflationRate)||0.06), y-1);
    const netSaving= energySav + demandSav - omCost;
    cumSavings    += netSaving;
    cumulativeArr.push({ year:`Y${y}`, cumulative: Math.round(cumSavings - totalCapex) });
    if (y <= 10) annualBreakdown.push({
      year:         `Y${y}`,
      blendedRate:  +by.blended.toFixed(2),
      gridRate:     +by.gridCostKwh.toFixed(2),
      dieselRate:   +by.dieselCostKwh.toFixed(2),
      energySaving: Math.round(energySav),
      demandSaving: Math.round(demandSav),
      netSaving:    Math.round(netSaving),
    });
  }
  const paybackYears = annualSavingsY1 > 0 ? totalCapex / annualSavingsY1 : 0;
  const npv25        = cumSavings - totalCapex;  // undiscounted (cost-basis)
  const roi25yr      = totalCapex > 0 ? (npv25 / totalCapex) * 100 : 0;

  // ── IRR (Internal Rate of Return) — Newton-Raphson on project cashflows ──
  // Cashflows: Year 0 = -CAPEX, Years 1..N = net annual savings
  // Build full annual savings series for IRR calc
  const projectYears = parseFloat(a.projectLifeYears) || 25;
  const cashflows = [-totalCapex];
  for (let y = 1; y <= projectYears; y++) {
    const by       = calcBlended(a, y);
    const degraded = annualKwh * a.targetSelfSufficiency * Math.pow(1-(parseFloat(a.pvDegradationPerYear)||0.005), y-1);
    const es = degraded * by.blended;
    const ds = annualDemandSaving * Math.pow(1+(parseFloat(a.tariffEscalation)||0.05), y-1);
    const om = (parseFloat(a.omCostPerYearKes)||120000) * Math.pow(1+(parseFloat(a.inflationRate)||0.06), y-1);
    cashflows.push(es + ds - om);
  }
  const calcNPVAtRate = (rate) => cashflows.reduce((npv, cf, t) => npv + cf / Math.pow(1+rate, t), 0);
  let irrRate = 0.15; // initial guess 15%
  for (let i = 0; i < 100; i++) {
    const npvVal  = calcNPVAtRate(irrRate);
    const npvDeriv= cashflows.reduce((s,cf,t)=> t===0?s : s - t*cf/Math.pow(1+irrRate,t+1), 0);
    if (Math.abs(npvDeriv) < 1e-10) break;
    const newRate = irrRate - npvVal / npvDeriv;
    if (Math.abs(newRate - irrRate) < 1e-8) { irrRate = newRate; break; }
    irrRate = Math.max(-0.99, Math.min(10, newRate)); // clamp to sane range
  }
  const irrPct = calcNPVAtRate(irrRate) < 500 ? +(irrRate * 100).toFixed(1) : null; // null if no convergence

  // ── Discounted NPV at user-configurable discount rate ─────────────────────
  const discountRate = parseFloat(a.discountRate) || 0.10; // default 10%
  const npvDiscounted = +calcNPVAtRate(discountRate).toFixed(0);

  // Hourly dispatch (two-pass for steady-state battery)
  const minBatt = bessActual * (1 - a.bessDod);

  // Build hourly solar generation from irradiance fractions
  const HOURLY_IRR_FRAC = [0,0,0,0,0,0,0.02,0.07,0.13,0.17,0.19,0.19,0.18,0.15,0.11,0.07,0.03,0.01,0,0,0,0,0,0];
  const IRR_FRAC_SUM    = HOURLY_IRR_FRAC.reduce((a,b)=>a+b,0); // normalise so fractions sum to 1
  // Each hour's generation: kWp × (fraction of day's irradiance) × avgIrr × efficiency
  // Capped at kWp (physical maximum) to handle any rounding edge cases
  const hourlyGenKw = HOURLY_IRR_FRAC.map(f =>
    +Math.min(actualPvKwp, actualPvKwp * (f/IRR_FRAC_SUM) * avgIrr * a.systemEff).toFixed(3)
  );

  const runDispatch = (startLevel) => {
    let batt = startLevel;
    return Array.from({length:24}, (_,h) => {
      const loadKw  = hourlyAvg[h];
      const solarKw = hourlyGenKw[h];
      const net = solarKw - loadKw;
      let gridKw = 0, battDelta = 0;
      if (net >= 0) {
        battDelta = Math.min(bessActual - batt, net * a.bessRoundTripEff);
        batt += battDelta;
      } else {
        const deficit = -net;
        if (batt > minBatt) {
          const d = Math.min(deficit, batt - minBatt);
          batt   -= d;
          gridKw  = Math.max(0, deficit - d / a.bessRoundTripEff);
        } else {
          gridKw = deficit;
        }
      }
      return { hour:`${h}:00`, load:+loadKw.toFixed(2), solar:+solarKw.toFixed(2),
               battery:+batt.toFixed(2), grid:+gridKw.toFixed(2), endBatt:batt };
    });
  };

  const pass1      = runDispatch(bessActual * a.bessInitSoc);
  const hourlyData = runDispatch(pass1[23].endBatt);

  // Self-sufficiency achieved
  const totalGridImport    = hourlyData.reduce((s,r)=>s+r.grid,0);
  const selfSufficiencyPct = +((1 - totalGridImport / dailyKwh) * 100).toFixed(1);
  const solarCoveredKwh    = +(dailyKwh - totalGridImport).toFixed(2);

  const monthlyData = MONTH_NAMES.map((m,i) => ({
    month: m,
    generation: +(actualPvKwp * irradiance[i] * 30 * a.systemEff).toFixed(0),
    demand:      +(dailyKwh * 30 * calendarFactor).toFixed(0), // calendar-adjusted
    irradiance:  irradiance[i],
  }));

  return {
    dailyKwh:+dailyKwh.toFixed(2), dayLoad:+dayLoad.toFixed(2), nightLoad:+nightLoad.toFixed(2),
    pvKwp:+pvKwp.toFixed(2), actualPvKwp:+actualPvKwp.toFixed(2), numModules,
    bessRequired:+bessReq.toFixed(2), bessActual, bessUnits,
    annualKwh:+annualKwh.toFixed(0),
    effectiveDays:+effectiveDays.toFixed(1),
    calendarFactor:+calendarFactor.toFixed(4),
    annualEnergySaving:+annualEnergySaving.toFixed(0),
    annualDemandSaving:+annualDemandSaving.toFixed(0),
    annualSavingsY1:+annualSavingsY1.toFixed(0),
    totalCapex:+totalCapex.toFixed(0),
    pvCostKes:+pvCostKes.toFixed(0), bessCostKes:+bessCostKes.toFixed(0), installationCost:+installationCost.toFixed(0),
    paybackYears:+paybackYears.toFixed(1), roi25yr:+roi25yr.toFixed(0), npv25:+npv25.toFixed(0),
    irrPct,          // IRR % — null if cashflows don't converge (e.g. very long payback)
    npvDiscounted,   // NPV at discountRate (default 10%)
    cumSavingsTotal:+cumSavings.toFixed(0), // raw lifetime savings — offer tab uses this for client NPV
    co2Annual:+(annualKwh * a.targetSelfSufficiency * (parseFloat(a.co2FactorKgPerKwh)||0.5) / 1000).toFixed(1),
    selfSufficiencyPct, solarCoveredKwh, totalGridImport:+totalGridImport.toFixed(2),
    avgIrr:+avgIrr.toFixed(2), hourlyData, hourlyGenKw, monthlyData, cumulativeArr, annualBreakdown,
    peakKw:         profile.peakKw,
    bessActualKwh:  bessActual,
    blendedRateY1:  +blendedRateY1.toFixed(2),
    dieselCostPerKwhY1: +dieselCostPerKwhY1.toFixed(2),
    gridPct:        b1.gridPct,
    gridRateY1:     +b1.gridCostKwh.toFixed(2),
  };
}

// ─── PVGIS FETCH (multi-proxy + built-in table fallback) ────────────────────
// Embedded irradiance table — used when all live API routes fail.
// Monthly avg kWh/m²/day from PVGIS-SARAH2 for major African cities.
const PVGIS_TABLE = {
  nairobi:      {lat:-1.29, lng:36.82, monthly:[5.49,5.83,5.67,5.21,4.84,4.54,4.41,4.72,5.32,5.54,5.38,5.27], annual:5.10},
  narok:        {lat:-1.08, lng:35.87, monthly:[5.82,6.12,5.97,5.52,5.07,4.79,4.62,5.02,5.72,5.89,5.68,5.62], annual:5.49},
  mombasa:      {lat:-4.05, lng:39.67, monthly:[6.12,6.45,6.21,5.74,5.28,4.81,4.63,4.92,5.61,5.95,5.88,5.98], annual:5.63},
  nakuru:       {lat:-0.28, lng:36.07, monthly:[5.71,6.02,5.89,5.44,4.98,4.68,4.52,4.92,5.60,5.77,5.59,5.51], annual:5.39},
  kisumu:       {lat:-0.10, lng:34.75, monthly:[5.42,5.74,5.58,5.12,4.66,4.38,4.21,4.61,5.31,5.49,5.30,5.22], annual:5.09},
  eldoret:      {lat: 0.52, lng:35.27, monthly:[5.61,5.92,5.78,5.33,4.87,4.57,4.41,4.81,5.50,5.68,5.49,5.41], annual:5.28},
  thika:        {lat:-1.03, lng:37.08, monthly:[5.45,5.78,5.63,5.18,4.72,4.43,4.27,4.67,5.37,5.55,5.36,5.28], annual:5.14},
  dar_es_salaam:{lat:-6.77, lng:39.27, monthly:[5.97,6.18,5.87,5.42,5.12,4.71,4.52,4.87,5.48,5.83,5.92,5.95], annual:5.49},
  arusha:       {lat:-3.37, lng:36.68, monthly:[6.21,6.45,6.18,5.74,5.31,4.91,4.72,5.12,5.78,6.01,5.97,6.08], annual:5.71},
  kampala:      {lat: 0.32, lng:32.57, monthly:[5.23,5.54,5.38,4.93,4.47,4.17,4.01,4.41,5.11,5.29,5.10,5.02], annual:4.89},
  kigali:       {lat:-1.94, lng:30.06, monthly:[5.38,5.68,5.52,5.07,4.61,4.31,4.15,4.55,5.25,5.43,5.24,5.16], annual:5.03},
  addis_ababa:  {lat: 8.99, lng:38.75, monthly:[6.12,6.38,6.18,5.84,5.61,5.21,4.87,5.12,5.72,6.01,5.98,6.04], annual:5.76},
  lagos:        {lat: 6.52, lng: 3.38, monthly:[5.72,5.98,5.71,5.28,4.82,4.32,3.91,4.12,5.01,5.48,5.67,5.71], annual:5.15},
  abuja:        {lat: 9.07, lng: 7.40, monthly:[6.42,6.71,6.48,6.05,5.61,5.08,4.62,4.87,5.68,6.08,6.31,6.38], annual:5.94},
  johannesburg: {lat:-26.20,lng:28.04, monthly:[6.48,6.21,5.98,5.42,4.87,4.52,4.68,5.18,5.78,6.12,6.31,6.42], annual:5.67},
  cape_town:    {lat:-33.93,lng:18.42, monthly:[7.21,6.87,6.12,5.14,4.21,3.78,3.98,4.72,5.68,6.42,6.98,7.18], annual:5.69},
  accra:        {lat: 5.55, lng:-0.20, monthly:[5.89,6.12,5.87,5.44,4.98,4.48,4.02,4.27,5.18,5.68,5.82,5.87], annual:5.30},
};
function nearestTableEntry(lat,lng){
  let best=null,bestDist=Infinity;
  Object.entries(PVGIS_TABLE).forEach(([name,loc])=>{
    const d=Math.sqrt((loc.lat-lat)**2+(loc.lng-lng)**2);
    if(d<bestDist){bestDist=d;best={name,loc,dist:d};}
  });
  return best;
}
async function fetchPVGIS(lat, lng) {
  const base=`https://re.jrc.ec.europa.eu/api/v5_2/PVcalc?lat=${lat}&lon=${lng}&peakpower=1&loss=14&outputformat=json&raddatabase=PVGIS-SARAH2`;
  const proxies=[
    base,
    `https://corsproxy.io/?${encodeURIComponent(base)}`,
    `https://api.allorigins.win/raw?url=${encodeURIComponent(base)}`,
    `https://thingproxy.freeboard.io/fetch/${base}`,
  ];
  const tryParse=async(url)=>{
    const ctrl=new AbortController();
    const timer=setTimeout(()=>ctrl.abort(),7000);
    try{
      const res=await fetch(url,{signal:ctrl.signal});
      clearTimeout(timer);
      if(!res.ok) throw new Error(`HTTP ${res.status}`);
      const raw=await res.json();
      const data=raw.contents?JSON.parse(raw.contents):raw;
      if(!data?.outputs?.monthly?.fixed) throw new Error("Unexpected structure");
      const monthly=data.outputs.monthly.fixed;
      return{monthly:monthly.map(m=>+(m['H(i)_m']/30).toFixed(2)),annual:+(data.outputs.totals.fixed['H(i)_y']/365).toFixed(2),source:"PVGIS-SARAH2 (live)",location:`${(+lat).toFixed(4)}, ${(+lng).toFixed(4)}`,fromTable:false};
    }catch(e){clearTimeout(timer);throw e;}
  };
  for(const url of proxies){try{return await tryParse(url);}catch(_){continue;}}
  // All live routes failed — nearest city from built-in table
  const nearest=nearestTableEntry(+lat,+lng);
  if(nearest&&nearest.dist<5){
    return{monthly:nearest.loc.monthly,annual:nearest.loc.annual,source:`PVGIS table (nearest: ${nearest.name.replace(/_/g," ")}, ${nearest.dist.toFixed(2)}° away)`,location:`${(+lat).toFixed(4)}, ${(+lng).toFixed(4)}`,fromTable:true};
  }
  const latAbs=Math.abs(+lat);
  const generic=latAbs<5?[5.4,5.7,5.6,5.2,4.8,4.5,4.3,4.6,5.2,5.4,5.3,5.3]:latAbs<15?[5.9,6.2,6.0,5.6,5.1,4.7,4.5,4.9,5.5,5.8,5.7,5.8]:[6.2,6.4,6.1,5.6,5.0,4.6,4.7,5.2,5.8,6.1,6.1,6.2];
  return{monthly:generic,annual:+(generic.reduce((a,b)=>a+b,0)/12).toFixed(2),source:`Estimated from latitude (live data unavailable)`,location:`${(+lat).toFixed(4)}, ${(+lng).toFixed(4)}`,fromTable:false};
}

// ─── FILE UPLOAD ZONE ────────────────────────────────────────────────────────
function UploadZone({ onParsed }) {
  const [status, setStatus]   = useState("idle"); // idle|parsing|ok|error
  const [error,  setError]    = useState("");
  const [parsed, setParsed]   = useState(null);
  const fileRef = useRef();

  const handle = useCallback(async (file) => {
    if (!file) return;
    setStatus("parsing"); setError("");
    try {
      const result = await parseLoggerFile(file);
      setParsed(result);
      setStatus("ok");
      onParsed(result, file.name);
    } catch(e) {
      setError(e.message);
      setStatus("error");
    }
  }, [onParsed]);

  const onDrop = e => { e.preventDefault(); handle(e.dataTransfer.files[0]); };
  const onInput= e => handle(e.target.files[0]);

  return (
    <div>
      <div
        onDrop={onDrop} onDragOver={e=>e.preventDefault()}
        onClick={() => fileRef.current.click()}
        style={{
          border:`2px dashed ${status==="ok"?C.green:status==="error"?C.red:C.border}`,
          borderRadius:"6px", padding:"32px 20px", textAlign:"center", cursor:"pointer",
          background: status==="ok"?`${C.lt}22`:status==="error"?"#fef2f2":C.deep,
          transition:"all 0.2s",
        }}>
        <input ref={fileRef} type="file" accept=".xlsx,.xls,.csv" onChange={onInput} style={{display:"none"}}/>
        {status==="idle" && <>
          <div style={{fontSize:"24px",marginBottom:"10px",opacity:0.4}}>↑</div>
          <div style={{fontSize:"12px",fontWeight:700,color:C.dim,marginBottom:"6px"}}>Drop power logger file here</div>
          <div style={{fontSize:"10px",color:C.muted}}>Supports Fusion Solar, SMA, SolarEdge, Solis, or any Excel/CSV with datetime + kW columns</div>
        </>}
        {status==="parsing" && <div style={{fontSize:"12px",color:C.mid}}>⟳ Parsing file…</div>}
        {status==="ok" && parsed && <>
          <div style={{fontSize:"12px",fontWeight:700,color:C.green,marginBottom:"8px"}}>✓ File parsed successfully</div>
          <div style={{display:"grid",gridTemplateColumns:"repeat(4,1fr)",gap:"10px",textAlign:"center"}}>
            {[["Daily demand",`${parsed.dailyKwh} kWh`],["Peak demand",`${parsed.peakKw} kW`],["Data interval",`${parsed.intervalMins} min`],["Days logged",parsed.days]].map(([l,v])=>(
              <div key={l} style={{background:C.surface,borderRadius:"3px",padding:"8px",border:`1px solid ${C.border}`}}>
                <div style={{fontSize:"9px",color:C.muted,marginBottom:"3px"}}>{l}</div>
                <div style={{fontSize:"13px",fontWeight:700,color:C.green}}>{v}</div>
              </div>
            ))}
          </div>
          {parsed.pvKwh > 0 && <div style={{marginTop:"8px",fontSize:"10px",color:C.amber}}>⚡ Existing solar detected: {parsed.pvKwh} kWh/day on-site generation</div>}
          {parsed.warnings.map((w,i)=><div key={i} style={{marginTop:"4px",fontSize:"9px",color:C.muted}}>ⓘ {w}</div>)}
          <div style={{marginTop:"10px",fontSize:"9px",color:C.muted,cursor:"pointer"}} onClick={e=>{e.stopPropagation();setStatus("idle");setParsed(null);onParsed(null,"");}}>↺ Upload different file</div>
        </>}
        {status==="error" && <>
          <div style={{fontSize:"12px",fontWeight:700,color:C.red,marginBottom:"6px"}}>✕ Parse failed</div>
          <div style={{fontSize:"10px",color:C.muted,marginBottom:"8px"}}>{error}</div>
          <div style={{fontSize:"10px",color:C.muted}}>Click to try another file</div>
        </>}
      </div>

      {/* Column mapping preview */}
      {status==="ok" && parsed && (
        <div style={{marginTop:"12px"}}>
          <div style={{...S.sec,marginBottom:"8px"}}>Detected columns</div>
          <div style={{display:"flex",gap:"8px",flexWrap:"wrap"}}>
            {[
              ["Datetime",    parsed.headers[parsed.columns.dtCol],   "blue"],
              ["Consumption", parsed.headers[parsed.columns.consCol], "green"],
              ["PV output",   parsed.columns.pvCol   !==-1 ? parsed.headers[parsed.columns.pvCol]    : "not found", parsed.columns.pvCol   !==-1?"amber":"red"],
              ["Mains import",parsed.columns.mainsCol!==-1 ? parsed.headers[parsed.columns.mainsCol] : "not found", parsed.columns.mainsCol!==-1?"amber":"red"],
            ].map(([label,col,color])=>(
              <div key={label} style={{fontSize:"10px"}}>
                <span style={{color:C.muted}}>{label}: </span>
                <span style={S.badge(color)}>{col}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ─── ASSUMPTIONS FIELD (module scope — prevents focus loss) ──────────────────
function AField({id,label,unit="",hint="",value,onUpdate}){
  return(
    <div>
      <label style={S.label}>{label}{unit&&<span style={{color:C.muted,marginLeft:"4px",fontSize:"9px"}}>({unit})</span>}</label>
      <input style={S.input} value={value??""} onChange={e=>onUpdate(id,e.target.value)}/>
      {hint&&<div style={{fontSize:"9px",color:C.muted,marginTop:"3px",lineHeight:1.3}}>{hint}</div>}
    </div>
  );
}
function ASection({title,cols=3,children}){
  return(
    <div style={{...S.card,marginBottom:"14px"}}>
      <div style={S.sec}>{title}</div>
      <div style={{display:"grid",gridTemplateColumns:`repeat(${cols},1fr)`,gap:"13px"}}>{children}</div>
    </div>
  );
}


// ─── C&I OFFER TAB ───────────────────────────────────────────────────────────
function CIOfferTab({results, site, assumptions, diesel, usdRate, setUsdRate, updAsm}){
  const [view,       setView]      = useState("internal");
  const [margin,     setMargin]    = useState(20);
  const [currency,   setCurrency]  = useState("KES");
  const [validity,   setValidity]  = useState(30);
  const [refNo,      setRefNo]     = useState(`QT-${new Date().getFullYear()}-${String(Math.floor(Math.random()*9000)+1000)}`);
  const [preparedBy, setPreparedBy]= useState("");
  const [deposit1,   setDeposit1]  = useState(40);
  const [deposit2,   setDeposit2]  = useState(40);

  // C&I-specific BOM — 7 sections
  const pvFarmBase  = results.actualPvKwp * parseFloat(assumptions.pvCostPerKwp||95000) * 0.65;
  const bessBase    = results.bessActual  * parseFloat(assumptions.bessCostPerKwh||75000);
  const mountBase   = results.actualPvKwp * parseFloat(assumptions.pvCostPerKwp||95000) * 0.10;
  const cableBase   = results.actualPvKwp * parseFloat(assumptions.pvCostPerKwp||95000) * 0.07;
  const protBase    = results.actualPvKwp * parseFloat(assumptions.pvCostPerKwp||95000) * 0.04;
  const installCost = results.installationCost || (results.numModules * parseFloat(assumptions.installationCostPerPanel||10));
  const labourBase  = installCost * 0.65;
  const pgBase      = installCost * 0.35;

  const [lines, setLines] = useState([
    { id:1, section:"Solar PV Farm",
      desc:`${results.actualPvKwp} kWp grid-tied C&I solar system`,
      items:[
        { name:`Solar PV Modules (${assumptions.moduleWp||580}Wp, Tier-1 Mono-PERC)`, qty:results.numModules, unit:"No.", costEach: pvFarmBase*0.58/Math.max(1,results.numModules) },
        { name:`Hybrid Solar Inverter / Charger, ${Math.ceil(results.actualPvKwp)} kW`, qty:1, unit:"No.", costEach: pvFarmBase*0.28 },
        { name:"Monitoring & SCADA System (5-yr subscription)", qty:1, unit:"No.", costEach: pvFarmBase*0.07 },
        { name:"PV System Accessories & Consumables", qty:1, unit:"Lot", costEach: pvFarmBase*0.07 },
      ]},
    { id:2, section:"Battery Energy Storage System (BESS)",
      desc:`${results.bessActual} kWh usable capacity`,
      items:[
        { name:`LiFePO₄ Battery Modules, ${assumptions.bessUnitKwh||100} kWh each`, qty:results.bessUnits, unit:"No.", costEach: bessBase*0.80/Math.max(1,results.bessUnits) },
        { name:"BESS Enclosure / Cabinet with BMS", qty:Math.max(1,Math.ceil(results.bessUnits/2)), unit:"No.", costEach: bessBase*0.13/Math.max(1,Math.ceil(results.bessUnits/2)) },
        { name:"BESS Accessories, Fusing & Integration", qty:1, unit:"Lot", costEach: bessBase*0.07 },
      ]},
    { id:3, section:"Aluminium Module Mounting Framing System",
      desc:"Anodised aluminium ground / roof mount structure",
      items:[
        { name:"Anodised Aluminium Rail & Clamp System", qty:results.numModules, unit:"panel", costEach: mountBase*0.65/Math.max(1,results.numModules) },
        { name:"Hot-Dip Galvanised Steel Subframe & Posts", qty:1, unit:"Lot", costEach: mountBase*0.25 },
        { name:"Fasteners, Brackets & Mounting Hardware", qty:1, unit:"Lot", costEach: mountBase*0.10 },
      ]},
    { id:4, section:"DC / AC Cabling",
      desc:"Full cabling supply from modules to distribution board",
      items:[
        { name:"DC Solar Cable, 6mm² (UV-resistant, TÜV rated)", qty:1, unit:"Lot", costEach: cableBase*0.35 },
        { name:"AC Power Cable — main feed & sub-circuits", qty:1, unit:"Lot", costEach: cableBase*0.30 },
        { name:"Cable Trays, Conduit & Containment", qty:1, unit:"Lot", costEach: cableBase*0.20 },
        { name:"Cable Lugs, Connectors & Termination Kit", qty:1, unit:"Lot", costEach: cableBase*0.15 },
      ]},
    { id:5, section:"All Circuit & Electrical Protections",
      desc:"Full protection suite — DC and AC sides",
      items:[
        { name:"DC Combiner Box with String Fusing & MCBs", qty:1, unit:"No.", costEach: protBase*0.30 },
        { name:"AC Distribution Board with MCBs & RCDs", qty:1, unit:"No.", costEach: protBase*0.25 },
        { name:"DC & AC Surge Protection Devices (SPDs)", qty:1, unit:"Set", costEach: protBase*0.20 },
        { name:"Earthing & Lightning Protection System", qty:1, unit:"Lot", costEach: protBase*0.25 },
      ]},
    { id:6, section:"Labour, Installation, Transport & Technical Services",
      desc:"End-to-end C&I project delivery",
      items:[
        { name:"Site Survey, Engineering Design & Drawings", qty:1, unit:"Lot", costEach: labourBase*0.12 },
        { name:"Mechanical & Structural Installation", qty:1, unit:"Lot", costEach: labourBase*0.28 },
        { name:"Electrical Installation & Wiring", qty:1, unit:"Lot", costEach: labourBase*0.30 },
        { name:"System Commissioning, Testing & Handover", qty:1, unit:"Lot", costEach: labourBase*0.15 },
        { name:"Transport, Logistics & Equipment Delivery", qty:1, unit:"Lot", costEach: labourBase*0.15 },
      ]},
    { id:7, section:"Preliminaries & Generals",
      desc:"Project overheads, compliance & warranty",
      items:[
        { name:"Project Management & Site Supervision", qty:1, unit:"Lot", costEach: pgBase*0.35 },
        { name:"Safety, Health & Environment (SHE) Plan", qty:1, unit:"Lot", costEach: pgBase*0.10 },
        { name:"Permits, Approvals & Utility Notifications", qty:1, unit:"Lot", costEach: pgBase*0.20 },
        { name:"Insurance (Contractors All-Risk)", qty:1, unit:"Lot", costEach: pgBase*0.15 },
        { name:"As-Built Drawings & O&M Manual", qty:1, unit:"Lot", costEach: pgBase*0.10 },
        { name:"2-Year Maintenance & Warranty Support", qty:1, unit:"Lot", costEach: pgBase*0.10 },
      ]},
  ]);

  const fx  = currency==="USD" ? 1/usdRate : 1;
  const sym = currency==="USD" ? "USD" : "KES";
  const fmt = v => `${sym} ${Math.round(v*fx).toLocaleString()}`;

  const sectionCost = lines.map(l=>l.items.reduce((s,i)=>s+i.qty*i.costEach,0));
  const totalCost   = sectionCost.reduce((a,b)=>a+b,0);
  const sectionSell = sectionCost.map(c=>c/(1-margin/100));
  const totalSell   = sectionSell.reduce((a,b)=>a+b,0);
  const totalVat    = totalSell*0.06;
  const totalInclVat= totalSell+totalVat;
  const grossProfit = totalSell-totalCost;
  const dep3        = Math.max(0,100-deposit1-deposit2);

  const milestones = [
    { label:"1st Payment — Contract Signing & Mobilisation", pct:deposit1, note:"Enables procurement & site mobilisation" },
    { label:"2nd Payment — Equipment Delivery on Site",      pct:deposit2, note:"On confirmed delivery of all major equipment" },
    { label:"3rd Payment — Practical Completion",            pct:dep3,     note:"On commissioning sign-off" },
  ];

  const updItem=(si,ii,val)=>setLines(prev=>{
    const n=prev.map(l=>({...l,items:[...l.items]}));
    const num=parseFloat(val);
    n[si].items[ii]={...n[si].items[ii],costEach:isNaN(num)?val:num};
    return n;
  });

  const SC=["blue","green","amber","blue","red","green","amber"];

  return(
    <div>
      {/* Controls */}
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:"14px",flexWrap:"wrap",gap:"10px"}}>
        <div style={{display:"flex"}}>
          {[["internal","⚙ Internal View"],["client","◻ Client Proposal"]].map(([k,l])=>(
            <button key={k} onClick={()=>setView(k)} style={{padding:"8px 18px",fontSize:"10px",letterSpacing:"0.12em",textTransform:"uppercase",border:`1px solid ${C.border}`,cursor:"pointer",fontFamily:"inherit",background:view===k?C.navy:C.deep,color:view===k?"#fff":C.muted,borderRadius:k==="internal"?"3px 0 0 3px":"0 3px 3px 0"}}>{l}</button>
          ))}
        </div>
        <div style={{display:"flex",gap:"8px",alignItems:"center",flexWrap:"wrap"}}>
          {["KES","USD"].map(c=>(
            <button key={c} onClick={()=>setCurrency(c)} style={{padding:"5px 12px",fontSize:"10px",border:`1px solid ${C.border}`,borderRadius:"3px",cursor:"pointer",fontFamily:"inherit",background:currency===c?C.navy:C.deep,color:currency===c?"#fff":C.muted}}>{c}</button>
          ))}
          {currency==="USD"&&<div style={{display:"flex",alignItems:"center",gap:"5px"}}><span style={{fontSize:"10px",color:C.muted}}>Rate:</span><input style={{...S.input,width:"68px",padding:"4px 7px"}} type="text" inputMode="numeric" value={usdRate} onChange={e=>setUsdRate(+e.target.value||130)}/><span style={{fontSize:"10px",color:C.muted}}>KES/USD</span></div>}
          <button
            onClick={async () => {
              try {
                const clientPayback = results.annualSavingsY1 > 0 ? +(totalInclVat / results.annualSavingsY1).toFixed(1) : null;
                const clientNpv = (results.cumSavingsTotal || results.npv25 || 0) - totalInclVat;
                await generateProposal({
                  site, assumptions, results, irrMeta: results.irrMeta,
                  refNo, validity, preparedBy,
                  totalExclVat: totalSell, vat: totalVat, totalInclVat,
                  paybackYears: clientPayback, npv25Client: clientNpv,
                  deposit1, deposit2,
                });
              } catch (err) {
                alert("Could not generate proposal: " + (err?.message || err));
              }
            }}
            style={{padding:"7px 14px",fontSize:"10px",letterSpacing:"0.12em",textTransform:"uppercase",border:"none",borderRadius:"3px",cursor:"pointer",fontFamily:"inherit",background:C.red,color:"#fff",fontWeight:700}}
            title="Download a Word .docx proposal built from these results"
          >
            ⬇ Generate Proposal (.docx)
          </button>
        </div>
      </div>

      {/* ═════ INTERNAL VIEW ═════ */}
      {view==="internal"&&(
        <div>
          <div style={{...S.card,marginBottom:"14px"}}>
            <div style={S.sec}>Pricing Controls — Internal Only</div>
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr 3fr",gap:"16px",alignItems:"start"}}>
              <div>
                <label style={S.label}>Gross Margin</label>
                <div style={{display:"flex",alignItems:"center",gap:"8px",marginTop:"4px"}}>
                  <input type="range" min="5" max="50" value={margin} onChange={e=>setMargin(+e.target.value)} style={{flex:1}}/>
                  <span style={{fontSize:"16px",fontWeight:700,color:C.green,minWidth:"38px"}}>{margin}%</span>
                </div>
              </div>
              <div><label style={S.label}>Quote Reference</label><input style={S.input} value={refNo} onChange={e=>setRefNo(e.target.value)}/></div>
              <div><label style={S.label}>Validity (days)</label><input style={S.input} type="text" inputMode="numeric" value={validity} onChange={e=>setValidity(+e.target.value||30)}/></div>
              <div style={{display:"grid",gridTemplateColumns:"repeat(3,1fr)",gap:"9px"}}>
                {[["Cost base",fmt(totalCost),C.amber],["Sell ex-VAT",fmt(totalSell),C.mid],["VAT 6%",fmt(totalVat),C.muted],["Total incl. VAT",fmt(totalInclVat),C.green],["Gross profit",fmt(grossProfit),C.green],["Markup",`${(grossProfit/totalCost*100).toFixed(1)}%`,C.dim]].map(([k,v,col])=>(
                  <div key={k} style={{padding:"8px 10px",background:C.deep,borderRadius:"3px",border:`1px solid ${C.border}`}}>
                    <div style={{fontSize:"9px",color:C.muted,letterSpacing:"0.08em",marginBottom:"2px"}}>{k.toUpperCase()}</div>
                    <div style={{fontSize:"13px",fontWeight:700,color:col}}>{v}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div style={{...S.card,marginBottom:"14px"}}>
            <div style={S.sec}>Bill of Materials — 7-Section Breakdown</div>
            {lines.map((sec,si)=>{
              const secCost=sec.items.reduce((s,i)=>s+i.qty*i.costEach,0);
              const secSell=secCost/(1-margin/100);
              return(
                <div key={sec.id} style={{marginBottom:"14px",border:`1px solid ${C.border}`,borderRadius:"4px",overflow:"hidden"}}>
                  <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",padding:"9px 14px",background:"#f8fafc",borderBottom:`1px solid ${C.border}`}}>
                    <div style={{display:"flex",alignItems:"center",gap:"10px"}}>
                      <span style={S.badge(SC[si])}>{si+1}</span>
                      <div>
                        <div style={{fontSize:"11px",fontWeight:700,color:C.dim}}>{sec.section}</div>
                        <div style={{fontSize:"9px",color:C.muted,marginTop:"1px"}}>{sec.desc}</div>
                      </div>
                    </div>
                    <div style={{textAlign:"right"}}>
                      <div style={{fontSize:"9px",color:C.muted}}>Sell (ex-VAT)</div>
                      <div style={{fontSize:"13px",fontWeight:700,color:C.mid}}>{fmt(secSell)}</div>
                    </div>
                  </div>
                  <table style={{width:"100%",borderCollapse:"collapse",fontSize:"10px"}}>
                    <thead><tr style={{background:"#f8fafc"}}>
                      <th style={{...S.th,width:"42%"}}>Description</th>
                      <th style={{...S.th,textAlign:"center"}}>Qty</th>
                      <th style={{...S.th,textAlign:"center"}}>Unit</th>
                      <th style={{...S.th,textAlign:"right"}}>Unit Cost (KES)</th>
                      <th style={{...S.th,textAlign:"right"}}>Line Cost</th>
                      <th style={{...S.th,textAlign:"right"}}>Line Sell</th>
                    </tr></thead>
                    <tbody>
                      {sec.items.map((item,ii)=>{
                        const lc=item.qty*item.costEach;
                        const ls=lc/(1-margin/100);
                        return(
                          <tr key={ii}>
                            <td style={S.td}>{item.name}</td>
                            <td style={{...S.td,textAlign:"center"}}>{item.qty}</td>
                            <td style={{...S.td,textAlign:"center",color:C.muted}}>{item.unit}</td>
                            <td style={{...S.td,textAlign:"right",padding:"4px 9px"}}>
                              <input type="text" inputMode="numeric" value={Math.round(item.costEach)} onChange={e=>updItem(si,ii,e.target.value)} style={{...S.input,width:"110px",textAlign:"right",padding:"3px 7px",fontSize:"10px"}}/>
                            </td>
                            <td style={{...S.td,textAlign:"right",color:C.amber}}>{fmt(lc)}</td>
                            <td style={{...S.td,textAlign:"right",color:C.mid}}>{fmt(ls)}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                    <tfoot>
                      <tr style={{background:"#f8fafc"}}>
                        <td colSpan={4} style={{...S.td,color:C.muted,fontWeight:700}}>Section total</td>
                        <td style={{...S.td,textAlign:"right",color:C.amber,fontWeight:700}}>{fmt(secCost)}</td>
                        <td style={{...S.td,textAlign:"right",color:C.mid,fontWeight:700}}>{fmt(secSell)}</td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              );
            })}
            <div style={{background:"#f8fafc",border:`1px solid ${C.border}`,borderRadius:"4px",padding:"14px 16px"}}>
              <div style={{display:"grid",gridTemplateColumns:"repeat(4,1fr)",gap:"12px"}}>
                {[["Total Cost Base",fmt(totalCost),C.amber],["Total Sell (ex-VAT)",fmt(totalSell),C.mid],["VAT 6%",fmt(totalVat),C.muted],["GRAND TOTAL incl. VAT",fmt(totalInclVat),C.green]].map(([k,v,col])=>(
                  <div key={k} style={{textAlign:"right"}}>
                    <div style={{fontSize:"9px",color:C.muted}}>{k}</div>
                    <div style={{fontSize:"15px",fontWeight:700,color:col,marginTop:"3px"}}>{v}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ═════ CLIENT PROPOSAL ═════ */}
      {view==="client"&&(
        <div>
          {/* Header */}
          <div style={{...S.card,marginBottom:"14px",border:`1px solid ${C.lt}`}}>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:"18px",flexWrap:"wrap",gap:"16px"}}>
              <div>
                <div style={{fontSize:"20px",fontWeight:700,color:C.mid,letterSpacing:"0.04em",marginBottom:"10px"}}>SOLAR ENERGY SYSTEM PROPOSAL</div>
                <div style={{display:"grid",gridTemplateColumns:"auto 1fr",gap:"3px 14px",fontSize:"10px"}}>
                  {[["Prepared for",site.clientName||"—"],["Site",`${site.siteName||"—"} — ${site.location||"—"}`],["Sector",site.sector||"—"],["Ref. No.",refNo],["Date",new Date().toLocaleDateString("en-GB",{day:"numeric",month:"long",year:"numeric"})],["Valid until",new Date(Date.now()+validity*864e5).toLocaleDateString("en-GB",{day:"numeric",month:"long",year:"numeric"})]].map(([k,v])=>(
                    <div key={k} style={{display:"contents"}}><span style={{color:C.muted}}>{k}:</span><span style={{color:C.text}}>{v}</span></div>
                  ))}
                </div>
                <div style={{marginTop:"10px",display:"flex",gap:"8px",alignItems:"center"}}>
                  <label style={S.label}>Prepared by:</label>
                  <input style={{...S.input,width:"200px"}} placeholder="Your name / company" value={preparedBy} onChange={e=>setPreparedBy(e.target.value)}/>
                </div>
              </div>
              <div style={{textAlign:"right",background:C.deep,border:`1px solid ${C.border}`,borderRadius:"4px",padding:"16px 20px"}}>
                <div style={{fontSize:"9px",color:C.muted,letterSpacing:"0.1em",marginBottom:"4px"}}>SYSTEM SIZE</div>
                <div style={{fontSize:"28px",fontWeight:700,color:C.mid,lineHeight:1}}>{results.actualPvKwp} kWp</div>
                <div style={{fontSize:"11px",color:C.muted,marginTop:"3px"}}>Solar PV + {results.bessActual} kWh BESS</div>
                <div style={{marginTop:"12px",fontSize:"9px",color:C.muted}}>TOTAL INVESTMENT</div>
                <div style={{fontSize:"20px",fontWeight:700,color:C.green}}>{fmt(totalInclVat)}</div>
                <div style={{fontSize:"9px",color:C.muted}}>incl. 6% VAT</div>
              </div>
            </div>
            {/* KPI cards — payback, NPV & IRR all computed from client sell price */}
            {(()=>{
              const clientPayback = results.annualSavingsY1 > 0
                ? (totalInclVat / results.annualSavingsY1).toFixed(1)
                : "—";
              // Client-facing NPV: total lifetime savings minus what they actually pay (incl. VAT & margin)
              const clientNpv = (results.cumSavingsTotal||results.npv25) - totalInclVat;
              // Client IRR: rebase Year-0 outflow to client quoted price
              const clientIrrPct = (() => {
                if (!results.irrPct) return null;
                // Newton-Raphson on client cashflows (Year 0 = -totalInclVat)
                const projectYears = parseFloat(assumptions.projectLifeYears)||25;
                const cf = [-totalInclVat];
                // Approximate: scale each year's net saving proportionally to quoted vs cost price ratio
                // (savings are real-world, independent of what client pays; only Year 0 changes)
                const annSav = results.annualSavingsY1;
                const tariffEsc = parseFloat(assumptions.tariffEscalation)||0.05;
                const pvDeg = parseFloat(assumptions.pvDegradationPerYear)||0.005;
                const omInfl = parseFloat(assumptions.inflationRate)||0.06;
                const om0 = parseFloat(assumptions.omCostPerYearKes)||120000;
                for (let y=1; y<=projectYears; y++) {
                  const scale = Math.pow(1-(pvDeg*(y-1)), 1) * Math.pow(1+tariffEsc, y-1);
                  cf.push(annSav * scale - om0*(Math.pow(1+omInfl,y-1)-1));
                }
                let r = 0.10;
                for (let i=0; i<100; i++) {
                  const npv = cf.reduce((s,v,t)=>s+v/Math.pow(1+r,t),0);
                  const dnpv = cf.reduce((s,v,t)=>t===0?s:s-t*v/Math.pow(1+r,t+1),0);
                  if (Math.abs(dnpv)<1e-10) break;
                  const nr = r - npv/dnpv;
                  if (Math.abs(nr-r)<1e-8){r=nr;break;}
                  r = Math.max(-0.99,Math.min(10,nr));
                }
                const check = cf.reduce((s,v,t)=>s+v/Math.pow(1+r,t),0);
                return Math.abs(check)<1000 ? +(r*100).toFixed(1) : null;
              })();
              return(
            <div style={{display:"grid",gridTemplateColumns:"repeat(3,1fr)",gap:"10px",marginBottom:"10px"}}>
              {[
                ["Annual savings (Y1)",      fmt(results.annualSavingsY1),                                                              "energy + demand charges",                                                                                                    "green"],
                ["Simple payback",           `${clientPayback} years`,                                                                  "from quoted price incl. VAT",                                                                                                "blue"],
                ["25-yr net benefit",        clientNpv>=0?fmt(clientNpv):`−${fmt(Math.abs(clientNpv))}`,                               "cumulative savings less investment",                                                                                          clientNpv>=0?"green":"red"],
                ["Blended tariff displaced", `KES ${safeKes(results.blendedRateY1)}/kWh`,                                             `${((results.gridPct||0)*100).toFixed(0)}% grid + ${((1-(results.gridPct||0))*100).toFixed(0)}% diesel`,                     "amber"],
                ["IRR (on quoted price)",    clientIrrPct!=null?`${clientIrrPct}%`:"—",                                                clientIrrPct!=null?"return on your total investment":"—",                                                                      clientIrrPct!=null&&clientIrrPct>15?"green":""],
                ["CO₂ avoided",              `${results.co2Annual} t/yr`,                                                              `${(results.co2Annual*parseFloat(assumptions.projectLifeYears||25)).toFixed(0)} t over project life`,                         ""],
              ].map(([l,v,sub,col])=>(
                <div key={l} style={{background:C.deep,border:`1px solid ${C.border}`,borderRadius:"4px",padding:"12px 14px"}}>
                  <div style={{fontSize:"9px",color:C.muted,letterSpacing:"0.08em",textTransform:"uppercase",marginBottom:"5px"}}>{l}</div>
                  <div style={{fontSize:"16px",fontWeight:700,color:col==="green"?C.green:col==="amber"?C.amber:col==="blue"?C.navy:col==="red"?C.red:C.dim,lineHeight:1}}>{v}</div>
                  <div style={{fontSize:"9px",color:C.muted,marginTop:"4px"}}>{sub}</div>
                </div>
              ))}
            </div>
              );
            })()}
          </div>


          {/* System Architecture Diagram */}
          <div style={{...S.card, marginBottom:"14px", border:`1px solid ${C.lt}`}}>
            <div style={S.sec}>System Architecture — Visual Overview</div>

            {/* Inverter size override */}
            {(()=>{
              const invKw = parseFloat(assumptions.inverterKwOverride) || Math.ceil(results.actualPvKwp);
              return null; // computed below
            })()}

            <div style={{display:"flex",alignItems:"center",gap:"10px",marginBottom:"14px",background:C.deep,padding:"10px 14px",borderRadius:"4px",border:`1px solid ${C.border}`}}>
              <span style={{fontSize:"10px",color:C.muted,letterSpacing:"0.08em",textTransform:"uppercase",whiteSpace:"nowrap"}}>Inverter size override</span>
              <select style={{...S.input,width:"180px",cursor:"pointer"}}
                value={assumptions.inverterKwOverride||""}
                onChange={e=>updAsm("inverterKwOverride", e.target.value)}>
                <option value="">Auto ({Math.ceil(results.actualPvKwp)} kW calculated)</option>
                {[30,40,50,60,80,100,120,150,160,200,250,300,350,400,500].map(kw=>(
                  <option key={kw} value={kw}>{kw} kW</option>
                ))}
              </select>
              <span style={{fontSize:"10px",color:C.muted}}>·</span>
              <select style={{...S.input,width:"180px",cursor:"pointer"}}
                value={assumptions.inverterConfigOverride||""}
                onChange={e=>updAsm("inverterConfigOverride", e.target.value)}>
                <option value="">Qty override (optional)</option>
                {[1,2,3,4,5,6,8,10].map(n=>(
                  <option key={n} value={n}>{n} unit{n>1?"s":""}</option>
                ))}
              </select>
              {(assumptions.inverterKwOverride||assumptions.inverterConfigOverride) && (
                <button style={S.btn("ghost")} onClick={()=>{updAsm("inverterKwOverride","");updAsm("inverterConfigOverride","");}}>↺ Reset</button>
              )}
              <span style={{fontSize:"10px",color:C.amber,marginLeft:"4px"}}>
                {(()=>{
                  const override = parseFloat(assumptions.inverterKwOverride)||0;
                  const calc = Math.ceil(results.actualPvKwp);
                  const qty = parseInt(assumptions.inverterConfigOverride)||1;
                  if(!override) return "";
                  const total = override * qty;
                  if(total < calc*0.9) return `⚠ ${total} kW total may be undersized for ${calc} kWp array`;
                  if(total > calc*2)   return `ℹ ${total} kW total — oversized (consider fewer units)`;
                  return `✓ ${qty > 1 ? qty+"× "+override+" kW = "+total+" kW total" : override+" kW"}`;
                })()}
              </span>
            </div>

            <svg viewBox="0 0 1000 350" style={{width:"100%",display:"block",fontFamily:"'Inter','Segoe UI',sans-serif"}}>
              {/* Background */}
              <rect x="0" y="0" width="1000" height="350" fill="#F4F6F9" rx="6"/>

              {/* ══════════════ CONNECTION LINES ══════════════ */}
              {/* PV → Inverter (two DC cables) */}
              <line x1="180" y1="115" x2="230" y2="115" stroke="#2E75B6" strokeWidth="3"/>
              <line x1="180" y1="185" x2="230" y2="185" stroke="#2E75B6" strokeWidth="3"/>
              <line x1="180" y1="115" x2="180" y2="185" stroke="#2E75B6" strokeWidth="3"/>
              {/* Inverter → Busbar (AC) */}
              <line x1="390" y1="150" x2="430" y2="150" stroke="#1F3A5F" strokeWidth="4"/>
              {/* Vertical busbar */}
              <line x1="430" y1="70" x2="430" y2="310" stroke="#1F3A5F" strokeWidth="6"/>
              {/* Busbar → BESS */}
              <line x1="430" y1="100" x2="470" y2="100" stroke="#C0392B" strokeWidth="3"/>
              {/* Busbar → Distribution Panel */}
              <line x1="430" y1="248" x2="470" y2="248" stroke="#1F3A5F" strokeWidth="3"/>
              {/* Distribution → Facility */}
              <line x1="645" y1="248" x2="695" y2="248" stroke="#1F3A5F" strokeWidth="3"/>
              {/* BESS ↔ Dist line (vertical inside right column) */}
              <line x1="557" y1="170" x2="557" y2="195" stroke="#C0392B" strokeWidth="2" strokeDasharray="4 3"/>
              {/* Grid → Busbar (dashed, from bottom) */}
              <line x1="310" y1="310" x2="430" y2="310" stroke="#94a3b8" strokeWidth="2" strokeDasharray="6 3"/>

              {/* ── Wire labels ── */}
              <rect x="191" y="140" width="28" height="14" fill="#2E75B6" rx="3"/>
              <text x="205" y="151" textAnchor="middle" fontSize="8" fill="white" fontWeight="700">DC</text>
              <rect x="396" y="143" width="28" height="14" fill="#1F3A5F" rx="3"/>
              <text x="410" y="154" textAnchor="middle" fontSize="8" fill="white" fontWeight="700">AC</text>

              {/* ══════════════ SOLAR PV ARRAY ══════════════ */}
              <rect x="10" y="60" width="170" height="190" fill="white" stroke="#2E75B6" strokeWidth="2" rx="6"/>
              {/* 3×3 panel grid */}
              {[0,1,2].map(col => [0,1,2].map(row => (
                <rect key={`p${col}${row}`} x={22+col*48} y={76+row*48} width="42" height="38"
                  fill={row===1?"#2E75B6":"#1F3A5F"} opacity={0.75} rx="3"/>
              )))}
              {/* Glare */}
              {[0,1,2].map(col => [0,1,2].map(row => (
                <line key={`g${col}${row}`} x1={25+col*48} y1={79+row*48} x2={35+col*48} y2={89+row*48}
                  stroke="white" strokeWidth="1.5" opacity="0.35"/>
              )))}
              <text x="95" y="225" textAnchor="middle" fontSize="11" fill="#1F3A5F" fontWeight="700">
                {results.numModules} × {assumptions.moduleWp||580}Wp
              </text>
              <text x="95" y="241" textAnchor="middle" fontSize="13" fill="#2E75B6" fontWeight="700">
                {results.actualPvKwp} kWp
              </text>
              <text x="95" y="50" textAnchor="middle" fontSize="9" fill="#64748b" letterSpacing="0.1em" fontWeight="600">SOLAR PV ARRAY</text>

              {/* ══════════════ INVERTER / CHARGER ══════════════ */}
              <rect x="230" y="80" width="160" height="140" fill="white" stroke="#1F3A5F" strokeWidth="2" rx="6"/>
              {/* Sine wave */}
              <path d="M248 150 Q258 122 270 150 Q282 178 292 150 Q302 122 314 150 Q326 178 336 150 Q346 122 356 150 Q366 178 374 150"
                fill="none" stroke="#1F3A5F" strokeWidth="2.5"/>
              {/* DC→AC label */}
              <text x="310" y="185" textAnchor="middle" fontSize="9" fill="#2E75B6" fontWeight="700">DC</text>
              <text x="326" y="185" fontSize="9" fill="#94a3b8">→</text>
              <text x="342" y="185" fontSize="9" fill="#1F3A5F" fontWeight="700">AC</text>
              {(()=>{
                const override = parseFloat(assumptions.inverterKwOverride)||0;
                const qty      = parseInt(assumptions.inverterConfigOverride)||1;
                const calcKw   = Math.ceil(results.actualPvKwp);
                const dispKw   = override || calcKw;
                const dispStr  = override
                  ? (qty > 1 ? `${qty} × ${dispKw} kW` : `${dispKw} kW`)
                  : `${dispKw} kW`;
                return (<>
                  <text x="310" y="106" textAnchor="middle" fontSize="13" fill="#1F3A5F" fontWeight="700">{dispStr}</text>
                  <text x="310" y="120" textAnchor="middle" fontSize="9" fill="#94a3b8">Hybrid Inverter</text>
                  {override && <text x="310" y="133" textAnchor="middle" fontSize="8" fill="#C0392B">▲ overridden</text>}
                </>);
              })()}
              <text x="310" y="70" textAnchor="middle" fontSize="9" fill="#64748b" letterSpacing="0.1em" fontWeight="600">INVERTER / CHARGER</text>

              {/* ══════════════ BUSBAR LABEL ══════════════ */}
              <text x="430" y="330" textAnchor="middle" fontSize="8" fill="#64748b" letterSpacing="0.08em">AC BUS</text>

              {/* ══════════════ BESS ══════════════ */}
              <rect x="470" y="55" width="175" height="115" fill="white" stroke="#C0392B" strokeWidth="2" rx="6"/>
              {/* Battery body */}
              <rect x="486" y="70" width="120" height="62" fill="#FFF5F5" stroke="#C0392B" strokeWidth="1.5" rx="3"/>
              <rect x="606" y="84" width="10" height="34" fill="#C0392B" rx="2"/>
              {/* Fill bars — 4 cells */}
              {[0,1,2,3].map(i => (
                <rect key={i} x={490+i*26} y="76" width="20" height="50"
                  fill={i<3?"#C0392B":"#FEE2E2"} rx="2" opacity={i<3?0.75:0.25}/>
              ))}
              {/* % overlay */}
              <text x="544" y="107" textAnchor="middle" fontSize="11" fill="white" fontWeight="700">75%</text>
              <text x="557" y="138" textAnchor="middle" fontSize="12" fill="#C0392B" fontWeight="700">
                {results.bessActual} kWh
              </text>
              <text x="557" y="153" textAnchor="middle" fontSize="9" fill="#94a3b8">
                {results.bessUnits} × {assumptions.bessUnitKwh||100} kWh units
              </text>
              <text x="557" y="45" textAnchor="middle" fontSize="9" fill="#64748b" letterSpacing="0.1em" fontWeight="600">BATTERY SYSTEM (BESS)</text>

              {/* ══════════════ DISTRIBUTION PANEL ══════════════ */}
              <rect x="470" y="195" width="175" height="106" fill="white" stroke="#1F3A5F" strokeWidth="2" rx="6"/>
              {/* Panel face */}
              <rect x="486" y="210" width="143" height="76" fill="#EDF1F7" stroke="#D6DEE8" strokeWidth="1" rx="2"/>
              {/* Main breaker */}
              <rect x="494" y="218" width="22" height="52" fill="#1F3A5F" rx="2" opacity="0.9"/>
              <text x="505" y="249" textAnchor="middle" fontSize="7" fill="white" fontWeight="700">MAIN</text>
              {/* Branch breakers */}
              {[0,1,2,3,4,5].map(i => (
                <rect key={i} x={524+i*17} y="218" width="12" height="30"
                  fill={i<4?"#2E75B6":"#BFD3F0"} rx="2" opacity={0.8}/>
              ))}
              {/* Bus bar inside panel */}
              <line x1="520" y1="255" x2="626" y2="255" stroke="#1F3A5F" strokeWidth="3"/>
              {[0,1,2,3,4,5].map(i => (
                <line key={i} x1={524+i*17+6} y1="250" x2={524+i*17+6} y2="258" stroke="#1F3A5F" strokeWidth="2"/>
              ))}
              <text x="557" y="275" textAnchor="middle" fontSize="10" fill="#1F3A5F" fontWeight="700">Distribution Panel</text>
              <text x="557" y="287" textAnchor="middle" fontSize="9" fill="#94a3b8">400A / LV</text>
              <text x="557" y="185" textAnchor="middle" fontSize="9" fill="#64748b" letterSpacing="0.1em" fontWeight="600">DISTRIBUTION PANEL</text>

              {/* ══════════════ TOTAL CONSUMPTION METER ══════════════ */}
              <rect x="695" y="80" width="295" height="220" fill="white" stroke="#1F3A5F" strokeWidth="2" rx="6"/>
              <text x="842" y="70" textAnchor="middle" fontSize="9" fill="#64748b" letterSpacing="0.1em" fontWeight="600">ENERGY METER</text>
              {/* Inner panel */}
              <rect x="715" y="98" width="255" height="184" fill="#EDF1F7" stroke="#D6DEE8" strokeWidth="1" rx="4"/>
              {/* Centred label */}
              <text x="842" y="182" textAnchor="middle" fontSize="16" fill="#1F3A5F" fontWeight="700">Total Consumption</text>
              <text x="842" y="204" textAnchor="middle" fontSize="16" fill="#1F3A5F" fontWeight="700">Meter</text>
              {/* kWh/day readout */}
              <text x="842" y="238" textAnchor="middle" fontSize="12" fill="#2E75B6" fontWeight="600">{Math.round(results.dailyKwh)} kWh/day</text>
              <text x="842" y="254" textAnchor="middle" fontSize="10" fill="#94a3b8">{results.peakKw} kW peak demand</text>
              {/* Client label */}
              <text x="842" y="274" textAnchor="middle" fontSize="10" fill="#1F3A5F" fontWeight="700">
                {(site.clientName||"Client Facility").toUpperCase()}
              </text>

                            {/* ══════════════ KPLC GRID ══════════════ */}
              <rect x="180" y="295" width="130" height="40" fill="white" stroke="#94a3b8" strokeWidth="1.5"
                strokeDasharray="5 3" rx="4"/>
              {/* Pylon icon */}
              <line x1="197" y1="299" x2="197" y2="331" stroke="#94a3b8" strokeWidth="1.5"/>
              <line x1="192" y1="306" x2="202" y2="306" stroke="#94a3b8" strokeWidth="1.5"/>
              <line x1="191" y1="314" x2="203" y2="314" stroke="#94a3b8" strokeWidth="1.5"/>
              <line x1="197" y1="306" x2="190" y2="318" stroke="#94a3b8" strokeWidth="1"/>
              <line x1="197" y1="306" x2="204" y2="318" stroke="#94a3b8" strokeWidth="1"/>
              <text x="255" y="313" textAnchor="middle" fontSize="10" fill="#64748b" fontWeight="600">KPLC Grid</text>
              <text x="255" y="327" textAnchor="middle" fontSize="8" fill="#94a3b8">backup / import</text>

              {/* ══════════════ FLOW LABELS ══════════════ */}
              <text x="207" y="107" textAnchor="middle" fontSize="8" fill="#2E75B6" fontWeight="600">Solar DC →</text>
              <text x="452" y="93" textAnchor="middle" fontSize="8" fill="#C0392B" fontWeight="600">↑ Charge</text>
              <text x="452" y="260" textAnchor="middle" fontSize="8" fill="#1F3A5F" fontWeight="600">↓ Load</text>
              <text x="670" y="243" textAnchor="middle" fontSize="8" fill="#1F3A5F" fontWeight="600">Supply →</text>
            </svg>

            {/* Metrics strip */}
            <div style={{display:"grid",gridTemplateColumns:"repeat(5,1fr)",gap:"10px",marginTop:"14px"}}>
              {(()=>{
                const override = parseFloat(assumptions.inverterKwOverride)||0;
                const qty      = parseInt(assumptions.inverterConfigOverride)||1;
                const calcKw   = Math.ceil(results.actualPvKwp);
                const dispKw   = override || calcKw;
                const invLabel = override
                  ? (qty > 1 ? `${qty} × ${dispKw} kW` : `${dispKw} kW`)
                  : `${calcKw} kW`;
                return [
                  ["Solar Array",      `${results.actualPvKwp} kWp`,  `${results.numModules} × ${assumptions.moduleWp||580}Wp panels`, "#2E75B6"],
                  ["Inverter",         invLabel,                        override?"Overridden — check sizing":"Hybrid grid-tied / charger",  override?"#C0392B":"#1F3A5F"],
                  ["Battery (BESS)",   `${results.bessActual} kWh`,   `${results.bessUnits} × ${assumptions.bessUnitKwh||100} kWh units`, "#C0392B"],
                  ["Self-sufficiency", `${results.selfSufficiencyPct}%`, "consumption met by solar + BESS",                                "#16a34a"],
                  ["Client load",      `${Math.round(results.dailyKwh)} kWh/d`,  `${results.peakKw} kW peak demand`,                                            "#1F3A5F"],
                ].map(([label,val,sub,col])=>(
                  <div key={label} style={{background:C.deep,borderRadius:"4px",padding:"10px 12px",borderTop:`2px solid ${col}`}}>
                    <div style={{fontSize:"9px",color:C.muted,letterSpacing:"0.08em",textTransform:"uppercase",marginBottom:"4px"}}>{label}</div>
                    <div style={{fontSize:"14px",fontWeight:700,color:col,lineHeight:1}}>{val}</div>
                    <div style={{fontSize:"9px",color:C.muted,marginTop:"4px",lineHeight:1.4}}>{sub}</div>
                  </div>
                ));
              })()}
            </div>
          </div>

          {/* Scope of supply */}          {/* Scope of supply */}
          <div style={{...S.card,marginBottom:"14px"}}>
            <div style={S.sec}>Scope of Supply & Works</div>
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:"12px"}}>
              {lines.map((sec,si)=>{
                const secSell=sectionCost[si]/(1-margin/100);
                return(
                  <div key={sec.id} style={{background:C.deep,border:`1px solid ${C.border}`,borderRadius:"4px",overflow:"hidden"}}>
                    <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",padding:"9px 12px",background:"#f8fafc",borderBottom:`1px solid ${C.border}`}}>
                      <div style={{display:"flex",alignItems:"center",gap:"8px"}}>
                        <span style={S.badge(SC[si])}>{si+1}</span>
                        <span style={{fontSize:"10px",fontWeight:700,color:C.dim}}>{sec.section}</span>
                      </div>
                      <span style={{fontSize:"11px",fontWeight:700,color:C.mid}}>{fmt(secSell)}</span>
                    </div>
                    <div style={{padding:"8px 12px"}}>
                      {sec.items.map((item,ii)=>(
                        <div key={ii} style={{display:"flex",justifyContent:"space-between",padding:"4px 0",borderBottom:`1px solid ${C.border}`,fontSize:"10px"}}>
                          <span style={{color:C.muted,flex:1}}>{item.name}</span>
                          <span style={{color:C.text,marginLeft:"8px",whiteSpace:"nowrap"}}>{item.qty} {item.unit}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Financial offer */}
          <div style={{...S.card,marginBottom:"14px",border:`1px solid #bbf7d0`}}>
            <div style={S.sec}>Financial Offer — Cash Purchase</div>
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:"24px"}}>
              <div>
                {lines.map((sec,si)=>{
                  const secSell=sectionCost[si]/(1-margin/100);
                  return(
                    <div key={sec.id} style={{display:"flex",justifyContent:"space-between",padding:"6px 0",borderBottom:`1px solid ${C.border}`,fontSize:"10px"}}>
                      <span style={{color:C.muted,display:"flex",alignItems:"center",gap:"6px"}}>
                        <span style={S.badge(SC[si])}>{si+1}</span>{sec.section}
                      </span>
                      <span style={{color:C.text,fontWeight:500}}>{fmt(secSell)}</span>
                    </div>
                  );
                })}
                <div style={{display:"flex",justifyContent:"space-between",padding:"8px 0",borderBottom:`1px solid ${C.border}`,fontSize:"11px",marginTop:"4px"}}>
                  <span style={{color:C.text}}>Subtotal (ex-VAT)</span>
                  <span style={{color:C.text,fontWeight:700}}>{fmt(totalSell)}</span>
                </div>
                <div style={{display:"flex",justifyContent:"space-between",padding:"5px 0",borderBottom:`1px solid ${C.border}`,fontSize:"10px"}}>
                  <span style={{color:C.muted}}>VAT @ 6%</span>
                  <span style={{color:C.muted}}>{fmt(totalVat)}</span>
                </div>
                <div style={{display:"flex",justifyContent:"space-between",padding:"10px 0",fontSize:"15px"}}>
                  <span style={{color:C.text,fontWeight:700}}>TOTAL (incl. VAT)</span>
                  <span style={{color:C.green,fontWeight:700,fontSize:"20px"}}>{fmt(totalInclVat)}</span>
                </div>
              </div>
              <div>
                <div style={{fontSize:"10px",color:C.muted,marginBottom:"10px"}}>Payment milestones:</div>
                <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:"10px",marginBottom:"14px"}}>
                  <div>
                    <label style={S.label}>1st payment — on signing</label>
                    <div style={{display:"flex",alignItems:"center",gap:"6px",marginTop:"4px"}}>
                      <input type="range" min="10" max="60" step="5" value={deposit1}
                        onChange={e=>{
                          const v=+e.target.value;
                          setDeposit1(v);
                          if(v+deposit2>90) setDeposit2(90-v);
                        }} style={{flex:1}}/>
                      <span style={{fontSize:"13px",fontWeight:700,color:C.mid,minWidth:"32px"}}>{deposit1}%</span>
                    </div>
                    <div style={{fontSize:"9px",color:C.muted,marginTop:"3px"}}>{fmt(totalInclVat*deposit1/100)}</div>
                  </div>
                  <div>
                    <label style={S.label}>2nd payment — on delivery</label>
                    <div style={{display:"flex",alignItems:"center",gap:"6px",marginTop:"4px"}}>
                      <input type="range" min="10" max={Math.max(10,90-deposit1)} step="5"
                        value={Math.min(deposit2,90-deposit1)}
                        onChange={e=>setDeposit2(+e.target.value)} style={{flex:1}}/>
                      <span style={{fontSize:"13px",fontWeight:700,color:C.mid,minWidth:"32px"}}>{Math.min(deposit2,90-deposit1)}%</span>
                    </div>
                    <div style={{fontSize:"9px",color:C.muted,marginTop:"3px"}}>{fmt(totalInclVat*Math.min(deposit2,90-deposit1)/100)}</div>
                  </div>
                  <div>
                    <label style={S.label}>3rd payment — on completion</label>
                    <div style={{display:"flex",alignItems:"center",gap:"6px",marginTop:"4px"}}>
                      <input type="range" min="0" max="80" step="5"
                        value={dep3} disabled style={{flex:1,opacity:0.5}}/>
                      <span style={{fontSize:"13px",fontWeight:700,color:dep3<0?C.red:C.mid,minWidth:"32px"}}>{dep3}%</span>
                    </div>
                    <div style={{fontSize:"9px",color:dep3<0?"#dc2626":C.muted,marginTop:"3px"}}>
                      {dep3<0?"⚠ Total exceeds 100%":fmt(totalInclVat*dep3/100)}
                    </div>
                  </div>
                </div>
                {dep3<0&&<div style={{padding:"6px 10px",background:"#fef2f2",border:"1px solid #fca5a5",borderRadius:"3px",fontSize:"9px",color:"#dc2626",marginBottom:"10px"}}>
                  1st + 2nd payments exceed 100%. Reduce one of them — the 3rd payment cannot be negative.
                </div>}
                {milestones.map((m,i)=>(
                  <div key={i} style={{background:C.deep,border:`1px solid ${C.border}`,borderRadius:"3px",padding:"10px 12px",marginBottom:"8px"}}>
                    <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:"3px"}}>
                      <span style={{fontSize:"10px",color:C.dim,fontWeight:700}}>{m.label}</span>
                      <span style={{fontSize:"14px",fontWeight:700,color:C.mid}}>{fmt(totalInclVat*m.pct/100)}</span>
                    </div>
                    <div style={{display:"flex",justifyContent:"space-between"}}>
                      <span style={{fontSize:"9px",color:C.muted}}>{m.note}</span>
                      <span style={{fontSize:"9px",color:C.muted}}>{m.pct}% of total</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Next steps */}
          <div style={{...S.card,marginBottom:"14px"}}>
            <div style={S.sec}>Next Steps</div>
            <div style={{display:"grid",gridTemplateColumns:"repeat(4,1fr)",gap:"12px"}}>
              {[
                ["01","Accept Offer","Sign and return the Letter of Award with the 1st milestone payment to commence procurement.","blue"],
                ["02","Site Survey & Design","Engineering team conducts a detailed site survey and produces full design drawings within 10 working days.","green"],
                ["03","Procurement & Delivery","All equipment procured, quality-inspected, and delivered to site. Lead time: 4–6 weeks from order.","amber"],
                ["04","Installation & Handover","Mechanical and electrical installation, commissioning, performance testing, and formal client handover.","green"],
              ].map(([num,title,body,col])=>(
                <div key={num} style={{background:C.deep,border:`1px solid ${C.border}`,borderRadius:"4px",padding:"14px"}}>
                  <div style={{...S.badge(col),marginBottom:"8px"}}>{num}</div>
                  <div style={{fontSize:"11px",fontWeight:700,color:C.dim,marginBottom:"7px"}}>{title}</div>
                  <div style={{fontSize:"10px",color:C.muted,lineHeight:1.6}}>{body}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Warranties */}
          <div style={S.card}>
            <div style={S.sec}>Warranties, Terms & Exclusions</div>
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:"20px",fontSize:"10px",color:C.muted,lineHeight:1.8}}>
              <div>
                <div style={{color:C.dim,fontWeight:700,marginBottom:"7px"}}>Warranties included</div>
                <div>• PV Modules: 12-year product + 25-year linear performance warranty</div>
                <div>• Hybrid Inverter / Charger: 5-year manufacturer warranty</div>
                <div>• BESS: 10-year capacity warranty (≥80% retained at Year 10)</div>
                <div>• Mounting structure: 10-year structural integrity warranty</div>
                <div>• Installation workmanship: 2-year defects liability period</div>
                <div>• SCADA monitoring: 5-year cloud platform subscription included</div>
              </div>
              <div>
                <div style={{color:C.dim,fontWeight:700,marginBottom:"7px"}}>Exclusions & notes</div>
                <div>• Offer valid for {validity} days from the date of this proposal</div>
                <div>• Excludes civil works beyond standard foundation scope</div>
                <div>• Excludes utility/KPLC grid interconnection fees (if applicable)</div>
                <div>• Savings projections based on provided power logger data</div>
                <div>• Blended tariff savings assume current diesel/grid ratio maintained</div>
                <div>• Prices subject to review if KES/USD forex moves more than 5%</div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── MAIN APP ─────────────────────────────────────────────────────────────────

// ─── PROJECTS PANEL ───────────────────────────────────────────────────────────
function ProjectsPanel({ site, assumptions, diesel, profile, results, irrData, irrMeta,
                         onLoad, onClose }) {
  const [projects,   setProjects]   = React.useState([]);
  const [loading,    setLoading]    = React.useState(true);
  const [error,      setError]      = React.useState("");
  const [saveName,   setSaveName]   = React.useState("");
  const [saved,      setSaved]      = React.useState(false);
  const [busy,       setBusy]       = React.useState(false);
  const [confirmDel, setConfirmDel] = React.useState(null);

  React.useEffect(() => {
    let cancelled = false;
    listProjects()
      .then(rows => { if (!cancelled) { setProjects(rows); setLoading(false); } })
      .catch(err => { if (!cancelled) { setError(err.message || String(err)); setLoading(false); } });
    return () => { cancelled = true; };
  }, []);

  const handleSave = async () => {
    setError("");
    setBusy(true);
    try {
      const row = await saveProject({
        name: saveName, site, assumptions, diesel, profile, results, irrData, irrMeta,
      });
      setProjects(prev => [row, ...prev]);
      setSaveName("");
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } catch (err) {
      setError(err.message || String(err));
    } finally {
      setBusy(false);
    }
  };

  const handleLoad = (proj) => {
    onLoad(proj);
    onClose();
  };

  const handleDelete = async (id) => {
    setError("");
    try {
      await deleteProject(id);
      setProjects(prev => prev.filter(p => p.id !== id));
      setConfirmDel(null);
    } catch (err) {
      setError(err.message || String(err));
    }
  };

  const overlay = {
    position:"fixed", inset:0, background:"rgba(0,0,0,0.55)", zIndex:1000,
    display:"flex", alignItems:"center", justifyContent:"center",
  };
  const panel = {
    background:"#ffffff", borderRadius:"6px", width:"580px", maxWidth:"95vw",
    maxHeight:"82vh", display:"flex", flexDirection:"column",
    boxShadow:"0 12px 40px rgba(31,58,95,0.25)", border:"1px solid #D6DEE8",
  };
  const ph = { padding:"16px 22px", borderBottom:"1px solid #D6DEE8", display:"flex", justifyContent:"space-between", alignItems:"center", background:"#1F3A5F", borderRadius:"6px 6px 0 0" };
  const pb = { padding:"18px 22px", overflowY:"auto", flex:1 };
  const S2 = {
    input: { width:"100%", background:"#f1f5f9", border:"1px solid #e2e8f0", borderRadius:"4px",
             color:"#1e293b", padding:"8px 10px", fontSize:"12px", fontFamily:"inherit",
             boxSizing:"border-box", outline:"none" },
    btn:   (v) => ({ padding:"7px 14px", fontSize:"10px", letterSpacing:"0.1em", textTransform:"uppercase",
             border:v==="ghost"?"1px solid #e2e8f0":"none", borderRadius:"3px", cursor:"pointer",
             fontFamily:"inherit", fontWeight:v==="primary"?700:400,
             background:v==="primary"?C.navy:v==="danger"?"#fee2e2":v==="success"?"#dcfce7":C.deep,
             color:v==="primary"?"#fff":v==="danger"?"#dc2626":v==="success"?"#166534":"#1e293b",
             whiteSpace:"nowrap" }),
    row:   { display:"flex", alignItems:"center", gap:"8px", marginBottom:"8px" },
  };

  return (
    <div style={overlay} onClick={e=>{ if(e.target===e.currentTarget) onClose(); }}>
      <div style={panel}>
        {/* Header */}
        <div style={ph}>
          <div style={{fontSize:"11px",fontWeight:700,letterSpacing:"0.18em",color:"#ffffff",textTransform:"uppercase"}}>Saved Projects</div>
          <button style={{...S2.btn("ghost"),padding:"4px 10px",borderColor:"rgba(255,255,255,0.3)",color:"#fff",background:"rgba(255,255,255,0.1)"}} onClick={onClose}>✕ Close</button>
        </div>

        <div style={pb}>
          {/* Save current project */}
          <div style={{...S2.row, marginBottom:"16px"}}>
            <input
              style={{...S2.input, flex:1}}
              placeholder={site.clientName ? `${site.clientName} — ${new Date().toLocaleDateString("en-GB")}` : "Project name (optional)"}
              value={saveName}
              onChange={e=>setSaveName(e.target.value)}
              onKeyDown={e=>{ if(e.key==="Enter") handleSave(); }}
            />
            <button style={S2.btn(saved?"success":"primary")} onClick={handleSave} disabled={busy}>
              {busy ? "Saving…" : saved ? "✓ Saved!" : "↓ Save current"}
            </button>
          </div>

          {/* Status */}
          {error && (
            <div style={{padding:"8px 10px",background:"#FDECEC",border:"1px solid #C0392B",color:"#C0392B",fontSize:"11px",borderRadius:"4px",marginBottom:"12px"}}>
              {error}
            </div>
          )}

          {/* Projects list */}
          {loading ? (
            <div style={{textAlign:"center",padding:"32px",color:"#94a3b8",fontSize:"12px"}}>Loading projects…</div>
          ) : projects.length === 0 ? (
            <div style={{textAlign:"center",padding:"32px",color:"#94a3b8",fontSize:"12px"}}>
              No saved projects yet. Fill in a simulation and click "Save current" above.
            </div>
          ) : (
            <div>
              <div style={{fontSize:"9px",letterSpacing:"0.14em",textTransform:"uppercase",color:"#94a3b8",marginBottom:"10px"}}>
                {projects.length} saved revision{projects.length!==1?"s":""}
              </div>
              {projects.map(proj => {
                const pd = proj.project_data || {};
                return (
                <div key={proj.id} style={{border:"1px solid #e2e8f0",borderRadius:"4px",padding:"12px 14px",marginBottom:"8px",display:"flex",justifyContent:"space-between",alignItems:"center",gap:"10px"}}>
                  <div style={{flex:1,minWidth:0}}>
                    <div style={{fontSize:"12px",fontWeight:700,color:"#1e293b",marginBottom:"3px",overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>
                      {proj.project_name} <span style={{color:"#94a3b8",fontWeight:400}}>· rev {proj.revision}</span>
                    </div>
                    <div style={{fontSize:"10px",color:"#94a3b8"}}>
                      Saved {new Date(proj.created_at).toLocaleString("en-GB")}
                      {pd.site?.location ? ` · ${pd.site.location}` : ""}
                      {pd.results ? ` · ${pd.results.actualPvKwp} kWp · ${pd.results.actualBessKwh} kWh BESS` : ""}
                    </div>
                  </div>
                  <div style={{display:"flex",gap:"6px",flexShrink:0}}>
                    <button style={S2.btn("primary")} onClick={()=>handleLoad(proj)}>↑ Load</button>
                    {confirmDel===proj.id ? (
                      <>
                        <button style={S2.btn("danger")} onClick={()=>handleDelete(proj.id)}>Delete?</button>
                        <button style={S2.btn("ghost")} onClick={()=>setConfirmDel(null)}>Cancel</button>
                      </>
                    ) : (
                      <button style={S2.btn("ghost")} onClick={()=>setConfirmDel(proj.id)}>✕</button>
                    )}
                  </div>
                </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default function App() {
  const [tab,          setTab]          = useState("inputs");
  const [site,         setSite]         = useState(DEFAULT_SITE);
  const [assumptions,  setAssumptions]  = useState(DEFAULT_ASSUMPTIONS);
  const [profile,      setProfile]      = useState(null);   // parsed logger data
  const [fileName,     setFileName]     = useState("");
  const [results,      setResults]      = useState(null);
  const [irrData,      setIrrData]      = useState(null);
  const [irrMeta,      setIrrMeta]      = useState(null);
  const [irrStatus,    setIrrStatus]    = useState("idle");
  const [irrError,     setIrrError]     = useState("");
  const [usdRate,      setUsdRate]      = useState(130);
  const [diesel,       setDiesel]       = useState({
    gridPct:"0.60", dieselPricePerL:"120", dieselSfc:"0.35",
    gensetOmFactor:"1.20", dieselEscalation:"0.08",
  });
  const [showProjects, setShowProjects] = useState(false);
  const [currentProjectName, setCurrentProjectName] = useState("");
  const [inputMode,   setInputMode]   = useState("logger");  // "logger" | "bills"
  const [bills,       setBills]       = useState([]);
  const [billFiles,   setBillFiles]   = useState([]);
  const [billError,   setBillError]   = useState("");
  const [billParsing, setBillParsing] = useState(false);
  const [manualBill,  setManualBill]  = useState({ period:"", unitsKwh:"", peakKva:"", highRateKwh:"", lowRateKwh:"", fecKwh:"", ferfaKwh:"", inflationAdjKwh:"", ercLevyKwh:"", wraLevyKwh:"", totalKes:"" });
  const billFileRef = React.useRef();

  const loadProject = (proj) => {
    // Cloud rows have a `project_data` blob; legacy localStorage rows are flat.
    const d = proj.project_data || proj;
    const displayName = proj.project_name || proj.name || "Project";
    if (d.site)        setSite(d.site);
    if (d.assumptions) setAssumptions(d.assumptions);
    if (d.diesel)      setDiesel(d.diesel);
    if (d.profile)     { setProfile(d.profile); setFileName(displayName); }
    if (d.results)     setResults(d.results);
    if (d.irrData)     { setIrrData(d.irrData); setIrrStatus("ok"); }
    if (d.irrMeta)     setIrrMeta(d.irrMeta);
    setCurrentProjectName(displayName);
    setTab("dashboard");
  };

  const updSite = useCallback((k,v) => setSite(p=>({...p,[k]:v})), []);
  const updAsm  = useCallback((k,v) => setAssumptions(p=>({...p,[k]:v})), []); // store as string while typing; parseFloat on simulate

  const onParsed = useCallback((result, name) => {
    setProfile(result);
    setFileName(name);
    setResults(null);
  }, []);

  const handleBillFiles = async (files) => {
    if (!files || files.length === 0) return;
    setBillParsing(true); setBillError("");
    const newBills = []; const newNames = [];
    for (const file of Array.from(files)) {
      try {
        const bill = await parseSingleBill(file);
        if (!bill.unitsKwh || bill.unitsKwh <= 0) throw new Error("Could not read units consumed from bill.");
        newBills.push(bill); newNames.push(file.name);
      } catch(e) {
        setBillError(`Error parsing ${file.name}: ${e.message}`);
        setBillParsing(false); return;
      }
    }
    const allBills = [...bills, ...newBills];
    const allNames = [...billFiles, ...newNames];
    setBills(allBills); setBillFiles(allNames);
    setBillParsing(false);
    try {
      const synth = synthesiseProfileFromBills(allBills);
      onParsed(synth, `${allBills.length} bill${allBills.length>1?"s":""} — ${synth.avgMonthlyKwh.toLocaleString()} kWh/mo avg`);
      if (synth.effTariff > 0) {
        // Auto-populate KPLC component rates from averaged bill data
        const avgComp = (field) => {
          const vals = allBills.map(b => parseFloat(b[field])||0).filter(v => v > 0);
          return vals.length > 0 ? +(vals.reduce((s,v)=>s+v,0)/vals.length).toFixed(4) : 0;
        };
        updAsm("highRateKwh",     avgComp("highRateKwh"));
        updAsm("lowRateKwh",      avgComp("lowRateKwh"));
        updAsm("fecKwh",          avgComp("fecKwh"));
        updAsm("ferfaKwh",        avgComp("ferfaKwh"));
        updAsm("inflationAdjKwh", avgComp("inflationAdjKwh"));
        updAsm("ercLevyKwh",      avgComp("ercLevyKwh"));
        updAsm("wraLevyKwh",      avgComp("wraLevyKwh"));
        // Also set the plain kplcRate for display
        updAsm("kplcRate",        synth.effTariff.toFixed(4));
      }
    } catch(e) { setBillError(e.message); }
  };

  const addManualBill = () => {
    const kwh = parseFloat(manualBill.unitsKwh);
    if (!kwh || kwh <= 0) { setBillError("Units consumed (kWh) is required."); return; }
    const bill = {
      period: manualBill.period || "Manual entry",
      unitsKwh: kwh,
      peakKva: parseFloat(manualBill.peakKva) || 0,
      totalKes: parseFloat(manualBill.totalKes) || 0,
      highRateKwh: parseFloat(manualBill.highRateKwh) || 0,
      lowRateKwh: parseFloat(manualBill.lowRateKwh) || 0,
      fecKwh: parseFloat(manualBill.fecKwh) || 0,
      ferfaKwh: parseFloat(manualBill.ferfaKwh) || 0,
      inflationAdjKwh: parseFloat(manualBill.inflationAdjKwh) || 0,
      ercLevyKwh: parseFloat(manualBill.ercLevyKwh) || 0,
      wraLevyKwh: parseFloat(manualBill.wraLevyKwh) || 0,
    };
    const allBills = [...bills, bill];
    setBills(allBills);
    setBillFiles([...billFiles, `Manual: ${manualBill.period||"bill"}`]);
    setManualBill({ period:"", unitsKwh:"", peakKva:"", highRateKwh:"", lowRateKwh:"", fecKwh:"", ferfaKwh:"", inflationAdjKwh:"", ercLevyKwh:"", wraLevyKwh:"", totalKes:"" });
    setBillError("");
    try {
      const synth = synthesiseProfileFromBills(allBills);
      onParsed(synth, `${allBills.length} bill${allBills.length>1?"s":""} — ${synth.avgMonthlyKwh.toLocaleString()} kWh/mo avg`);
    } catch(e) { setBillError(e.message); }
  };

  const removeBill = (idx) => {
    const nb = bills.filter((_,i) => i!==idx);
    const nf = billFiles.filter((_,i) => i!==idx);
    setBills(nb); setBillFiles(nf);
    if (nb.length > 0) {
      try {
        const synth = synthesiseProfileFromBills(nb);
        onParsed(synth, `${nb.length} bill${nb.length>1?"s":""} — ${synth.avgMonthlyKwh.toLocaleString()} kWh/mo avg`);
      } catch(e) {}
    } else {
      onParsed(null, "");
    }
  };

  const fetchIrr = async () => {
    setIrrStatus("loading"); setIrrError("");
    const lat = parseFloat(site.lat), lng = parseFloat(site.lng);
    if (isNaN(lat)||isNaN(lng)) { setIrrError("Enter valid coordinates first."); setIrrStatus("error"); return; }
    try {
      const d = await fetchPVGIS(lat, lng);
      setIrrData(d.monthly); setIrrMeta({source:d.source,location:d.location,annual:d.annual,fromTable:d.fromTable||false});
      setIrrStatus("ok");
    } catch(e) { setIrrError(e.message); setIrrStatus("error"); }
  };

  const simulate = () => {
    if (!profile) return;
    const irr = irrData || NAROK_IRR;
    const parsedA = Object.fromEntries(Object.entries(assumptions).map(([k,v])=>[k, isNaN(parseFloat(v))?v:parseFloat(v)]));
    // Merge diesel inputs into assumptions for engine
    const dieselParsed = Object.fromEntries(Object.entries(diesel).map(([k,v])=>[k,parseFloat(v)||0]));
    const newResults = runSimulation(profile, site, {...parsedA, ...dieselParsed}, irr);
    setResults(newResults);
    setTab("dashboard");
    // Auto-save a new revision after each simulation. Fire-and-forget; UI stays
    // responsive even if the cloud save fails.
    saveProject({
      name: currentProjectName, site, assumptions, diesel, profile,
      results: newResults, irrData, irrMeta,
    }).catch(err => {
      // eslint-disable-next-line no-console
      console.warn("Auto-save failed:", err.message || err);
    });
  };

  const TABS = [
    ["inputs",     "01 — Data Input"],
    ["assumptions","02 — Assumptions"],
    ["dashboard",  "03 — Dashboard"],
    ["dispatch",   "04 — Dispatch"],
    ["financial",  "05 — Financial"],
    ["offer",      "06 — Client Offer"],
  ];

  const canSimulate = !!profile;

  return (
    <div style={S.app}>
      {/* Inter font + global styles */}
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&display=swap');
        * { box-sizing: border-box; }
        input:focus { border-color: #1F3A5F !important; box-shadow: 0 0 0 2px rgba(31,58,95,0.12); }
        input::placeholder { color: #94a3b8; }
        ::-webkit-scrollbar { width: 6px; height: 6px; }
        ::-webkit-scrollbar-track { background: #EDF1F7; }
        ::-webkit-scrollbar-thumb { background: #BFD3F0; border-radius: 3px; }
        ::-webkit-scrollbar-thumb:hover { background: #1F3A5F; }
      `}</style>
      {/* Header */}
      <div style={S.header}>
        {/* Left — brand */}
        <div style={{display:"flex",alignItems:"center",gap:"20px"}}>
          {/* Esencee logo */}
          <div style={{display:"flex",alignItems:"center",borderRight:`1px solid rgba(255,255,255,0.15)`,paddingRight:"20px"}}>
            <img src="/esencee_logo.png" alt="Esencee Energy" style={{height:"32px",display:"block"}}/>
          </div>
          <div>
            <div style={{fontSize:"11px",fontWeight:700,letterSpacing:"0.2em",color:"#ffffff",textTransform:"uppercase",lineHeight:1}}>SolarSim Pro</div>
            <div style={{fontSize:"9px",color:C.lt,letterSpacing:"0.08em",marginTop:"3px"}}>C&amp;I · Commercial &amp; Industrial Energy System Design</div>
          </div>
        </div>
        {/* Right — actions */}
        <div style={{display:"flex",gap:"8px",alignItems:"center"}}>
          {irrStatus==="ok"   && <span style={S.badge(irrMeta?.fromTable?"amber":"navy")}>{irrMeta?.fromTable?"✓ Table irr.":"✓ PVGIS live"}</span>}
          {irrStatus==="error"&& <span style={{...S.badge(""),background:"#7f1d1d",color:"#fca5a5"}}>⚠ fallback irr.</span>}
          {profile && <span style={S.badge("navy")}>✓ {fileName}</span>}
          <button
            style={{...S.btn("ghost"),borderColor:"rgba(255,255,255,0.25)",color:"#fff",background:"rgba(255,255,255,0.08)"}}
            onClick={()=>setShowProjects(true)}>
            📁 Projects
          </button>
          <button
            style={{
              ...S.btn("primary"),
              background:canSimulate?C.red:"rgba(255,255,255,0.15)",
              color:"#ffffff",
              border:`1px solid ${canSimulate?C.red:"rgba(255,255,255,0.2)"}`,
              opacity:canSimulate?1:0.6,
              cursor:canSimulate?"pointer":"not-allowed",
            }}
            onClick={simulate} disabled={!canSimulate}
            title={!canSimulate?"Upload a power logger file first":"Run simulation"}>
            ▶ Run Simulation
          </button>
        </div>
      </div>

      {/* Projects panel modal */}
      {showProjects && (
        <ProjectsPanel
          site={site} assumptions={assumptions} diesel={diesel}
          profile={profile} results={results}
          irrData={irrData} irrMeta={irrMeta}
          onLoad={loadProject}
          onClose={()=>setShowProjects(false)}
        />
      )}

      {/* Tabs */}
      <div style={S.tabs}>
        {TABS.map(([k,l]) => <button key={k} style={S.tab(tab===k)} onClick={()=>setTab(k)}>{l}</button>)}
      </div>

      <div style={S.content}>

        {/* ══ 01 DATA INPUT ══════════════════════════════════════════════════ */}
        {tab==="inputs" && (
          <div>
            {/* Site details */}
            <div style={{...S.card,marginBottom:"16px"}}>
              <div style={S.sec}>Client & Site Details</div>
              <div style={{display:"grid",gridTemplateColumns:"repeat(3,1fr)",gap:"12px"}}>
                <div><label style={S.label}>Client Name</label><input style={S.input} value={site.clientName} onChange={e=>updSite("clientName",e.target.value)}/></div>
                <div><label style={S.label}>Site Name</label><input style={S.input} value={site.siteName} onChange={e=>updSite("siteName",e.target.value)}/></div>
                <div><label style={S.label}>Location</label><input style={S.input} value={site.location} onChange={e=>updSite("location",e.target.value)}/></div>
                <div>
                  <label style={S.label}>Sector</label>
                  <select style={S.input} value={site.sector} onChange={e=>updSite("sector",e.target.value)}>
                    {["Commercial","Industrial","Agricultural","Hospitality","Healthcare","Education","Retail","Manufacturing"].map(s=><option key={s}>{s}</option>)}
                  </select>
                </div>
                <div><label style={S.label}>Latitude</label><input style={S.input} type="text" inputMode="decimal" placeholder="-1.2921" value={site.lat} onChange={e=>updSite("lat",e.target.value)}/></div>
                <div><label style={S.label}>Longitude</label><input style={S.input} type="text" inputMode="decimal" placeholder="36.8219" value={site.lng} onChange={e=>updSite("lng",e.target.value)}/></div>
              </div>
            </div>

            {/* Blended tariff calculator */}
            <div style={{...S.card,marginBottom:"16px",border:`1px solid ${C.lt}`}}>
              <div style={S.sec}>Energy Cost — Blended Tariff Calculator</div>
              <div style={{display:"grid",gridTemplateColumns:"repeat(3,1fr)",gap:"12px",marginBottom:"14px"}}>
                <div>
                  <label style={S.label}>Grid supply share</label>
                  <input style={S.input} type="text" inputMode="decimal" placeholder="0.60"
                    value={diesel.gridPct}
                    onChange={e=>setDiesel(p=>({...p,gridPct:e.target.value}))}/>
                  <div style={{fontSize:"9px",color:C.muted,marginTop:"3px"}}>Fraction from grid (e.g. 0.60 = 60%)</div>
                </div>
                <div>
                  <label style={S.label}>Diesel price</label>
                  <input style={S.input} type="text" inputMode="decimal" placeholder="120"
                    value={diesel.dieselPricePerL}
                    onChange={e=>setDiesel(p=>({...p,dieselPricePerL:e.target.value}))}/>
                  <div style={{fontSize:"9px",color:C.muted,marginTop:"3px"}}>KES per litre of diesel fuel</div>
                </div>
                <div>
                  <label style={S.label}>Specific fuel consumption</label>
                  <input style={S.input} type="text" inputMode="decimal" placeholder="0.35"
                    value={diesel.dieselSfc}
                    onChange={e=>setDiesel(p=>({...p,dieselSfc:e.target.value}))}/>
                  <div style={{fontSize:"9px",color:C.muted,marginTop:"3px"}}>Litres per kWh generated (typ. 0.30–0.40)</div>
                </div>
                <div>
                  <label style={S.label}>Genset O&M factor</label>
                  <input style={S.input} type="text" inputMode="decimal" placeholder="1.20"
                    value={diesel.gensetOmFactor}
                    onChange={e=>setDiesel(p=>({...p,gensetOmFactor:e.target.value}))}/>
                  <div style={{fontSize:"9px",color:C.muted,marginTop:"3px"}}>Multiplier for maintenance on top of fuel (1.20 = 20%)</div>
                </div>
                <div>
                  <label style={S.label}>Diesel price escalation</label>
                  <input style={S.input} type="text" inputMode="decimal" placeholder="0.08"
                    value={diesel.dieselEscalation}
                    onChange={e=>setDiesel(p=>({...p,dieselEscalation:e.target.value}))}/>
                  <div style={{fontSize:"9px",color:C.muted,marginTop:"3px"}}>Annual fuel cost inflation (e.g. 0.08 = 8%/yr)</div>
                </div>
                {/* Live blended rate preview */}
                <div style={{background:C.deep,borderRadius:"4px",border:`1px solid ${C.border}`,padding:"10px 12px",display:"flex",flexDirection:"column",justifyContent:"center"}}>
                  {(() => {
                    const gp   = parseFloat(diesel.gridPct)||0.6;
                    const dp   = 1-gp;
                    const dCost= (parseFloat(diesel.dieselPricePerL)||0)*(parseFloat(diesel.dieselSfc)||0)*(parseFloat(diesel.gensetOmFactor)||1);
                    const kplc = calcKplcFromComponents(assumptions);
                    const blended = gp*kplc + dp*dCost;
                    return(<>
                      <div style={{fontSize:"9px",color:C.muted,marginBottom:"4px",letterSpacing:"0.1em"}}>BLENDED EFFECTIVE TARIFF</div>
                      <div style={{fontSize:"22px",fontWeight:700,color:C.amber,lineHeight:1}}>KES {blended.toFixed(2)}</div>
                      <div style={{fontSize:"9px",color:C.muted,marginTop:"4px"}}>/kWh · Year 1</div>
                      <div style={{marginTop:"8px",fontSize:"9px",color:C.muted,lineHeight:1.6}}>
                        Grid ({(gp*100).toFixed(0)}%): KES {kplc.toFixed(2)}/kWh<br/>
                        Diesel ({(dp*100).toFixed(0)}%): KES {dCost.toFixed(2)}/kWh
                      </div>
                    </>);
                  })()}
                </div>
              </div>
              <div style={{fontSize:"9px",color:C.muted,padding:"8px 10px",background:"#f8fafc",borderRadius:"3px",lineHeight:1.6}}>
                ℹ The blended tariff is the weighted average cost of electricity from grid and diesel. This is used in the financial model instead of the grid tariff alone — giving a more accurate payback and ROI for sites that run gensets.
                Diesel cost also inflates annually at the rate you set above.
              </div>
            </div>

            {/* Irradiance */}
            <div style={{...S.card,marginBottom:"16px"}}>
              <div style={S.sec}>Solar Irradiance — PVGIS</div>
              <div style={{display:"flex",gap:"10px",alignItems:"center",marginBottom:"10px",flexWrap:"wrap"}}>
                <button style={{...S.btn("ghost"),whiteSpace:"nowrap"}} onClick={fetchIrr} disabled={irrStatus==="loading"}>
                  {irrStatus==="loading"?"⟳ Fetching…":"↓ Fetch from PVGIS"}
                </button>
                {irrStatus==="ok" && irrMeta && <span style={{fontSize:"10px",color:C.green}}>✓ {irrMeta.location} · avg {irrMeta.annual} kWh/m²/day</span>}
                {irrStatus==="error" && <span style={{fontSize:"10px",color:C.amber}}>⚠ {irrError} — using defaults</span>}
                {irrStatus==="idle" && <span style={{fontSize:"10px",color:C.muted}}>Enter coordinates above, then fetch live irradiance data</span>}
              </div>
              {irrStatus==="ok" && irrData && (
                <div style={{display:"flex",gap:"3px",alignItems:"flex-end",height:"40px"}}>
                  {irrData.map((v,i) => (
                    <div key={i} title={`${MONTH_NAMES[i]}: ${v}`} style={{flex:1,display:"flex",flexDirection:"column",alignItems:"center",gap:"2px"}}>
                      <div style={{width:"100%",background:C.mid,opacity:0.3+v/14,borderRadius:"2px",height:`${Math.round(v/8*40)}px`,minHeight:"3px"}}/>
                      <div style={{fontSize:"7px",color:C.muted}}>{MONTH_NAMES[i][0]}</div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Data input — tabbed logger / bills */}
            <div style={S.card}>
              {/* Mode toggle */}
              <div style={{display:"flex",gap:"0",marginBottom:"18px",borderRadius:"4px",overflow:"hidden",border:`1px solid ${C.border}`,width:"fit-content"}}>
                {[["logger","Power Logger (measured data)"],["bills","Utility Bills (preliminary sizing)"]].map(([m,l])=>(
                  <button key={m} style={{padding:"8px 18px",fontSize:"9px",letterSpacing:"0.1em",textTransform:"uppercase",cursor:"pointer",border:"none",fontFamily:"inherit",fontWeight:inputMode===m?700:500,background:inputMode===m?C.navy:"transparent",color:inputMode===m?"#fff":C.muted,transition:"all 0.15s"}}
                    onClick={()=>{setInputMode(m);setBillError("");}}>
                    {l}
                  </button>
                ))}
              </div>

              {/* ─── LOGGER MODE ─── */}
              {inputMode==="logger" && (<>
                <div style={S.sec}>Power Logger Data — Upload Excel / CSV</div>
                <UploadZone onParsed={onParsed}/>
              </>)}

              {/* ─── BILL MODE ─── */}
              {inputMode==="bills" && (<>
                <div style={S.sec}>Utility Bills — Preliminary Sizing</div>
                <div style={{fontSize:"12px",color:C.muted,marginBottom:"14px",lineHeight:1.65,background:`${C.lt}22`,padding:"10px 12px",borderRadius:"4px",borderLeft:`3px solid ${C.amber}`}}>
                  Upload 1–12 months of KPLC bills (PDF or photo). Claude will extract consumption and demand data from each bill to size the system. For a bankable proposal, replace with measured power logger data.
                </div>

                {/* Upload drop zone */}
                <div
                  style={{border:`2px dashed ${C.border}`,borderRadius:"4px",padding:"28px",textAlign:"center",cursor:"pointer",background:C.deep,marginBottom:"12px"}}
                  onDragOver={e=>{e.preventDefault();e.currentTarget.style.borderColor=C.navy;}}
                  onDragLeave={e=>{e.currentTarget.style.borderColor=C.border;}}
                  onDrop={e=>{e.preventDefault();e.currentTarget.style.borderColor=C.border;handleBillFiles(e.dataTransfer.files);}}
                  onClick={()=>billFileRef.current?.click()}
                >
                  <input ref={billFileRef} type="file" accept=".pdf,.jpg,.jpeg,.png,.webp" multiple style={{display:"none"}}
                    onChange={e=>handleBillFiles(e.target.files)}/>
                  {billParsing
                    ? <div style={{color:C.navy,fontSize:"12px"}}>⟳ Extracting bill data with Claude…</div>
                    : <div>
                        <div style={{fontSize:"12px",color:C.muted,marginBottom:"4px"}}>Drop KPLC bill PDFs or photos here, or click to browse</div>
                        <div style={{fontSize:"10px",color:C.muted}}>PDF · JPG · PNG · WEBP — multiple files accepted — Claude reads each bill automatically</div>
                      </div>
                  }
                </div>

                {/* Manual entry */}
                <div style={{...S.card,marginBottom:"12px",background:C.deep,border:`1px solid ${C.border}`}}>
                  <div style={{fontSize:"9px",color:C.muted,letterSpacing:"0.12em",textTransform:"uppercase",marginBottom:"10px",fontWeight:600}}>Or enter bill data manually</div>
                  <div style={{display:"grid",gridTemplateColumns:"repeat(4,1fr)",gap:"8px",marginBottom:"8px"}}>
                    {[["period","Bill period","2025-11","text"],["unitsKwh","Units (kWh)","45000","number"],["peakKva","Peak demand (kVA)","120","number"],["totalKes","Total bill (KES)","180000","number"]].map(([k,l,ph,t])=>(
                      <div key={k}>
                        <label style={S.label}>{l}</label>
                        <input style={S.input} type={t} placeholder={ph} value={manualBill[k]||""}
                          onChange={e=>setManualBill(p=>({...p,[k]:e.target.value}))}/>
                      </div>
                    ))}
                  </div>
                  <div style={{display:"grid",gridTemplateColumns:"repeat(4,1fr)",gap:"8px",marginBottom:"10px"}}>
                    {[["highRateKwh","High rate (KES/kWh)","7.50"],["lowRateKwh","Low rate (KES/kWh)","5.00"],["fecKwh","FEC (KES/kWh)","2.50"],["ferfaKwh","FERFA (KES/kWh)","1.20"]].map(([k,l,ph])=>(
                      <div key={k}>
                        <label style={S.label}>{l}</label>
                        <input style={S.input} type="number" placeholder={ph} value={manualBill[k]||""}
                          onChange={e=>setManualBill(p=>({...p,[k]:e.target.value}))}/>
                      </div>
                    ))}
                  </div>
                  <div style={{display:"grid",gridTemplateColumns:"repeat(4,1fr) auto",gap:"8px",alignItems:"end"}}>
                    {[["inflationAdjKwh","Inflation (KES/kWh)","0.50"],["ercLevyKwh","ERC levy (KES/kWh)","0.05"],["wraLevyKwh","WRA levy (KES/kWh)","0.03"]].map(([k,l,ph])=>(
                      <div key={k}>
                        <label style={S.label}>{l}</label>
                        <input style={S.input} type="number" placeholder={ph} value={manualBill[k]||""}
                          onChange={e=>setManualBill(p=>({...p,[k]:e.target.value}))}/>
                      </div>
                    ))}
                    <div/>
                    <button style={S.btn("primary")} onClick={addManualBill}>+ Add bill</button>
                  </div>
                </div>

                {/* Error */}
                {billError && (
                  <div style={{fontSize:"11px",color:C.red,background:"#fee2e2",padding:"8px 12px",borderRadius:"4px",marginBottom:"10px",lineHeight:1.5}}>
                    {billError}
                  </div>
                )}

                {/* Bills loaded */}
                {bills.length > 0 && (
                  <div>
                    <div style={{fontSize:"9px",color:C.muted,letterSpacing:"0.12em",textTransform:"uppercase",marginBottom:"8px",fontWeight:600}}>{bills.length} bill{bills.length>1?"s":""} loaded</div>
                    <div style={{overflowX:"auto",marginBottom:"14px"}}>
                      <table style={{width:"100%",borderCollapse:"collapse",fontSize:"11px"}}>
                        <thead><tr>
                          {["Period","Units (kWh)","Peak (kVA)","High rate","Low rate","FEC","FERFA","ERC+WRA+Infl","Calc. tariff (KES/kWh)","Total (KES)",""].map(h=><th key={h} style={S.th}>{h}</th>)}
                        </tr></thead>
                        <tbody>
                          {bills.map((b,i)=>{
                            const t = calcTariffFromBill(b);
                            const hasComps = (b.highRateKwh||0)+(b.lowRateKwh||0)+(b.fecKwh||0) > 0;
                            const fmt1 = v => v>0?v.toFixed(4):"—";
                            return(
                              <tr key={i}>
                                <td style={{...S.td,fontWeight:600,color:C.text}}>{b.period||"—"}</td>
                                <td style={{...S.td,color:C.mid,fontWeight:600}}>{(b.unitsKwh||0).toLocaleString()}</td>
                                <td style={{...S.td,color:C.amber}}>{b.peakKva||"—"}</td>
                                <td style={S.td}>{fmt1(b.highRateKwh)}</td>
                                <td style={S.td}>{fmt1(b.lowRateKwh)}</td>
                                <td style={S.td}>{fmt1(b.fecKwh)}</td>
                                <td style={S.td}>{fmt1(b.ferfaKwh)}</td>
                                <td style={S.td}>{((b.ercLevyKwh||0)+(b.wraLevyKwh||0)+(b.inflationAdjKwh||0))>0?((b.ercLevyKwh||0)+(b.wraLevyKwh||0)+(b.inflationAdjKwh||0)).toFixed(4):"—"}</td>
                                <td style={{...S.td,color:C.green,fontWeight:700}}>
                                  {t>0?t.toFixed(4):"—"}
                                  {!hasComps&&t>0&&<span style={{fontSize:"9px",color:C.amber,marginLeft:"4px"}}>(÷kWh)</span>}
                                </td>
                                <td style={{...S.td,color:C.mid}}>{b.totalKes>0?`KES ${Math.round(b.totalKes).toLocaleString()}`:"—"}</td>
                                <td style={S.td}><button style={{...S.btn("sm"),background:"#fee2e2",color:C.red,border:"1px solid #fca5a5"}} onClick={()=>removeBill(i)}>✕</button></td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>

                    {/* Summary KPIs from bills */}
                    {profile?.fromBills && (
                      <div style={{display:"grid",gridTemplateColumns:"repeat(4,1fr)",gap:"10px",marginBottom:"14px"}}>
                        {[
                          ["Avg monthly consumption",`${profile.avgMonthlyKwh.toLocaleString()} kWh`,"from bills"],
                          ["Avg daily consumption",`${profile.dailyKwh} kWh/day`,"used for sizing"],
                          ["Peak demand",`${profile.peakKw} kW`,profile.avgPeakKva>0?`${profile.avgPeakKva} kVA × PF 0.9`:"estimated from consumption"],
                          ["Calculated tariff",`KES ${profile.effTariff}/kWh`,profile.usedComponentCalc?"(HR+LR+FEC+FERFA+Infl+ERC+WRA)×1.05":"total ÷ kWh"],
                        ].map(([l,v,u])=>(
                          <div key={l} style={S.metric}>
                            <div style={{...S.label,marginBottom:"5px"}}>{l}</div>
                            <div style={{fontSize:"16px",fontWeight:700,color:C.navy}}>{v}</div>
                            <div style={{fontSize:"10px",color:C.muted,marginTop:"3px"}}>{u}</div>
                          </div>
                        ))}
                      </div>
                    )}

                    {/* Seasonal chart */}
                    {profile?.fromBills && profile.seasonal && (
                      <div>
                        <div style={S.ct}>Monthly consumption from bills (kWh)</div>
                        <ResponsiveContainer width="100%" height={140}>
                          <BarChart data={profile.seasonal}>
                            <CartesianGrid stroke={C.border} strokeDasharray="3 3"/>
                            <XAxis dataKey="month" stroke={C.muted} tick={{fontSize:8}}/>
                            <YAxis stroke={C.muted} tick={{fontSize:8}} tickFormatter={v=>`${(v/1000).toFixed(0)}k`}/>
                            <Tooltip formatter={v=>[`${v.toLocaleString()} kWh`,""]}/>
                            <Bar dataKey="kwh" fill={`${C.lt}44`} stroke={C.mid} strokeWidth={1} name="Monthly kWh"/>
                          </BarChart>
                        </ResponsiveContainer>
                        <div style={{fontSize:"10px",color:C.amber,marginTop:"6px"}}>
                          ⚠ Seasonal variation shown is estimated. Add bills from different months to improve accuracy.
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </>)}

              {/* 5-min raw chart if parsed */}
              {profile && profile.rawRows.length > 0 && (
                <div style={{marginTop:"20px"}}>
                  <div style={S.ct}>5-minute power profile — {fileName}</div>
                  <ResponsiveContainer width="100%" height={220}>
                    <ComposedChart data={profile.rawRows}>
                      <CartesianGrid stroke={C.border} strokeDasharray="3 3"/>
                      <XAxis dataKey="time" stroke={C.muted} tick={{fontSize:7}} interval={Math.floor(profile.rawRows.length/12)}/>
                      <YAxis stroke={C.muted} tick={{fontSize:8}} label={{value:"kW",angle:-90,position:"insideLeft",fill:C.muted,fontSize:9}}/>
                      <Tooltip content={<CT unit="kW"/>}/>
                      {profile.pvKwh > 0 && <Area type="monotone" dataKey="pv"   fill="#2E75B633" stroke="#2E75B6" strokeWidth={1} dot={false} name="PV gen"/>}
                      {profile.mainsKwh > 0 && <Area type="monotone" dataKey="mains" fill="#1F3A5F33" stroke="#1F3A5F" strokeWidth={1} dot={false} name="Mains import"/>}
                      <Line type="monotone" dataKey="cons" stroke="#1F3A5F" strokeWidth={1.5} dot={false} name="Total consumption"/>
                    </ComposedChart>
                  </ResponsiveContainer>
                  <div style={{display:"flex",gap:"14px",marginTop:"8px",fontSize:"10px",color:C.muted}}>
                    <span style={{color:C.amber}}>— Total consumption</span>
                    {profile.pvKwh   >0 && <span style={{color:C.green}}>— PV generation</span>}
                    {profile.mainsKwh>0 && <span style={{color:C.mid}}>— Mains import</span>}
                  </div>

                  {/* Hourly summary table */}
                  <div style={{marginTop:"16px"}}>
                    <div style={S.ct}>Hourly demand summary (averaged across {profile.days} day{profile.days>1?"s":""})</div>
                    <div style={{overflowX:"auto"}}>
                      <table style={{width:"100%",borderCollapse:"collapse",fontSize:"10px"}}>
                        <thead><tr>
                          <th style={S.th}>Hour</th>
                          {Array.from({length:24},(_,h)=><th key={h} style={{...S.th,textAlign:"center",padding:"5px 4px"}}>{h}:00</th>)}
                        </tr></thead>
                        <tbody>
                          <tr>
                            <td style={{...S.td,color:C.muted,whiteSpace:"nowrap"}}>Avg kW</td>
                            {profile.hourlyAvg.map((v,h)=>(
                              <td key={h} style={{...S.td,textAlign:"center",padding:"5px 4px",
                                color: v>50?C.red:v>20?C.amber:v>5?C.mid:C.muted,
                                fontWeight:v===Math.max(...profile.hourlyAvg)?700:400,
                              }}>{v.toFixed(1)}</td>
                            ))}
                          </tr>
                          <tr>
                            <td style={{...S.td,color:C.muted,whiteSpace:"nowrap"}}>Max kW</td>
                            {profile.hourlyMax.map((v,h)=>(
                              <td key={h} style={{...S.td,textAlign:"center",padding:"5px 4px",color:C.muted}}>{v.toFixed(1)}</td>
                            ))}
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* ══ 02 ASSUMPTIONS ════════════════════════════════════════════════ */}
        {tab==="assumptions" && (
          <div>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:"16px"}}>
              <div style={{fontSize:"10px",color:C.muted}}>C&I-specific assumptions. Changes apply on next simulation run.</div>
              <button style={S.btn("ghost")} onClick={()=>setAssumptions(DEFAULT_ASSUMPTIONS)}>↺ Reset Defaults</button>
            </div>
            <ASection title="Financial — Tariff & Revenue" cols={3}>
              <AField id="demandCharge"      label="Demand charge"         unit="KES/kVA"  hint="Monthly maximum demand charge"   value={assumptions.demandCharge}      onUpdate={updAsm}/>
              <AField id="targetSelfSufficiency" label="Target self-sufficiency" unit="0–1" hint="Fraction of demand covered by solar+BESS" value={assumptions.targetSelfSufficiency} onUpdate={updAsm}/>
              <AField id="tariffEscalation"  label="Tariff escalation"     unit="fraction/yr" hint="Annual price increase"        value={assumptions.tariffEscalation}  onUpdate={updAsm}/>
              <AField id="projectLifeYears"  label="Project life"          unit="years"    hint="Analysis horizon"                value={assumptions.projectLifeYears}  onUpdate={updAsm}/>
              <AField id="omCostPerYearKes"  label="O&M cost"              unit="KES/yr"   hint="Annual operations & maintenance"  value={assumptions.omCostPerYearKes}  onUpdate={updAsm}/>
              <AField id="inflationRate"     label="O&M inflation"         unit="fraction/yr" hint="Annual O&M cost increase. E.g. 0.06 = 6%" value={assumptions.inflationRate} onUpdate={updAsm}/>
              <AField id="discountRate"      label="Discount rate (NPV)"   unit="fraction" hint="For discounted NPV. E.g. 0.10 = 10%" value={assumptions.discountRate}   onUpdate={updAsm}/>
            </ASection>

            {/* KPLC Component Tariff */}
            <div style={{...S.card, marginBottom:"16px", border:`1px solid ${C.lt}`}}>
              <div style={S.sec}>KPLC Tariff — Component Rate Calculation</div>
              <div style={{fontSize:"11px",color:C.muted,marginBottom:"14px",lineHeight:1.65,background:`${C.lt}22`,padding:"9px 12px",borderRadius:"4px",borderLeft:`3px solid ${C.mid}`}}>
                Enter each component from your KPLC bill (KES/kWh). These are auto-populated when utility bills are uploaded.
                Formula used in financial model: <strong style={{color:C.navy}}>(HR + LR + FEC + FERFA + Infl + ERC + WRA) × 1.05</strong>
              </div>
              <div style={{display:"grid",gridTemplateColumns:"repeat(4,1fr)",gap:"12px",marginBottom:"12px"}}>
                {[
                  ["highRateKwh",     "High rate (HR)",            "Peak rate energy charge per kWh"],
                  ["lowRateKwh",      "Low rate (LR)",             "Off-peak rate energy charge per kWh"],
                  ["fecKwh",          "Fuel energy charge (FEC)",  "Fuel cost surcharge per kWh"],
                  ["ferfaKwh",        "FERFA",                     "Foreign exchange adjustment per kWh"],
                  ["inflationAdjKwh", "Inflation adj. (IAS)",      "Inflation adjustment surcharge per kWh"],
                  ["ercLevyKwh",      "ERC levy",                  "Energy Regulatory Commission per kWh"],
                  ["wraLevyKwh",      "WRA levy",                  "Water Resources Authority per kWh"],
                ].map(([id, label, hint]) => (
                  <AField key={id} id={id} label={label} unit="KES/kWh" hint={hint}
                    value={assumptions[id]} onUpdate={updAsm}/>
                ))}
                {/* Live formula result */}
                {(()=>{
                  const high  = parseFloat(assumptions.highRateKwh)||0;
                  const low   = parseFloat(assumptions.lowRateKwh)||0;
                  const fec   = parseFloat(assumptions.fecKwh)||0;
                  const ferfa = parseFloat(assumptions.ferfaKwh)||0;
                  const infl  = parseFloat(assumptions.inflationAdjKwh)||0;
                  const erc   = parseFloat(assumptions.ercLevyKwh)||0;
                  const wra   = parseFloat(assumptions.wraLevyKwh)||0;
                  const sum   = high+low+fec+ferfa+infl+erc+wra;
                  const result= sum > 0.5 ? +(sum*1.05).toFixed(4) : parseFloat(assumptions.kplcRate)||22.5;
                  const usingComponents = sum > 0.5;
                  return (
                    <div style={{background:C.navy,borderRadius:"4px",padding:"12px 14px",display:"flex",flexDirection:"column",justifyContent:"center"}}>
                      <div style={{fontSize:"9px",color:C.lt,letterSpacing:"0.12em",textTransform:"uppercase",marginBottom:"5px"}}>
                        {usingComponents ? "Calculated tariff" : "Manual tariff (no components)"}
                      </div>
                      <div style={{fontSize:"22px",fontWeight:700,color:usingComponents?C.lt:C.amber,lineHeight:1}}>
                        KES {result.toFixed(2)}
                      </div>
                      <div style={{fontSize:"9px",color:"rgba(191,211,240,0.6)",marginTop:"4px"}}>/kWh · used in financial model</div>
                      {usingComponents && (
                        <div style={{fontSize:"9px",color:C.lt,marginTop:"6px",lineHeight:1.6,opacity:0.7}}>
                          Sum: KES {sum.toFixed(4)} × 1.05
                        </div>
                      )}
                    </div>
                  );
                })()}
              </div>
              <div style={{borderTop:`1px solid ${C.border}`,paddingTop:"10px",marginTop:"4px"}}>
                <label style={S.label}>Fallback tariff — used when no components are entered</label>
                <div style={{display:"flex",gap:"10px",alignItems:"center"}}>
                  <input style={{...S.input, maxWidth:"160px"}} type="text" inputMode="decimal"
                    placeholder="22.5" value={assumptions.kplcRate}
                    onChange={e=>updAsm("kplcRate", e.target.value)}/>
                  <span style={{fontSize:"11px",color:C.muted}}>KES/kWh — enter components above for the accurate bill-derived rate</span>
                </div>
              </div>
            </div>
            <ASection title="Financial — Capital Costs" cols={3}>
              <AField id="pvCostPerKwp"      label="PV cost"               unit="KES/kWp"  hint="C&I supply & install per kWp"    value={assumptions.pvCostPerKwp}      onUpdate={updAsm}/>
              <AField id="bessCostPerKwh"    label="BESS cost"             unit="KES/kWh"  hint="Battery storage per kWh"         value={assumptions.bessCostPerKwh}    onUpdate={updAsm}/>
              <AField id="installationCostPerPanel" label="Installation cost" unit="KES/panel" hint="BOS, civils & commissioning per panel installed" value={assumptions.installationCostPerPanel} onUpdate={updAsm}/>
            </ASection>
            {/* Operating Calendar */}
            <div style={{...S.card, marginBottom:"16px", border:`1px solid ${C.lt}`}}>
              <div style={S.sec}>Operating Calendar — Weekend & Holiday Adjustment</div>
              <div style={{fontSize:"11px",color:C.muted,marginBottom:"14px",lineHeight:1.65,background:`${C.amber}15`,padding:"9px 12px",borderRadius:"4px",borderLeft:`3px solid ${C.amber}`}}>
                Assuming 365 full operating days overstates annual savings by up to 19% for facilities closed on weekends.
                Set the demand fraction for Saturdays and Sundays relative to a full weekday.
              </div>
              <div style={{display:"grid",gridTemplateColumns:"repeat(4,1fr)",gap:"14px",alignItems:"end"}}>
                <AField id="satFactor"             label="Saturday factor"         unit="0–1"   hint="Fraction of weekday demand. 0.55 = half day till noon" value={assumptions.satFactor}             onUpdate={updAsm}/>
                <AField id="sunFactor"             label="Sunday / holiday factor" unit="0–1"   hint="Base load fraction. 0.10 = essential loads only"       value={assumptions.sunFactor}             onUpdate={updAsm}/>
                <AField id="publicHolidaysPerYear" label="Public holidays / year"  unit="days"  hint="Kenya: ~10. Treated same as Sundays"                   value={assumptions.publicHolidaysPerYear} onUpdate={updAsm}/>
                {(()=>{
                  const sat = parseFloat(assumptions.satFactor)||0.55;
                  const sun = parseFloat(assumptions.sunFactor)||0.10;
                  const ph  = parseFloat(assumptions.publicHolidaysPerYear)||10;
                  const eff = 261*1.0 + 52*sat + (52+ph)*sun;
                  const pct = (eff/365*100).toFixed(1);
                  const saving = (100 - eff/365*100).toFixed(1);
                  return(
                    <div style={{background:C.navy,borderRadius:"4px",padding:"12px 14px"}}>
                      <div style={{fontSize:"9px",color:C.lt,letterSpacing:"0.12em",textTransform:"uppercase",marginBottom:"5px"}}>Effective operating days/yr</div>
                      <div style={{fontSize:"22px",fontWeight:700,color:C.lt,lineHeight:1}}>{eff.toFixed(1)}</div>
                      <div style={{fontSize:"9px",color:"rgba(191,211,240,0.7)",marginTop:"5px",lineHeight:1.6}}>
                        {pct}% of 365 · saves {saving}% overstatement<br/>
                        261 wkdays + {(52*sat).toFixed(0)} Sat + {((52+ph)*sun).toFixed(0)} Sun/PH
                      </div>
                    </div>
                  );
                })()}
              </div>
            </div>

            <ASection title="Technical — PV System" cols={4}>
              <AField id="systemEff"         label="System efficiency"     unit="0–1"      hint="Inverter + wiring + soiling"     value={assumptions.systemEff}         onUpdate={updAsm}/>
              <AField id="moduleWp"          label="Module size"           unit="Wp"       hint="Panel rated power"               value={assumptions.moduleWp}          onUpdate={updAsm}/>
              <AField id="pvDegradationPerYear" label="PV degradation"     unit="fraction/yr" hint="Annual output decline"        value={assumptions.pvDegradationPerYear} onUpdate={updAsm}/>
              <AField id="co2FactorKgPerKwh" label="Grid CO₂ factor"      unit="kg/kWh"   hint="Grid emission intensity"         value={assumptions.co2FactorKgPerKwh} onUpdate={updAsm}/>
            </ASection>
            <ASection title="Technical — Battery (BESS)" cols={5}>
              <AField id="bessUnitKwh"       label="Unit size"             unit="kWh"      hint="Single battery cabinet"          value={assumptions.bessUnitKwh}       onUpdate={updAsm}/>
              <AField id="bessDod"           label="Depth of discharge"    unit="0–1"      hint="Max usable fraction"             value={assumptions.bessDod}           onUpdate={updAsm}/>
              <AField id="bessDeg10yr"       label="10yr degradation"      unit="fraction" hint="Capacity loss after 10 years"    value={assumptions.bessDeg10yr}       onUpdate={updAsm}/>
              <AField id="bessRoundTripEff"  label="Round-trip eff."       unit="0–1"      hint="Charge/discharge ratio"          value={assumptions.bessRoundTripEff}  onUpdate={updAsm}/>
              <AField id="bessInitSoc"       label="Initial SoC"           unit="0–1"      hint="Battery level at sim start"      value={assumptions.bessInitSoc}       onUpdate={updAsm}/>
            </ASection>

            {/* ══ APFC — Automatic Power Factor Correction ═══════════════════ */}
            <ASection title="Power Factor Correction — APFC Design Tool" cols={1}>
              {(() => {
                // ── 1. Resolve the existing power factor from best available data ──
                let pfExisting = 0.85;
                let pfSource   = "Manual / default";
                let pTotalKw   = null;
                let sTotalKva  = null;

                const loggerPf = profile?.avgPowerFactor;
                const billsHaveKva = Array.isArray(bills) && bills.length > 0
                  && bills.some(b => (parseFloat(b.peakKva)||0) > 0);

                if (loggerPf && loggerPf > 0 && loggerPf <= 1) {
                  pfExisting = loggerPf;
                  pfSource   = "Power logger (measured)";
                  if (profile?.avgKw)  pTotalKw  = profile.avgKw;
                  if (profile?.avgKva) sTotalKva = profile.avgKva;
                } else if (billsHaveKva) {
                  // Bills don't carry true PF — KPLC's peakKw is computed as peakKva × 0.9.
                  // We therefore use 0.90 as the bill-derived assumption, in line with the
                  // platform's existing convention.
                  const avgKva = bills.reduce((s,b)=>s+(parseFloat(b.peakKva)||0),0)/bills.length;
                  pfExisting = 0.90;
                  pfSource   = `KPLC bills (${bills.length} bill${bills.length>1?"s":""}, PF assumed 0.90)`;
                  sTotalKva  = avgKva;
                  pTotalKw   = avgKva * 0.90;
                }

                // Derive P (kW) and S (kVA) from results if not already known
                if (pTotalKw == null) {
                  pTotalKw = results?.peakKw ?? (profile?.peakKw ?? 100);
                }
                if (sTotalKva == null) {
                  sTotalKva = pTotalKw / Math.max(0.01, pfExisting);
                }

                // ── 2. Existing reactive power Q = P × tan(acos(PF)) ──
                const thetaExisting = Math.acos(pfExisting);
                const qExisting     = pTotalKw * Math.tan(thetaExisting);

                // ── 3. PF after solar PV (PV injects no reactive power) ──
                const pvKwp        = parseFloat(results?.actualPvKwp) || 0;
                const pSolarAvg    = pvKwp * 0.75;            // ~75% of nameplate during sun hours
                const pAfterSolar  = Math.max(0.01, pTotalKw - pSolarAvg);
                const qAfterSolar  = qExisting;                // unchanged
                const sAfterSolar  = Math.sqrt(pAfterSolar*pAfterSolar + qAfterSolar*qAfterSolar);
                const pfAfterSolar = pAfterSolar / sAfterSolar;

                // ── 4. Required correction to hit target PF ──
                const pfTarget   = parseFloat(assumptions.apfcTargetPf)    || 0.95;
                const stepSize   = parseFloat(assumptions.apfcStepSize)    || 10;
                const detuning   = parseFloat(assumptions.apfcDetuning)    || 0.07;
                const costUsdKvar= parseFloat(assumptions.apfcCostPerKvar) || 65;

                const qTarget     = pAfterSolar * Math.tan(Math.acos(pfTarget));
                const qCorrection = Math.max(0, qExisting - qTarget);

                // ── 5. Panel sizing (round up to nearest step) ──
                const numSteps    = Math.ceil(qCorrection / Math.max(0.01, stepSize));
                const panelRating = numSteps * stepSize;

                // ── 6. Detuning reactor sizing ──
                const reactorPerStep = detuning * stepSize;
                const tuningHz       = 50 / Math.sqrt(Math.max(0.001, detuning));

                // ── 7. Financial impact ──
                const demandRate = parseFloat(assumptions.demandCharge) || 800;
                const usdToKes   = 130;

                // KPLC PF surcharge: only applies when measured PF < 0.90
                const measuredPf = pfAfterSolar; // PF as KPLC sees at the meter
                const monthlySurcharge = measuredPf < 0.90
                  ? sAfterSolar * demandRate * (0.90/measuredPf - 1)
                  : 0;
                const annualPfSaving = monthlySurcharge * 12;

                // Demand-charge saving from kVA reduction
                const sBefore   = sAfterSolar;
                const sAfterPfc = pAfterSolar / pfTarget;
                const kvaReduction      = Math.max(0, sBefore - sAfterPfc);
                const annualDemandSaving= kvaReduction * demandRate * 12;
                const annualTotalSaving = annualPfSaving + annualDemandSaving;

                const panelCostUsd = panelRating * costUsdKvar;
                const panelCostKes = panelCostUsd * usdToKes;
                const paybackMonths= annualTotalSaving > 0
                  ? (panelCostKes / (annualTotalSaving / 12))
                  : null;

                // Helpers
                const fmtKes = n => "KES " + Math.round(n).toLocaleString("en-GB");
                const fmtUsd = n => "USD " + Math.round(n).toLocaleString("en-GB");
                const pfColor = pf => pf >= 0.95
                  ? { fg: "#166534", bg: "#DCFCE7" }
                  : pf >= 0.90
                  ? { fg: "#92400E", bg: "#FEF3C7" }
                  : { fg: "#DC2626", bg: "#FEE2E2" };

                const pfCard = (label, pf) => {
                  const c = pfColor(pf);
                  return (
                    <div style={{background:c.bg,borderRadius:"4px",padding:"10px"}}>
                      <div style={{fontSize:"9px",color:C.muted,letterSpacing:"0.05em",textTransform:"uppercase"}}>{label}</div>
                      <div style={{fontSize:"22px",fontWeight:700,color:c.fg,lineHeight:1.1,marginTop:"3px"}}>
                        {pf.toFixed(3)}
                      </div>
                    </div>
                  );
                };
                const numCard = (label, value, sub) => (
                  <div style={{background:C.deep,borderRadius:"4px",padding:"10px"}}>
                    <div style={{fontSize:"9px",color:C.muted,letterSpacing:"0.05em",textTransform:"uppercase"}}>{label}</div>
                    <div style={{fontSize:"16px",fontWeight:700,color:C.navy,lineHeight:1.2,marginTop:"3px"}}>{value}</div>
                    {sub && <div style={{fontSize:"9px",color:C.muted,marginTop:"3px"}}>{sub}</div>}
                  </div>
                );

                return (
                  <div>
                    {/* a) Source indicator */}
                    <div style={{fontSize:"10px",color:C.muted,marginBottom:"10px"}}>
                      <strong style={{color:C.navy}}>Data source:</strong> {pfSource}
                      {pvKwp > 0 && <span> · Solar PV: {pvKwp} kWp (avg {pSolarAvg.toFixed(1)} kW during sun)</span>}
                      {pvKwp === 0 && <span style={{color:C.amber}}> · Run a simulation to see post-PV impact</span>}
                    </div>

                    {/* b) PF status cards */}
                    <div style={{display:"grid",gridTemplateColumns:"repeat(3,1fr)",gap:"10px",marginBottom:"10px"}}>
                      {pfCard("Existing PF", pfExisting)}
                      {pfCard("PF after solar PV", pfAfterSolar)}
                      {pfCard("Target PF", pfTarget)}
                    </div>

                    {/* c) Warning banner */}
                    {pfAfterSolar < 0.90 && pvKwp > 0 && (
                      <div style={{background:"#FEF3C7",border:"1px solid #F59E0B",color:"#92400E",borderRadius:"4px",padding:"10px 14px",marginBottom:"10px",fontSize:"11px",lineHeight:1.6}}>
                        <strong>⚠ PF will fall below 0.90 after solar installation</strong> ({pfAfterSolar.toFixed(3)}).
                        KPLC applies a power-factor surcharge below 0.90. An APFC capacitor bank is recommended
                        to restore PF to {pfTarget.toFixed(2)} and avoid the surcharge.
                      </div>
                    )}

                    {/* d) Reactive power analysis */}
                    <div style={{fontSize:"9px",fontWeight:700,color:C.red,textTransform:"uppercase",letterSpacing:"0.5px",margin:"14px 0 8px"}}>
                      Reactive power analysis
                    </div>
                    <div style={{display:"grid",gridTemplateColumns:"repeat(4,1fr)",gap:"10px",marginBottom:"10px"}}>
                      {numCard("Existing Q",       `${qExisting.toFixed(1)} kVAR`, `at PF ${pfExisting.toFixed(3)}`)}
                      {numCard("Q after solar",    `${qAfterSolar.toFixed(1)} kVAR`, "unchanged — PV inj. 0 kVAR")}
                      {numCard("Q target",         `${qTarget.toFixed(1)} kVAR`,    `at PF ${pfTarget.toFixed(2)}`)}
                      {numCard("Correction needed",`${qCorrection.toFixed(1)} kVAR`, qCorrection > 0 ? "capacitive" : "none")}
                    </div>

                    {/* e) APFC panel design table */}
                    {qCorrection > 0 && (
                      <>
                        <div style={{fontSize:"9px",fontWeight:700,color:C.red,textTransform:"uppercase",letterSpacing:"0.5px",margin:"14px 0 8px"}}>
                          APFC panel design
                        </div>
                        <table style={{width:"100%",fontSize:"11px",borderCollapse:"collapse",marginBottom:"10px"}}>
                          <tbody>
                            {[
                              ["Panel rating",       `${panelRating} kVAR`,   `${numSteps} × ${stepSize} kVAR steps`],
                              ["Detuning reactors",  `${(detuning*100).toFixed(0)}% (${tuningHz.toFixed(0)} Hz tuning)`, `${reactorPerStep.toFixed(2)} kVAR per step`],
                              ["Controller",         "Automatic 6-step / 12-step", `regulates capacitor steps based on real-time PF`],
                              ["Connection point",   "3-phase, at main distribution board", `downstream of the KPLC meter`],
                            ].map(([k,v,sub],i)=>(
                              <tr key={i} style={{borderBottom:`1px solid ${C.deep}`}}>
                                <td style={{padding:"7px 4px",color:C.muted,width:"30%"}}>{k}</td>
                                <td style={{padding:"7px 4px",color:C.navy,fontWeight:600,width:"30%"}}>{v}</td>
                                <td style={{padding:"7px 4px",color:C.muted,fontSize:"10px"}}>{sub}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </>
                    )}

                    {/* f) Financial impact */}
                    {qCorrection > 0 && (
                      <>
                        <div style={{fontSize:"9px",fontWeight:700,color:C.red,textTransform:"uppercase",letterSpacing:"0.5px",margin:"14px 0 8px"}}>
                          Financial impact
                        </div>
                        <div style={{display:"grid",gridTemplateColumns:"repeat(4,1fr)",gap:"10px",marginBottom:"10px"}}>
                          {numCard("PF surcharge eliminated", fmtKes(annualPfSaving), "per year (KPLC)")}
                          {numCard("Demand charge saving",    fmtKes(annualDemandSaving), `${kvaReduction.toFixed(1)} kVA reduction`)}
                          {numCard("APFC panel cost",         fmtUsd(panelCostUsd), `${fmtKes(panelCostKes)} @ KES 130/USD`)}
                          {numCard("Payback",
                            paybackMonths != null
                              ? `${paybackMonths.toFixed(1)} months`
                              : "—",
                            paybackMonths != null
                              ? `${(paybackMonths/12).toFixed(1)} years`
                              : "no recoverable saving")}
                        </div>
                      </>
                    )}

                    {/* g) Formulas — expandable */}
                    <details style={{marginTop:"10px",fontSize:"11px"}}>
                      <summary style={{cursor:"pointer",color:C.mid,fontWeight:600,padding:"6px 0"}}>
                        📐 Formulas &amp; methodology used
                      </summary>
                      <pre style={{background:C.deep,padding:"12px 14px",borderRadius:"4px",fontFamily:"'Consolas','Menlo',monospace",fontSize:"10.5px",color:C.text,whiteSpace:"pre-wrap",lineHeight:1.6,marginTop:"6px"}}>
{`Step 1 — Existing PF
  PF_existing = P / S = ${pTotalKw.toFixed(2)} / ${sTotalKva.toFixed(2)} = ${pfExisting.toFixed(4)}

Step 2 — Existing reactive power
  θ          = acos(${pfExisting.toFixed(4)}) = ${(thetaExisting*180/Math.PI).toFixed(2)}°
  Q_existing = P × tan(θ) = ${pTotalKw.toFixed(2)} × ${Math.tan(thetaExisting).toFixed(4)} = ${qExisting.toFixed(2)} kVAR

Step 3 — PF after solar PV
  P_solar_avg     = ${pvKwp} kWp × 0.75 = ${pSolarAvg.toFixed(2)} kW
  P_after_solar   = ${pTotalKw.toFixed(2)} − ${pSolarAvg.toFixed(2)} = ${pAfterSolar.toFixed(2)} kW
  Q_after_solar   = ${qAfterSolar.toFixed(2)} kVAR (unchanged — PV injects no reactive)
  S_after_solar   = √(${pAfterSolar.toFixed(2)}² + ${qAfterSolar.toFixed(2)}²) = ${sAfterSolar.toFixed(2)} kVA
  PF_after_solar  = ${pAfterSolar.toFixed(2)} / ${sAfterSolar.toFixed(2)} = ${pfAfterSolar.toFixed(4)}

Step 4 — Required capacitive correction
  Q_target      = P_after_solar × tan(acos(${pfTarget.toFixed(2)})) = ${qTarget.toFixed(2)} kVAR
  Q_correction  = ${qExisting.toFixed(2)} − ${qTarget.toFixed(2)} = ${qCorrection.toFixed(2)} kVAR

Step 5 — APFC panel sizing
  Steps         = ceil(${qCorrection.toFixed(2)} / ${stepSize}) = ${numSteps}
  Panel rating  = ${numSteps} × ${stepSize} = ${panelRating} kVAR

Step 6 — Detuning reactors
  p             = ${detuning} (${(detuning*100).toFixed(0)}% detuning)
  Reactor/step  = ${detuning} × ${stepSize} = ${reactorPerStep.toFixed(2)} kVAR
  Tuning freq.  = 50 / √${detuning} = ${tuningHz.toFixed(1)} Hz (must be < 250 Hz / 5th harmonic)

Step 7 — Financial
  Monthly PF surcharge = ${sAfterSolar.toFixed(2)} × ${demandRate} × (0.90/${measuredPf.toFixed(4)} − 1)
                       = ${fmtKes(monthlySurcharge)} / month  ${measuredPf >= 0.90 ? "(zero — PF already ≥ 0.90)" : ""}
  Annual PF saving     = ${fmtKes(annualPfSaving)}
  kVA reduction        = ${sBefore.toFixed(2)} − ${sAfterPfc.toFixed(2)} = ${kvaReduction.toFixed(2)} kVA
  Annual demand saving = ${kvaReduction.toFixed(2)} × ${demandRate} × 12 = ${fmtKes(annualDemandSaving)}
  Panel cost           = ${panelRating} × USD ${costUsdKvar} = ${fmtUsd(panelCostUsd)} ≈ ${fmtKes(panelCostKes)}
  Payback              = ${paybackMonths != null ? paybackMonths.toFixed(1) + " months" : "—"}`}
                      </pre>
                    </details>

                    {/* h) No correction needed */}
                    {qCorrection <= 0 && (
                      <div style={{background:"#DCFCE7",color:"#166534",borderRadius:"4px",padding:"10px 14px",margin:"10px 0",fontSize:"11px",lineHeight:1.6}}>
                        ✓ <strong>No power factor correction needed.</strong> Existing PF already meets the target of {pfTarget.toFixed(2)}.
                      </div>
                    )}

                    {/* i) Adjustable parameters */}
                    <div style={{fontSize:"9px",fontWeight:700,color:C.red,textTransform:"uppercase",letterSpacing:"0.5px",margin:"18px 0 8px"}}>
                      APFC parameters
                    </div>
                    <div style={{display:"grid",gridTemplateColumns:"repeat(4,1fr)",gap:"13px"}}>
                      <AField id="apfcTargetPf"    label="Target PF"          unit="0–1"        hint="Aim for 0.95 to give margin above the 0.90 threshold" value={assumptions.apfcTargetPf}    onUpdate={updAsm}/>
                      <AField id="apfcStepSize"    label="Capacitor step"     unit="kVAR"       hint="Standard sizes: 5, 10, 12.5, 25, 50"                  value={assumptions.apfcStepSize}    onUpdate={updAsm}/>
                      <AField id="apfcDetuning"    label="Detuning factor"    unit="fraction"   hint="0.07 standard · 0.14 for high-THD sites"              value={assumptions.apfcDetuning}    onUpdate={updAsm}/>
                      <AField id="apfcCostPerKvar" label="APFC cost"          unit="USD/kVAR"   hint="Panel + controller + installation"                    value={assumptions.apfcCostPerKvar} onUpdate={updAsm}/>
                    </div>
                  </div>
                );
              })()}
            </ASection>
          </div>
        )}

        {/* ══ 03 DASHBOARD ═════════════════════════════════════════════════ */}
        {tab==="dashboard" && results && (
          <div>
            {profile?.fromBills && (
              <div style={{background:`${C.amber}18`,border:`1px solid ${C.amber}`,borderRadius:"4px",padding:"10px 16px",marginBottom:"16px",fontSize:"12px",color:"#78350f",lineHeight:1.7}}>
                <strong>Preliminary sizing — based on {profile.billCount} utility bill{profile.billCount>1?"s":""}.</strong> Load profile is synthetic. Results are indicative and suitable for client discussion — replace with power logger data for a bankable proposal.
              </div>
            )}
            {/* KPI strip */}
            <div style={S.grid4}>
              {[
                ["Daily Demand",     `${results.dailyKwh} kWh`,    "kWh/day"],
                ["Peak Demand",      `${results.peakKw} kW`,       "measured"],
                ["PV System",        `${results.actualPvKwp} kWp`, `${results.numModules}×${assumptions.moduleWp}Wp`],
                ["BESS",             `${results.bessActual} kWh`,   `${results.bessUnits}×${assumptions.bessUnitKwh}kWh`],
              ].map(([l,v,u])=>(
                <div key={l} style={S.metric}>
                  <div style={{fontSize:"9px",color:C.muted,letterSpacing:"0.12em",textTransform:"uppercase",marginBottom:"5px"}}>{l}</div>
                  <div style={{fontSize:"22px",fontWeight:700,color:C.mid,lineHeight:1}}>{v}</div>
                  <div style={{fontSize:"10px",color:C.muted,marginTop:"3px"}}>{u}</div>
                </div>
              ))}
            </div>
            <div style={S.grid4}>
              {[
                ["Self-sufficiency",   `${results.selfSufficiencyPct}%`,                "of demand covered"],
                ["Blended tariff Y1",  `KES ${safeKes(results.blendedRateY1)}/kWh`,   `grid ${((results.gridPct||0)*100).toFixed(0)}% + diesel ${((1-(results.gridPct||0))*100).toFixed(0)}%`],
                ["Y1 savings",         `KES ${results.annualSavingsY1.toLocaleString()}`, "energy + demand"],
                ["Payback (cost basis)",`${results.paybackYears} yrs`,                  "on equipment cost"],
              ].map(([l,v,u])=>(
                <div key={l} style={S.metric}>
                  <div style={{fontSize:"9px",color:C.muted,letterSpacing:"0.12em",textTransform:"uppercase",marginBottom:"5px"}}>{l}</div>
                  <div style={{fontSize:"18px",fontWeight:700,color:C.green,lineHeight:1}}>{v}</div>
                  {u && <div style={{fontSize:"10px",color:C.muted,marginTop:"3px"}}>{u}</div>}
                </div>
              ))}
            </div>

            {irrMeta && <div style={{...S.badge("navy"),marginBottom:"14px",display:"inline-flex",padding:"5px 10px",fontSize:"10px"}}>✓ PVGIS · {irrMeta.location} · {irrMeta.annual} kWh/m²/day avg</div>}
            {results?.effectiveDays && <div style={{...S.badge("navy"),marginBottom:"14px",marginLeft:"8px",display:"inline-flex",padding:"5px 10px",fontSize:"10px"}}>📅 {results.effectiveDays} effective days/yr ({(results.calendarFactor*100).toFixed(1)}% of 365)</div>}

            {/* Charts */}
            <div style={S.grid2}>
              <div style={S.card}>
                <div style={S.ct}>Measured demand vs designed solar generation (kW)</div>
                <ResponsiveContainer width="100%" height={240}>
                  <ComposedChart data={Array.from({length:24},(_,h)=>({
                    hour:`${h}:00`,
                    demand: profile.hourlyAvg[h],
                    solar:  results.hourlyGenKw[h],
                    maxDemand: profile.hourlyMax[h],
                  }))}>
                    <CartesianGrid stroke={C.border} strokeDasharray="3 3"/>
                    <XAxis dataKey="hour" stroke={C.muted} tick={{fontSize:8}} interval={2}/>
                    <YAxis stroke={C.muted} tick={{fontSize:8}} label={{value:"kW",angle:-90,position:"insideLeft",fill:C.muted,fontSize:9}}/>
                    <Tooltip content={<CT unit="kW"/>}/>
                    <Legend verticalAlign="top" height={32} wrapperStyle={{fontSize:"10px"}}/>
                    <Bar dataKey="maxDemand" fill="#C0392B33" name="Peak demand envelope" radius={[2,2,0,0]}/>
                    <Line type="monotone" dataKey="demand" stroke="#1F3A5F" strokeWidth={2.5} dot={false} name="Avg demand (kW)"/>
                    <Line type="monotone" dataKey="solar"  stroke="#2E75B6" strokeWidth={2}   dot={false} name="Solar generation (kW)"/>
                  </ComposedChart>
                </ResponsiveContainer>
              </div>
              <div style={S.card}>
                <div style={S.ct}>Monthly generation vs demand (kWh)</div>
                <ResponsiveContainer width="100%" height={240}>
                  <BarChart data={results.monthlyData}>
                    <CartesianGrid stroke={C.border} strokeDasharray="3 3"/>
                    <XAxis dataKey="month" stroke={C.muted} tick={{fontSize:8}}/>
                    <YAxis stroke={C.muted} tick={{fontSize:8}} label={{value:"kWh",angle:-90,position:"insideLeft",fill:C.muted,fontSize:9}}/>
                    <Tooltip content={<CT unit="kWh"/>}/>
                    <Legend verticalAlign="top" height={32} wrapperStyle={{fontSize:"10px"}}/>
                    <Bar dataKey="generation" fill="#2E75B6" opacity={0.85} name="Solar generation (kWh)" radius={[2,2,0,0]}/>
                    <Bar dataKey="demand"     fill="#1F3A5F" opacity={0.45} name="Monthly demand (kWh)"   radius={[2,2,0,0]}/>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>

            {/* System summary */}
            <div style={S.card}>
              <div style={S.sec}>System Configuration — {site.clientName} · {site.siteName} · {site.sector}</div>
              <div style={{display:"grid",gridTemplateColumns:"repeat(4,1fr)",gap:"16px"}}>
                {[
                  ["Measured Profile",[["Daily demand",`${results.dailyKwh} kWh`],["Peak demand",`${results.peakKw} kW`],["Day load",`${results.dayLoad.toFixed(1)} kWh`],["Night load",`${results.nightLoad.toFixed(1)} kWh`]]],
                  ["PV Array",[["Required",`${results.pvKwp} kWp`],["Installed",`${results.actualPvKwp} kWp`],["Modules",`${results.numModules}×${assumptions.moduleWp}Wp`],["Avg irr.",`${results.avgIrr} kWh/m²/d`]]],
                  ["BESS",[["Required",`${results.bessRequired.toFixed(1)} kWh`],["Installed",`${results.bessActual} kWh`],["Units",`${results.bessUnits}×${assumptions.bessUnitKwh}kWh`],["DoD",`${(assumptions.bessDod*100).toFixed(0)}%`]]],
                  ["Energy Balance",[["Self-sufficiency",`${results.selfSufficiencyPct}%`],["Annual output",`${results.annualKwh.toLocaleString()} kWh`],["CO₂ avoided",`${results.co2Annual} t/yr`],["Grid import",`${results.totalGridImport.toFixed(1)} kWh/d`]]],
                ].map(([title,rows])=>(
                  <div key={title}>
                    <div style={{fontSize:"9px",color:C.muted,letterSpacing:"0.12em",textTransform:"uppercase",marginBottom:"7px"}}>{title}</div>
                    {rows.map(([k,v])=>(
                      <div key={k} style={{display:"flex",justifyContent:"space-between",padding:"4px 0",borderBottom:`1px solid ${C.border}`,fontSize:"10px"}}>
                        <span style={{color:C.muted}}>{k}</span>
                        <span style={{color:C.text}}>{v}</span>
                      </div>
                    ))}
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* ══ 04 DISPATCH ══════════════════════════════════════════════════ */}
        {tab==="dispatch" && results && (
          <div>
            <div style={S.grid4}>
              {[
                ["Peak Load",    Math.max(...results.hourlyData.map(h=>h.load)).toFixed(2),"kW"],
                ["Peak Solar",   Math.max(...results.hourlyData.map(h=>h.solar)).toFixed(2),"kW"],
                ["Grid Imports", results.hourlyData.reduce((a,h)=>a+h.grid,0).toFixed(2),"kWh/day"],
                ["Min SoC",      (()=>{const cap=results.bessActualKwh||results.bessActual||1;const minKwh=Math.min(...results.hourlyData.map(h=>h.battery));return ((minKwh/cap)*100).toFixed(1)+"%";})(),"battery level"],
              ].map(([l,v,u])=>(
                <div key={l} style={S.metric}>
                  <div style={{fontSize:"9px",color:C.muted,letterSpacing:"0.12em",textTransform:"uppercase",marginBottom:"5px"}}>{l}</div>
                  <div style={{fontSize:"20px",fontWeight:700,color:C.mid,lineHeight:1}}>{v}</div>
                  <div style={{fontSize:"10px",color:C.muted,marginTop:"3px"}}>{u}</div>
                </div>
              ))}
            </div>
            <div style={S.card}>
              <div style={S.ct}>Hourly dispatch — measured demand vs solar vs grid import (kW)</div>
              <ResponsiveContainer width="100%" height={260}>
                <LineChart data={results.hourlyData}>
                  <CartesianGrid stroke={C.border} strokeDasharray="3 3"/>
                  <XAxis dataKey="hour" stroke={C.muted} tick={{fontSize:8}} interval={1}/>
                  <YAxis stroke={C.muted} tick={{fontSize:8}} label={{value:"kW",angle:-90,position:"insideLeft",fill:C.muted,fontSize:9}}/>
                  <Tooltip content={<CT unit="kW"/>}/>
                  <Legend verticalAlign="top" height={32} wrapperStyle={{fontSize:"10px"}}/>
                  <Line type="monotone" dataKey="load"  stroke="#1F3A5F" strokeWidth={2.5} dot={false} name="Demand (kW)"/>
                  <Line type="monotone" dataKey="solar" stroke="#2E75B6" strokeWidth={2.5} dot={false} name="Solar generation (kW)"/>
                  <Line type="monotone" dataKey="grid"  stroke="#C0392B" strokeWidth={2}   dot={false} strokeDasharray="6 3" name="Grid import (kW)"/>
                </LineChart>
              </ResponsiveContainer>
            </div>
            <div style={{...S.card,marginTop:"14px"}}>
              <div style={S.ct}>Battery state of charge (% SoC)</div>
              {(() => {
                const cap   = (results.bessActualKwh || results.bessActual) || 1; // ||1 avoids /0
                const minSoc= +((1-(parseFloat(assumptions.bessDod)||0.9))*100).toFixed(0);
                const socData = results.hourlyData.map(r=>({
                  ...r,
                  soc: +((r.battery/cap)*100).toFixed(1),
                }));
                return (
                  <ResponsiveContainer width="100%" height={190}>
                    <AreaChart data={socData}>
                      <defs>
                        <linearGradient id="socGrad" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%"  stopColor="#2E75B6" stopOpacity={0.25}/>
                          <stop offset="95%" stopColor="#2E75B6" stopOpacity={0}/>
                        </linearGradient>
                      </defs>
                      <CartesianGrid stroke={C.border} strokeDasharray="3 3"/>
                      <XAxis dataKey="hour" stroke={C.muted} tick={{fontSize:8}} interval={1}/>
                      <YAxis stroke={C.muted} tick={{fontSize:8}} domain={[0,105]}
                             tickFormatter={v=>`${v}%`}/>
                      <Tooltip formatter={(v,n)=>[`${v}%`,"SoC"]} labelFormatter={l=>`Hour: ${l}`}/>
                      <Legend verticalAlign="top" height={28}
                              formatter={()=>"Battery SoC (%)"}
                              wrapperStyle={{fontSize:"10px",color:C.muted}}/>
                      <ReferenceLine y={minSoc} stroke={C.red}   strokeDasharray="4 2"
                                     label={{value:`Min ${minSoc}%`,fill:C.red,fontSize:8}}/>
                      <ReferenceLine y={100}    stroke={C.green} strokeDasharray="4 2"
                                     label={{value:"100%",fill:C.green,fontSize:8}}/>
                      <Area type="monotone" dataKey="soc" stroke="#2E75B6" fill="url(#socGrad)"
                            strokeWidth={2} dot={false} name="SoC (%)"/>
                    </AreaChart>
                  </ResponsiveContainer>
                );
              })()}
            </div>
            <div style={{...S.card,marginTop:"14px"}}>
              <div style={S.sec}>Hourly Dispatch Table</div>
              <table style={{width:"100%",borderCollapse:"collapse",fontSize:"10px"}}>
                <thead><tr>{["Hour","Demand (kW)","Solar (kW)","Battery (kWh)","SoC %","Grid (kW)","Status"].map(h=><th key={h} style={S.th}>{h}</th>)}</tr></thead>
                <tbody>
                  {results.hourlyData.map((row,i)=>{
                    const st = row.grid>0.1?"grid":row.solar>row.load?"surplus":"battery";
                    return(
                      <tr key={i}>
                        <td style={{...S.td,color:C.muted}}>{row.hour}</td>
                        <td style={S.td}>{row.load}</td>
                        <td style={{...S.td,color:C.green}}>{row.solar}</td>
                        <td style={{...S.td,color:C.amber}}>{row.battery}</td>
                        <td style={{...S.td,color:C.teal}}>{(()=>{const cap=results.bessActualKwh||results.bessActual||1;return ((row.battery/cap)*100).toFixed(1);})()}%</td>
                        <td style={{...S.td,color:row.grid>0.1?C.red:C.muted}}>{row.grid}</td>
                        <td style={S.td}><span style={S.badge(st==="grid"?"red":st==="surplus"?"green":"amber")}>{st==="grid"?"grid import":st==="surplus"?"solar surplus":"battery"}</span></td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* ══ 05 FINANCIAL ═════════════════════════════════════════════════ */}
        {tab==="financial" && results && (
          <div>
            {/* Blended tariff summary strip */}
            <div style={{...S.card,marginBottom:"14px",border:`1px solid ${C.lt}`}}>
              <div style={S.sec}>Blended Effective Tariff — Basis for Financial Model</div>
              <div style={{display:"grid",gridTemplateColumns:"repeat(4,1fr)",gap:"12px"}}>
                {[
                  ["Grid share",         `${(results.gridPct*100).toFixed(0)}%`,        ""],
                  ["Grid cost Y1",       `KES ${safeKes(results.gridRateY1)}/kWh`,      ""],
                  ["Diesel cost Y1",     `KES ${safeKes(results.dieselCostPerKwhY1)}/kWh`, "fuel + O&M"],
                  ["Blended rate Y1",    `KES ${safeKes(results.blendedRateY1)}/kWh`,   "weighted average"],
                ].map(([l,v,u])=>(
                  <div key={l} style={{background:C.deep,borderRadius:"3px",border:`1px solid ${C.border}`,padding:"10px 12px"}}>
                    <div style={{fontSize:"9px",color:C.muted,letterSpacing:"0.1em",textTransform:"uppercase",marginBottom:"4px"}}>{l}</div>
                    <div style={{fontSize:"15px",fontWeight:700,color:C.amber}}>{v}</div>
                    {u&&<div style={{fontSize:"9px",color:C.muted,marginTop:"2px"}}>{u}</div>}
                  </div>
                ))}
              </div>
            </div>
            <div style={S.grid3}>
              {[
                ["Total CAPEX",             `KES ${results.totalCapex.toLocaleString()}`,         "",                                                                                                                             "amber"],
                ["Energy savings (Y1)",     `KES ${results.annualEnergySaving.toLocaleString()}`, `blended KES ${safeKes(results.blendedRateY1)}/kWh × ${(parseFloat(assumptions.targetSelfSufficiency||0.8)*100).toFixed(0)}%`,"blue"],
                ["Demand savings (Y1)",     `KES ${results.annualDemandSaving.toLocaleString()}`, `KES ${assumptions.demandCharge}/kVA × peak reduction`,                                                                        "blue"],
                ["Total savings (Y1)",      `KES ${results.annualSavingsY1.toLocaleString()}`,    "energy + demand − O&M",                                                                                                       "green"],
                ["Payback (cost basis)",    `${results.paybackYears} yrs`,                        "on equipment cost ex-margin",                                                                                                  ""],
                ["NPV — undiscounted",      `KES ${(results.npv25/1e6).toFixed(1)}M`,             "cumulative savings less CAPEX",                                                                                                "green"],
                ["NPV — discounted",        results.npvDiscounted>=0?`KES ${(results.npvDiscounted/1e6).toFixed(1)}M`:`−KES ${(Math.abs(results.npvDiscounted)/1e6).toFixed(1)}M`,
                                            `at ${((parseFloat(assumptions.discountRate)||0.10)*100).toFixed(0)}% discount rate`,                                                                                               results.npvDiscounted>=0?"green":"red"],
                ["IRR",                     results.irrPct!=null?`${results.irrPct}%`:"—",        results.irrPct!=null?"internal rate of return on CAPEX":"insufficient cashflow",                                               results.irrPct!=null&&results.irrPct>15?"green":""],
              ].map(([l,v,u,col])=>(
                <div key={l} style={S.metric}>
                  <div style={{fontSize:"9px",color:C.muted,letterSpacing:"0.12em",textTransform:"uppercase",marginBottom:"5px"}}>{l}</div>
                  <div style={{fontSize:"18px",fontWeight:700,color:col==="amber"?C.amber:col==="green"?C.green:col==="red"?C.red:C.navy,lineHeight:1}}>{v}</div>
                  {u&&<div style={{fontSize:"10px",color:C.muted,marginTop:"3px"}}>{u}</div>}
                </div>
              ))}
            </div>

            <div style={S.grid2}>
              <div style={S.card}>
                <div style={S.sec}>Cost Breakdown</div>
                {[["PV Array",results.pvCostKes],["BESS",results.bessCostKes],["Installation & BOS (per panel)",results.installationCost||0]].map(([l,v])=>(
                  <div key={l} style={{marginBottom:"12px"}}>
                    <div style={{display:"flex",justifyContent:"space-between",marginBottom:"4px"}}>
                      <span style={{fontSize:"10px",color:C.dim}}>{l}</span>
                      <span style={{fontSize:"10px"}}>KES {v.toLocaleString()}</span>
                    </div>
                    <div style={{background:"${C.border}",borderRadius:"2px",height:"3px"}}>
                      <div style={{background:C.navy,height:"3px",borderRadius:"2px",width:`${(v/results.totalCapex*100).toFixed(0)}%`}}/>
                    </div>
                    <div style={{fontSize:"9px",color:C.muted,marginTop:"2px"}}>{(v/results.totalCapex*100).toFixed(0)}% of CAPEX</div>
                  </div>
                ))}
                <div style={{marginTop:"14px",paddingTop:"12px",borderTop:`1px solid ${C.border}`,fontSize:"10px"}}>
                  <div style={{color:C.dim,marginBottom:"8px",fontWeight:700}}>C&I savings breakdown (Year 1)</div>
                  {[
                    ["Energy charge saving",`KES ${results.annualEnergySaving.toLocaleString()}`],
                    ["Demand charge saving", `KES ${results.annualDemandSaving.toLocaleString()}`],
                    ["O&M cost",             `− KES ${assumptions.omCostPerYearKes.toLocaleString()}`],
                    ["Net annual benefit",   `KES ${results.annualSavingsY1.toLocaleString()}`],
                  ].map(([k,v])=>(
                    <div key={k} style={{display:"flex",justifyContent:"space-between",padding:"4px 0",borderBottom:`1px solid ${C.border}`}}>
                      <span style={{color:C.muted}}>{k}</span>
                      <span style={{color:k.includes("Net")?C.green:k.includes("O&M")?C.red:C.text,fontWeight:k.includes("Net")?700:400}}>{v}</span>
                    </div>
                  ))}
                </div>
              </div>
              <div style={S.card}>
                <div style={S.ct}>Cumulative cashflow — {assumptions.projectLifeYears} years (KES)</div>
                <ResponsiveContainer width="100%" height={260}>
                  <AreaChart data={results.cumulativeArr}>
                    <defs>
                      <linearGradient id="savGrad" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%"  stopColor="#C0392B" stopOpacity={0.3}/>
                        <stop offset="95%" stopColor="#C0392B" stopOpacity={0}/>
                      </linearGradient>
                    </defs>
                    <CartesianGrid stroke={C.border} strokeDasharray="3 3"/>
                    <XAxis dataKey="year" stroke={C.muted} tick={{fontSize:8}} interval={Math.floor(+(parseFloat(assumptions.projectLifeYears)||25)/7)}/>
                    <YAxis stroke={C.muted} tick={{fontSize:8}} tickFormatter={v=>`${(v/1e6).toFixed(1)}M`} label={{value:"KES",angle:-90,position:"insideLeft",fill:C.muted,fontSize:9}}/>
                    <Tooltip formatter={(v)=>[`KES ${(v/1e6).toFixed(2)}M`,"Cumulative NPV"]} labelFormatter={l=>`Year: ${l}`}/>
                    <Legend verticalAlign="top" height={28} wrapperStyle={{fontSize:"10px"}}/>
                    <ReferenceLine y={0} stroke={C.red} strokeDasharray="4 2" label={{value:"Break-even",fill:C.red,fontSize:8,position:"insideTopLeft"}}/>
                    <Area type="monotone" dataKey="cumulative" stroke="#C0392B" fill="url(#savGrad)"
                          strokeWidth={2.5} dot={false} name="Cumulative cashflow (KES)"/>
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            </div>
            {/* 10-yr blended rate escalation table */}
            {/* IRR & NPV sensitivity */}
            {results.irrPct != null && (
              <div style={{...S.card,marginTop:"14px"}}>
                <div style={S.sec}>IRR & NPV Sensitivity Analysis</div>
                <div style={{fontSize:"10px",color:C.muted,marginBottom:"12px"}}>
                  Project IRR: <span style={{color:results.irrPct>20?C.green:results.irrPct>12?C.amber:C.red,fontWeight:700,fontSize:"13px"}}>{results.irrPct}%</span>
                  <span style={{marginLeft:"16px"}}>NPV at different discount rates (cost basis):</span>
                </div>
                <div style={{display:"grid",gridTemplateColumns:"repeat(6,1fr)",gap:"8px",marginBottom:"12px"}}>
                  {[5,8,10,12,15,20].map(r=>{
                    const rate = r/100;
                    const projectYears = parseFloat(assumptions.projectLifeYears)||25;
                    const tariffEsc   = parseFloat(assumptions.tariffEscalation)||0.05;
                    const pvDeg       = parseFloat(assumptions.pvDegradationPerYear)||0.005;
                    const omInfl      = parseFloat(assumptions.inflationRate)||0.06;
                    const om0         = parseFloat(assumptions.omCostPerYearKes)||120000;
                    const selfSuff    = parseFloat(assumptions.targetSelfSufficiency)||0.8;
                    let npvR = -results.totalCapex;
                    for(let y=1;y<=projectYears;y++){
                      const blendY = (results.blendedRateY1||0)*Math.pow(1+tariffEsc,y-1);
                      const degraded = (results.annualKwh||0)*selfSuff*Math.pow(1-pvDeg,y-1);
                      const es = degraded*blendY;
                      const ds = (results.annualDemandSaving||0)*Math.pow(1+tariffEsc,y-1);
                      const om = om0*Math.pow(1+omInfl,y-1);
                      npvR += (es+ds-om)/Math.pow(1+rate,y);
                    }
                    const npvM = npvR/1e6;
                    const positive = npvR>=0;
                    const isActive = Math.abs(r/100-(parseFloat(assumptions.discountRate)||0.10))<0.005;
                    return(
                      <div key={r} style={{background:isActive?C.navy:C.deep,border:`1px solid ${isActive?C.lt:C.border}`,borderRadius:"3px",padding:"10px 8px",textAlign:"center"}}>
                        <div style={{fontSize:"9px",color:isActive?C.lt:C.muted,letterSpacing:"0.1em",marginBottom:"4px"}}>{r}% rate</div>
                        <div style={{fontSize:"13px",fontWeight:700,color:positive?C.green:C.red}}>{positive?"":"-"}KES {Math.abs(npvM).toFixed(1)}M</div>
                        {isActive&&<div style={{fontSize:"8px",color:C.mid,marginTop:"2px"}}>← your rate</div>}
                      </div>
                    );
                  })}
                </div>
                <div style={{fontSize:"9px",color:C.muted,lineHeight:1.6}}>
                  IRR is the discount rate at which NPV = 0. A project creates value when its IRR exceeds your cost of capital or required return.
                  The highlighted column reflects the discount rate set in your Assumptions tab.
                </div>
              </div>
            )}
            {results.annualBreakdown && (
              <div style={{...S.card,marginTop:"14px"}}>
                <div style={S.sec}>10-Year Blended Rate & Savings Escalation</div>
                <div style={{overflowX:"auto"}}>
                  <table style={{width:"100%",borderCollapse:"collapse",fontSize:"10px"}}>
                    <thead><tr>
                      {["Year","Grid rate (KES/kWh)","Diesel cost (KES/kWh)","Blended rate (KES/kWh)","Energy saving (KES)","Demand saving (KES)","Net saving (KES)"].map(h=><th key={h} style={S.th}>{h}</th>)}
                    </tr></thead>
                    <tbody>
                      {results.annualBreakdown.map((row,i)=>(
                        <tr key={i}>
                          <td style={{...S.td,color:C.muted,fontWeight:700}}>{row.year}</td>
                          <td style={{...S.td,color:C.mid}}>{row.gridRate.toFixed(2)}</td>
                          <td style={{...S.td,color:C.amber}}>{row.dieselRate.toFixed(2)}</td>
                          <td style={{...S.td,color:C.amber,fontWeight:700}}>{row.blendedRate.toFixed(2)}</td>
                          <td style={{...S.td,color:C.green}}>KES {row.energySaving.toLocaleString()}</td>
                          <td style={{...S.td,color:C.green}}>KES {row.demandSaving.toLocaleString()}</td>
                          <td style={{...S.td,color:C.green,fontWeight:700}}>KES {row.netSaving.toLocaleString()}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        )}

        {/* ══ 06 CLIENT OFFER ══════════════════════════════════════════════ */}
        {tab==="offer" && results && (
          <CIOfferTab
            results={results}
            site={site}
            assumptions={assumptions}
            diesel={diesel}
            usdRate={usdRate}
            setUsdRate={setUsdRate}
            updAsm={updAsm}
          />
        )}

        {/* Empty state */}
        {(tab==="dashboard"||tab==="dispatch"||tab==="financial"||tab==="offer") && !results && (
          <div style={{textAlign:"center",padding:"70px 20px",color:C.muted}}>
            <div style={{fontSize:"36px",marginBottom:"12px",opacity:0.2}}>⚡</div>
            <div style={{fontSize:"10px",letterSpacing:"0.15em",marginBottom:"5px"}}>NO SIMULATION RESULTS</div>
            <div style={{fontSize:"10px",marginBottom:"18px"}}>
              {!profile ? "Upload a power logger file first, then click Run Simulation" : "Click ▶ Run Simulation in the header"}
            </div>
            <button style={S.btn("primary")} onClick={()=>setTab("inputs")}>← Go to Data Input</button>
          </div>
        )}
      </div>
      {/* Footer — brand tagline */}
      <div style={{
        background:C.navy, borderTop:`3px solid ${C.red}`,
        padding:"14px 32px", display:"flex", justifyContent:"space-between", alignItems:"center",
        marginTop:"auto",
      }}>
        <div style={{fontSize:"10px",color:C.lt,letterSpacing:"0.1em",fontStyle:"italic"}}>
          "Engineering is truth. Mathematics is the language."
        </div>
        <div style={{fontSize:"9px",color:"rgba(191,211,240,0.5)",letterSpacing:"0.15em",textTransform:"uppercase"}}>
          Esencee Energy Limited · SolarSim Pro v2.0
        </div>
      </div>
    </div>
  );
}
